// Three languages, one catalogue, no framework. About two hundred strings do not
// need a loader, a plural engine, or a build step.
//
// Every visible string lives here. A component never holds a literal, because a
// literal is invisible to the translator and to the reviewer.

// 'ar' is Arabic as Egyptians speak it; 'msa' is Modern Standard Arabic.
export type Language = 'en' | 'ar' | 'msa';

export const languages: readonly Language[] = ['en', 'ar', 'msa'] as const;

/** Whether a language is one of the two Arabics: right to left, Arabic dates. */
export function isArabic(language: Language): boolean {
	return language !== 'en';
}

/** Text direction for a language. The document root carries it. */
export function directionOf(language: Language): 'ltr' | 'rtl' {
	return isArabic(language) ? 'rtl' : 'ltr';
}

/** The language as the document's lang attribute names it: both Arabics are Arabic. */
export function langTag(language: Language): string {
	return isArabic(language) ? 'ar' : 'en';
}

/** The language the restaurant chose, read from the API; anything else is Arabic. */
export function languageOf(value: string): Language {
	return value === 'en' || value === 'msa' ? value : 'ar';
}

const en = {
	ordersShift: 'Shift',
	shiftTitle: 'Shift',
	shiftOpenSince: 'Open since %1',
	shiftOpenedBy: 'Opened by %1',
	shiftFloat: 'Float',
	shiftNoneOpen: 'No shift is open.',
	shiftOpen: 'Open shift',
	shiftClose: 'Close shift',
	shiftLastClosed: 'Last closed shift',
	shiftClosedBy: 'Closed %1 by %2',
	shiftApprovedBy: 'Approved by %1',
	shiftOver: 'Over %1',
	shiftShort: 'Short %1',
	shiftEven: 'Even',
	shiftPrintAgain: 'Print again',
	shiftPrinting: 'Printing…',
	shiftPrintFailed: 'The shift report did not print.',
	shiftLoading: 'Loading the shift',
	shiftLoadFailed: 'The shift could not be loaded.',
	shiftGoesTo: "This till's cash goes to %1.",
	shiftThereOpen: "%1's shift is open since %2.",
	shiftOpenQuestion: 'How much cash is in the drawer?',
	shiftOpenAmount: 'Cash in the drawer',
	shiftOpening: 'Opening…',
	shiftAlreadyOpen: 'A shift is open already.',
	shiftNotCashPoint: "This till's cash goes to another till, so it opens and closes no shift.",
	shiftTooLarge: 'That amount is more than a till takes.',
	shiftAskTitle: 'Sales from other tills',
	shiftAsking: 'Asking…',
	shiftReceived: 'Received',
	shiftCannotReach: 'Cannot reach',
	shiftAskAgain: 'Ask again',
	shiftCloseAnyway: 'Close anyway',
	shiftNext: 'Next',
	shiftCountQuestion: 'Count the cash in the drawer',
	shiftCountAmount: 'Counted cash',
	shiftClosing: 'Closing…',
	shiftApprovalTitle: 'Close the shift on %1',
	shiftCloseForbidden: 'Only a manager can close a shift.',
	shiftAlreadyClosed: 'This shift is closed already.',
	shiftClosedTitle: 'Shift closed',
	shiftExpected: 'Expected',
	shiftCounted: 'Counted',
	shiftCashSales: 'Cash sales',
	shiftCardSales: 'Card sales',
	shiftVoids: 'Voids',
	shiftNoSaleOpens: 'No-sale opens on this till',
	shiftDue: 'Due at close',
	shiftDueOrders: 'Open orders',
	shiftDueAmount: 'Amount',
	shiftDueNoDriver: 'No driver',
	shiftDueNotDelivery: 'Not a delivery',
	shiftDueByDriverNote: 'Unsettled deliveries on this cash point, not cash a driver holds',
	shiftNotReached: 'Not reached: %1',
	shiftBackToShift: 'Back to Shift',
	shiftNoShiftHere: 'No shift is open. Open one to take payments.',
	shiftNoShiftThere: 'No payment: no shift is open on %1.',
	shiftStale:
		"No payment: this till has not heard from %1 for 5 minutes. Check that %1 is on and on the shop's Wi-Fi.",
	shiftUnreachable: 'No payment: this till cannot reach %1, where its cash goes.',
	shiftVoidNoShift: 'No shift is open. Open one to void a paid order.',
	shiftCashOut: 'Cash out',
	shiftCashIn: 'Cash in',
	shiftCashOutIn: 'Cash out / in',
	shiftCashTotals: 'Cash out %1 · Cash in %2',
	shiftMovementBy: '%1 · by %2',
	shiftMovementByOnly: 'by %1',
	cashMoveQuestion: 'How much?',
	cashMoveAmount: 'Amount',
	cashMoveWhy: 'Why?',
	cashMoveRecording: 'Recording…',
	cashMoveNoShift: 'Open a shift first.',
	cashMoveAmountBounds: 'Type an amount from 0.01 to the most a till takes.',
	cashMoveNoteNeeded: 'Say why the cash moves, on one line.',
	cashMoveReason: 'Reason',
	cashMoveOther: 'Other',
	cashMoveDriverFloat: 'Driver float',
	cashMoveDriver: 'Which driver?',
	cashMoveNoDrivers: 'No drivers yet. Ask a manager to add them.',
	cashMoveDriversFailed: 'The drivers did not load.',
	cashMoveDriverNotFound: "That driver is not on this shop's list.",
	cashMoveDriverRemoved: 'That driver was removed.',
	shiftMovementDriverBy: 'Driver float: %1 · by %2',
	ownerShifts: 'Shifts',
	shiftsAllCashPoints: 'All cash points',
	shiftsCashPoint: 'Cash point',
	shiftsOpened: 'Opened',
	shiftsClosed: 'Closed',
	shiftsOverShort: 'Over or short',
	shiftsLate: 'Late',
	shiftsLateCount: '%1 late',
	shiftsOpenChip: 'Open',
	shiftsShowOlder: 'Show older',
	shiftsEmpty: 'No shifts yet.',
	shiftsNote: 'Newest first. The figures are worked out again each time, late sales included.',
	shiftsReportTitle: 'Shift on %1',
	shiftsOpenedLine: 'Opened %1 by %2',
	shiftsNoSaleOpensOn: 'No-sale opens on %1',
	shiftsSalesHeading: 'Sales and voids',
	shiftsTill: 'Till',
	shiftsSale: 'Sale',
	shiftsVoid: 'Void',
	shiftsArrivedAfterClose: 'Arrived after close',
	shiftsNoSales: 'No sales in this shift yet.',
	shiftsMovementsHeading: 'Cash out and in',
	shiftsCashOut: 'Cash out',
	shiftsCashIn: 'Cash in',
	shiftsMovementDriver: 'Driver float: %1',
	shiftsMovementBy: 'by %1',
	shiftsFiguresClamped: 'The figures are beyond what Bosla shows.',
	shiftsNoShift: 'No shift',
	tillsCashPointSelf: 'Cash point: this till',
	tillsCashPointOther: 'Cash point: %1',
	tillsCashPointInUse: '%1 sends its cash to this till. Point it to another till first.',
	shiftsDueOn: 'Due at close on %1',
	// The close's split of Due at close by driver on D27 (UC-63.R7, #1000).
	shiftsDueByDriver: 'Due at close by driver',
	shiftsDueByDriverNote: 'Unsettled deliveries on this cash point, not cash a driver holds',
	shiftsDueNoDriver: 'No driver',
	shiftsDueNotDelivery: 'Not a delivery',
	// The close's rows per driver on D27 (UC-63.R8, #1044).
	shiftsDriverCollected: 'Collected',
	shiftsDriverHanded: 'Handed in',
	shiftsDriverShortExtra: 'Short or extra',
	shiftsDriverFloats: 'Floats',
	shiftsDriverRefused: 'Refused',
	shiftsDriverPaidDirect: 'Paid direct',
	shiftsDriverBalance: 'With him',
	shiftsDriverExtra: 'Extra %1',
	shiftsDriverExtraNote: 'Unexplained cash, never revenue or a tip',
	shiftsOlderLoading: 'Loading…',
	tillsCashPointInUseMany: '%1 send their cash to this till. Point them to other tills first.',
	tillsOnline: 'Online',
	tillsOffline: 'Offline',
	tillsNeverSeen: 'Never seen',
	tillsLastSeen: 'Last seen %',
	tillsSeenToday: 'today',
	tillsSeenYesterday: 'yesterday',
	tillsAppBuild: 'App build %',
	tillsWaitingSalesOne: '1 waiting sale',
	tillsWaitingSalesNone: '0 waiting sales',
	tillsWaitingSalesTwo: '2 waiting sales',
	tillsWaitingSalesFew: '% waiting sales',
	tillsWaitingSalesMany: '% waiting sales',
	tillsWindows: 'Windows',
	tillsAndroid: 'Android',

	printingTitle: 'Printing',
	printingSharedNote: "Shared by all shops. Each print uses its own shop's details.",
	printingReceipt: 'Customer receipt',
	printingKitchen: 'Kitchen ticket',
	printingLanguage: 'Print language',
	printingFont: 'Font size',
	printingNormal: 'Normal',
	printingLarge: 'Large',
	printingRoomy: 'Extra spacing',
	printingSeparators: 'Item separators',
	printingLogo: 'Brand logo',
	printingDetails: 'Item customizations and notes',
	printingOptionPrices: 'Option prices',
	printingUnitPrices: 'Unit prices',
	printingLineTotals: 'Line totals',
	printingQR: 'Shop catalogue QR code',
	printingAutomatic: 'Print automatically after payment',
	printingIdentifiers: 'Larger order and table numbers',
	printingType: 'Prominent order type',
	printingInstructions: 'Bold special instructions',
	printingGroupIdentical: 'Group identical customizations',
	printingGroupCategories: 'Group by preparation category',
	printingItemsNote: 'Items and quantities are always included.',
	printingSample: 'Sample preview',
	printingSampleNote: 'Preview choices change no shop settings. Nothing is printed.',
	printingShop: 'Preview shop',
	printingPaper: 'Paper width',
	printingPaperWidth: '%1 mm',
	printingUnpublished: "This shop's catalogue page is not published, so its QR code is left out.",
	printingNoLogo: 'No logo to print is uploaded yet. Add one here, in the brand logo section.',
	printingLogoUpload: 'Logo to print',
	printingLogoRemoved: 'Logo to print removed',
	printingLogoUnreadable: 'The brand logo could not be read, so it is left out.',
	printingUpdating: 'Updating preview…',
	printingSaved: 'Printing settings saved',
	printingOrder: 'Order of print, top to bottom',
	printingOrderNote:
		'Drag a row by its grip, or focus a grip and press Up or Down. Items, totals and payment keep that order.',
	printingRefused:
		'Totals and payment stay below the items, and payment below the totals. The order did not change.',
	printingRefusedLive: 'Move refused',
	printingSectionMoved: '%1: now %2 of %3',
	printingAddText: 'Add a text line',
	printingAddSeparator: 'Add a separator',
	printingCenter: 'Centre',
	printingShown: 'Shown',
	printingHidden: 'Hidden',
	printingBold: 'Bold',
	printingDelete: 'Delete',
	printingDashed: 'Dashed',
	printingBlank: 'Blank',
	printingLessSpace: 'Less space',
	printingMoreSpace: 'More space',
	printingSpace: '%1 mm',
	printingSmallerLogo: 'Smaller logo',
	printingLargerLogo: 'Larger logo',
	printingWidth: '%1%',
	printingOriginalSize: 'Original size',
	printingOneLine: 'On one line',
	printingDrag: 'Drag to reorder',
	printingTextLine: 'Text line',
	printingSeparator: 'Separator',
	printingShopName: 'Shop name',
	printingShopAddress: 'Shop address',
	printingShopPhone: 'Shop phone',
	printingOrderDetails: 'Order details',
	printingItems: 'Items',
	printingTotals: 'Totals',
	printingPayment: 'Payment',
	shopsPhone: 'Phone (optional)',
	shopsPhoneNote: 'Printed on receipts when filled in.',
	shopsPhoneInvalid: 'Check the phone number.',
	printingMoved: 'Receipt words and layout are now shared by all shops, under Settings → Printing.',
	printingOpenSettings: 'Open printing settings',

	ownerShopSettings: 'Shop settings',
	ownerAccountSettings: 'Account settings',
	ownerPublicPage: 'Public page',
	ownerDelivery: 'Delivery areas',
	ownerShopVisibility: 'Location & readiness',
	ownerReturnToTask: 'Return to previous task',
	ownerShopMissing: 'This shop is unavailable. Choose a shop to continue.',
	ownerAllCashiers: 'All staff',
	ownerAllTills: 'All devices',
	ownerOpenPublicSettings: 'Open business public page settings',
	ownerOpenShop: 'Open shop',
	ownerBackToBusiness: 'Business overview',

	appName: 'Cashier',
	pairTitle: 'Pair this till',
	pairInstruction: 'Enter the code from the dashboard.',
	pairCodeLabel: 'Pairing code',
	lockTitle: 'Enter your passcode',
	offline: 'No connection. The sale cannot finish until it returns.',
	tillNotReady: 'The till is still getting ready. Try again in a moment.',
	backspace: 'Delete',

	signInTitle: 'Restaurant dashboard',
	signInInstruction: 'Sign in with Google, or with your email and password.',
	signInWithGoogle: 'Sign in with Google',

	createTitle: 'Create your restaurant',
	createInstruction: 'This takes a minute, and everything can change later.',
	createName: 'Restaurant name',
	createCountryCode: 'Dialling code for phones',
	createCountryCodeHint: 'No plus sign. Egypt is 20.',
	createShopName: 'First shop name',
	createShopNameHint: 'Left empty, it will be called “Main”.',
	createSubmit: 'Create restaurant',
	createWorking: 'Creating…',

	ownerHome: 'Home',
	ownerShops: 'Shops',
	ownerCashiers: 'Cashiers',
	ownerTills: 'Tills',
	ownerSignOut: 'Sign out',

	shopsAdd: 'New shop',
	shopsName: 'Shop name',
	shopsAddress: 'Address',
	shopsDayStart: 'Trading day starts at',
	shopsFee: 'Delivery with no area',
	shopsClose: 'Close shop',

	tillsAdd: 'New till',
	tillsName: 'Till name',
	tillsShop: 'Shop',
	tillsSave: 'Add till',
	tillsRevoke: 'Revoke',
	tillsEmpty: 'No tills yet.',
	tillsCodeTitle: 'Pairing code ·',
	tillsCodeOnce: 'Shown once: close this and it is gone.',
	tillsCodeDone: 'Close it',
	tillsUnpaired: 'Waiting to be paired',
	tillsNoShops: 'Open a shop first.',

	pairFailed: 'That code does not work. Check it against the dashboard.',
	pairChecking: 'Pairing this till…',
	pairLocked: 'Too many tries. Wait a minute.',
	pairedAs: 'Paired',

	cashiersAdd: 'New cashier',
	cashiersName: 'Name',
	cashiersShop: 'Shop',
	cashiersRole: 'Role',
	cashiersRoleCashier: 'Cashier',
	cashiersRoleManager: 'Manager',
	cashiersPasscode: 'Passcode',
	cashiersPasscodeHint: '8 digits. Never shown again after saving.',
	cashiersPasscodeKeep: 'Leave empty to keep the current one',
	cashiersEdit: 'Edit',
	cashiersEmpty: 'No cashiers yet.',
	cashiersNoShops: 'Open a shop first.',
	cashiersNameTooLong: 'A name is at most 22 characters.',

	lockEnterPasscode: 'Enter your passcode',
	lockWrong: 'That passcode does not work here.',
	lockLocked: 'Too many tries. Wait and try again.',
	lockLockedFor: 'Too many tries. Try again in',
	lockChecking: 'Checking…',
	lockAccountLocked: 'Too many wrong passcodes in this restaurant. Everyone must wait.',
	lockLockNow: 'Lock',
	lockSignOut: 'Sign out',
	headerOpenCount: 'open',
	headerOpenCountAria: '%1 open, open the open orders',
	lockOrderWaiting: 'Order in progress',
	lockUnsentSale: 'Unsent sale',
	lockItems: 'items',

	sellPay: 'Pay',
	sellPrintBill: 'Print bill',
	sellBillPending: 'Printing…',
	sellBillChanged: 'Bill changed since printed',
	sellBillFailed: 'Bill did not print. Check that the printer is working and has paper.',
	sellType: 'Order type',
	sellEmpty: 'Tap an item to start.',
	sellSubtotal: 'Subtotal',
	sellDelivery: 'Delivery',
	sellTotal: 'Total',
	sellDineIn: 'Dine in',
	sellTakeaway: 'Takeaway',
	sellDeliveryType: 'Delivery',
	sellRemove: 'Remove',
	sellUndo: 'Undo',
	sellRemoved: 'Removed',
	sellDiscount: 'Discount',
	discountTitle: 'Discount',
	discountBefore: 'Before discount',
	discountNone: 'None',
	discountType: 'Type a discount',
	discountPercentNote: 'A whole number. Rounds down to a whole pound.',
	discountAmountNote: 'Above the total, the order is free.',
	discountTakesOff: 'Takes off %1. New total %2.',
	discountTakesAll: 'Takes off %1. The order is free.',
	discountEmpty: 'No discounts yet. The owner adds them in the dashboard.',
	discountManager: 'Manager discount',
	discountManagerOnly: 'Only a manager can give this discount.',
	discountRemoved: 'That discount is no longer offered. Pick another, or none.',
	discountsDown: "Discounts can't be given right now. Take the discount off to sell.",
	discountRefused: "That discount can't be given. Change it, or take it off to sell.",
	payFree: 'Free order. Exact cash finishes it, and the drawer stays shut.',
	doneFree: 'Free order',
	ordersFree: 'Free order',
	ordersDiscount: 'Discount: %1',
	ordersDiscountGiven: 'Discount: %1 · given by %2',
	sellSoldOut: 'Sold out',
	sellQty: 'Quantity',
	sellPendingTitle: 'Checkout not confirmed',
	sellPendingRetry: 'Retry',
	sellSend: 'Send',
	sellSent: 'Sent',
	sellSendFirst: 'Send first',
	sellNewOrder: 'New order',
	sellOrderFrom: 'from %1',
	ordersOpenOnSell: 'Open',
	sellNoMenu: 'This shop has no menu yet.',

	// The primary that finishes a task, with a check beside it (design: Confirm).
	confirm: 'Confirm',
	editorNote: 'Note',
	editorChooseOne: 'Choose 1',
	editorChooseAtLeast: 'Choose at least',
	editorBelowZero: 'Price below zero, ask a manager.',

	deliveryTitle: 'Delivery details',
	deliveryArea: 'Area',
	deliveryNoArea: 'No area',
	deliveryPhone: 'Phone',
	deliveryAddress: 'Address',
	deliveryName: 'Customer name',
	deliveryPhoneRequired: 'Phone required',
	deliveryPhoneCheck: 'Check phone',
	deliveryStepPhone: 'Customer phone',
	deliveryStepWhere: 'Where to?',
	deliveryNewCustomer: 'New customer',
	deliveryNewAddress: 'New address',
	deliverySuggestFrom: 'Saved customers show from the 5th digit',
	deliverySuggestions: 'Saved customers',
	deliveryNewNumber: 'New number. “New customer” is below.',
	deliveryNoMatch: 'No saved customer has these digits.',
	deliveryNoSavedAddress: 'No saved address',
	deliveryRequired: 'Required',
	deliveryAddressHint: 'Street and building number',
	deliveryLandmark: 'Landmark (optional)',
	deliveryLandmarkHint: 'e.g. Floor 3, flat 7, next to the pharmacy',
	deliveryReadBack: 'Read it back',
	deliveryNoAreaYet: 'No area yet, pick one below',

	payTitle: 'Payment',
	payExact: 'Exact cash',
	payCard: 'Card',
	payWithDriver: 'Send with driver',
	payTendered: 'Tendered',
	payChange: 'Change',

	doneSending: 'Sending the sale to Bosla…',
	doneNotPrinted: 'The receipt did not print. Try Print again.',
	doneRetry: 'Retry',
	doneDrawerFailed: 'The drawer did not open.',
	donePrinting: 'Printing…',

	ordersTitle: 'Orders',
	ordersNumber: 'No.',
	ordersStatus: 'Status',
	ordersBack: 'Back',
	ordersToday: 'All',
	ordersPaid: 'Paid',
	ordersVoid: 'Void',
	ordersOpen: 'Open',
	// A closed order the till's order store has not had confirmed (#371).
	ordersWaiting: 'Waiting to send',
	ordersWaitingTell: 'The till keeps this sale and sends it to Bosla once it can.',
	ordersNotTaken: 'Not taken',
	ordersNotTakenTell: 'Bosla did not take this sale: %1 The till keeps it.',
	ordersNotTakenTotal: "its total differs from Bosla's.",
	ordersNotTakenInvalid: 'something in it is not valid.',
	ordersNotTakenHeld: 'Bosla holds this sale with other details.',
	ordersNotTakenNumber: 'another sale of this till has its number today.',
	ordersNotTakenOther: 'Bosla holds this order for another till.',
	ordersNotTakenCode: 'reason %1.',
	ordersCash: 'Cash',
	ordersDue: 'Due',
	ordersNoSale: 'No-sale',
	ordersEmpty: 'No orders yet today.',
	ordersReprint: 'Print again',
	ordersTakePayment: 'Take payment',
	ordersVoidAction: 'Void',
	ordersOpenDrawer: 'Open drawer',
	ordersTestPrint: 'Test print',
	ordersPrinters: 'Printers',
	ordersNoReceiptPrinter: 'No receipt printer',
	ordersNoDrawer: 'No drawer',
	// T10, a manager only: a newer Bosla app for this till (UC-57, #650).
	appUpdateOffered: 'A newer Bosla app is ready to download: %1.',
	appUpdateAction: 'Update app',
	appUpdateDownloading: 'Downloading the update.',
	appUpdateDownloadingAction: 'Downloading…',
	appUpdateReady:
		'The update is downloaded. It installs when the till locks with nothing on its way.',
	// Install now on the ready Notice, and its words while it waits (#925).
	appUpdateInstallNow: 'Install now',
	appUpdateInstallWaiting: 'Installs when the till is free…',
	appUpdateStopped: 'The update did not download.',
	appUpdateNotChecked: 'The update did not check out, so the till did not keep it.',
	// Till settings and onboarding (#774, UC-59, T26/T27).
	tillSettings: 'Till settings',
	tillSettingsLoading: "Loading this till's settings",
	tillSettingsFailed: "This till's settings could not be loaded.",
	// Refresh from Bosla (T26, UC-13.R21), for everyone signed in.
	tillRefreshTitle: 'Refresh from Bosla',
	tillRefreshState: 'Gets the newest menu, prices, printers and drivers now',
	tillRefresh: 'Refresh',
	tillRefreshing: 'Refreshing…',
	tillRefreshed: 'Up to date',
	tillRefreshFailed: 'Bosla did not answer. Try again in a moment.',
	// Drivers (T34, UC-65), a manager's, from Till settings.
	tillDriversTitle: 'Drivers',
	tillDriversNone: 'No drivers yet',
	tillDriversOne: '1 driver',
	tillDriversMany: '%1 drivers',
	tillDriversLoading: 'Loading the drivers',
	tillDriversLoadFailed: 'The drivers could not be loaded.',
	tillDriversEmpty: 'No drivers yet.',
	tillDriversAdd: 'New driver',
	tillDriversName: 'Driver name',
	tillDriversNameTaken: 'That name is taken in this shop, by a current or a removed driver.',
	tillDriversNameBounds: 'A driver needs a name of one line, 1 to 40 characters.',
	tillDriversNeedsInternet: 'A change to the drivers needs the internet.',
	tillDriversForbidden: 'Only a manager can change the drivers.',
	tillDriversRemoved: 'Driver removed: %1',
	tillDriversGone: 'That driver was removed from this shop.',
	tillSettingsItem: 'Setting',
	tillSettingsState: 'State',
	setupOpen: 'Open',
	setupPrintersNone: 'No printer is set up yet',
	setupPrinterOne: '1 printer',
	setupPrintersTwo: '2 printers',
	setupPrintersMany: '%1 printers',
	setupTitle: 'Set up this till',
	setupSkip: 'Skip',
	setupNext: 'Next',
	setupPrintersOpen: 'Set up printers',

	// T28: sharing with this shop's tills and the link test (UC-58, UC-59, #775).
	sharingTitle: "Sharing with this shop's tills",
	sharingIntro:
		"The tills of this shop share their printer setup with each other on the shop's network.",
	sharingOn: 'Sharing is on.',
	sharingNeedsYes: "Sharing needs Windows' administrator to say yes once.",
	sharingOff: 'This till does not share now.',
	sharingRowOn: 'On',
	sharingRowNeedsYes: "Needs Windows' yes",
	sharingRowOff: 'Off',
	sharingTill: 'Till',
	sharingLinkColumn: 'Link',
	sharingHeard: 'Heard %1',
	sharingHeardToday: 'today',
	sharingHeardYesterday: 'yesterday',
	sharingNotHeard: 'Not heard yet',
	sharingLegacy: 'On an older Bosla app',
	sharingNoTills: 'This shop has no other till to test.',
	sharingReadFailed: 'This till could not read its sharing.',
	linkTest: 'Test the link',
	linkTestAgain: 'Test the link again',
	linkTesting: 'Testing…',
	linkAnswered: '%1 answered.',
	linkNotHeard:
		'This till does not hear %1. Check that %1 is on and on the same Wi-Fi as this till.',
	linkNoAnswer:
		'%1 did not answer. Some Wi-Fi keeps its devices apart, and then tills cannot reach each other.',
	linkMeshOff: 'Sharing is off on this till now.',
	linkGone: '%1 is no longer a till of this shop.',
	// T32: the cash-point step, in Till settings and the onboarding (UC-61, UC-62, #792).
	cashPointTitle: 'Cash point',
	cashPointQuestion: "Where does this till's cash go?",
	cashPointThisTill: 'This till',
	cashPointNoOthers: 'No other till of this shop is a cash point.',
	cashPointTestFirst: 'Test the link to %1 first.',
	cashPointHearing: "Checking %1's shift…",
	cashPointReady: "Confirm sends this till's cash to %1.",
	cashPointOlderApp: '%1 needs the newer Bosla app.',
	cashPointOpenHere: "Close this till's shift first.",
	cashPointInUse: 'Other tills send their cash to this till. Point them to another till first.',
	cashPointNotAllowed: "%1 sends its cash to another till, so it cannot take this till's cash.",
	cashPointForbidden: "Only a manager chooses where this till's cash goes.",
	cashPointNeedsInternet: 'Choosing a cash point needs the internet.',
	cashPointReadFailed: 'This till could not read where its cash goes.',
	cashPointRemoved: 'Removed',
	pairNextSetup: 'Next, a manager signs in to set up this till.',
	printersTitle: 'Printers',
	printersBack: 'Back',
	printersEmpty: 'No printer is set up yet.',
	printersManagerSetsUp: 'A manager sets printers up on a till.',
	printerPrintsReceipts: 'Receipts and bills',
	printerPrintsKitchen: 'Kitchen slips',
	printerPrintsAll: 'Receipts, bills and kitchen slips',
	printerPaper58: '58 mm',
	printerPaper80: '80 mm',
	printerDrawer: 'Cash drawer',
	printerYes: 'Yes',
	printerNo: 'No',
	printerNotAnswering: 'Not answering',
	printersAdd: 'Add a printer',
	printersChange: 'Change',
	printersSetUpAgain: 'Set up again',
	printerSetupTitle: 'Set up a printer',
	printerAddressLabel: 'Printer address',
	printerAddressHint: 'Such as 192.168.1.50',
	printerTestSlip: 'Print test slip',
	printerCodeQuestion: 'Did this code print?',
	printerCodeLine: 'Code: %1',
	printerNameLabel: 'Name',
	printerNameHint: '1 to 40 characters',
	printerPrintsLabel: 'Prints',
	printerPaperLabel: 'Paper',
	printerDrawerOn: 'Cash drawer is on this printer',
	printerSave: 'Save',
	printerCancel: 'Cancel',
	printerPurposeTaken: '"%1" already prints %2.',
	printerPurposeTakenAnon: 'Another printer already prints %1.',
	printerAlreadyAddedAnon: 'A printer is already set up at this address.',
	printerPurposeReceipts: 'receipts and bills',
	printerPurposeKitchen: 'kitchen slips',
	printerNameInUse: 'Another printer already has that name.',
	printerAlreadyAdded: '"%1" is already set up at this address.',
	printerNoAnswer: 'No printer answers at that address.',
	printerCheckAddress: 'Check the address and try again.',
	printerForbidden: 'Only a manager sets printers up.',
	printerFailed: 'That did not go through. Try again.',
	printerSlipUnreachable: 'The printer does not answer. Check it is on and on this network.',
	printerSlipOtherPrinter: 'Another printer answers at that address.',
	printNoticeNoReceiptPrinter:
		'No receipt printer is set up. A manager sets one up in Orders > Printers.',
	printNoticeNoDrawer: 'No drawer is set up on the receipt printer.',
	printNoticeKitchen: 'The kitchen slip for order %1 did not print.',
	printNoticeBill: 'The bill for order %1 did not print.',
	printNoticeKitchenAnon: 'The kitchen slip did not print.',
	printNoticeBillAnon: 'The bill did not print.',
	printNoticeAgain: 'Print again',
	printHeldKitchen:
		'Kitchen slip for order %1: delivery uncertain. The earlier job may still complete.',
	printHeldBill: 'Bill for order %1: delivery uncertain. The earlier job may still complete.',
	printHeldKitchenAnon: 'Kitchen slip: delivery uncertain. The earlier job may still complete.',
	printHeldBillAnon: 'Bill: delivery uncertain. The earlier job may still complete.',
	printHeldReceipt: 'Receipt: delivery uncertain. The earlier job may still complete.',
	printHeldDrawer: 'Drawer: delivery uncertain. The earlier job may still complete.',
	printHeldAsk: 'Delivery uncertain. The earlier job may still complete.',
	printDecide: 'Decide',
	printWaitingDecision: 'Waiting for a decision',
	printHaveIt: 'I have it',
	printAnotherCopy: 'Print another copy',
	printDrawerOpened: 'The drawer opened',
	printOpenDrawerNow: 'Open drawer now',
	printDocReceipt: 'Receipt',
	printDocBill: 'Bill',
	printDocKitchen: 'Kitchen slip',
	printDocDrawer: 'Drawer',
	printDocShiftReport: 'Shift report',
	printHeldNoSale: 'No sale',
	printDecidedAlready: 'Someone already decided this print.',
	printBillChangedCopy: 'The order changed since this bill. Print its bill again.',
	printDrawerTooLate: 'The 2 minutes to open the drawer have passed. Use Open drawer in Orders.',
	printerChangeTitle: 'Change “%1”',
	printerSetUpAgainTitle: 'New address for “%1”',
	printerKeepsName:
		'The printer keeps its name and what it prints. We will test it with a slip at the new address.',
	printerBackAddress: 'Address',
	printerTryAgain: 'Try again',
	printerChangeAddress: 'Change the address',
	printerCodeNotPrinted:
		'Then what is at this address is not this printer, or the slip did not come out. Try again or change the address.',
	printerAddressNote: 'Printed on the configuration slip from the printer, such as 192.168.1.60',
	printerNameNote: 'Such as: Cashier, Kitchen',
	printerCodeIntro: 'The printer at %1 should have printed this code:',
	printerAtAddress: 'At %1',
	printerPaperUnit: 'mm',
	printerTestSlipPrinting: 'Printing test slip…',
	printersFromShop: "These printers come from the shop's list on Bosla.",
	printersSetUpHere: 'Set printers up on this till',
	printersSetUpHereAsk:
		"This till then keeps its own printers, also with no internet. The shop's other tills keep theirs.",
	printersPrintInProgress: 'A print is going out now. Try again in a moment.',
	printersOnThisTill: 'Set up on this till',
	printerSetupStale: 'The printers changed on this till. Check them and save again.',
	printersShared: "Shared with this shop's tills",
	printersSharedFromAnother: 'Set up on another till of this shop, %1',
	printersSharedFromHere: 'Set up on this till, %1',
	printersShareWaiting:
		'Another till of this shop has newer printers, but this till cannot reach one of them. This till keeps its own printers until it can.',
	printersShareNeedsFirewall:
		"Sharing with this shop's tills needs the firewall step in the till setup guide.",
	printersShareTurnOn: 'Turn on sharing',
	printersShareAllowed: 'Windows allowed sharing. It starts within a minute.',
	printersShareDeclined:
		"Windows did not allow it. Someone with this PC's administrator password must say yes.",
	printersShareFailed:
		'Windows could not turn sharing on. Use the firewall step in the till setup guide.',
	printersHostStarting:
		'The print host is starting on this computer. If this stays, restart Bosla.',
	printerHostNotReady: 'The print host is not ready yet. Try again in a moment.',
	printerUsbOnThisTill: 'USB printer on this till: %1',
	printerUsbPick: 'A printer plugged into this till',
	printerUsbOrAddress: "Or type a network printer's address",
	printerShare: "Share with the shop's other tills",
	printerSharedAt: 'Shared at %1: on an older till, add a printer at this address',
	printerShareFirewall: 'Waiting for the firewall step in the till setup guide',
	printerSharePortTaken: 'Port 9100 is used by another program',
	printerShareOffline: 'The printer is offline',
	printerShareHelp: "A shared printer takes printing from any device on the shop's network.",
	printerUsbOptionAnon: 'USB printer on this till',
	printerUsbHelp:
		'A USB printer shows here once Android lets Bosla use it: plug it in, and tap Allow. Tick Always so Android does not ask again.',
	printerCodeIntroUsb: 'The USB printer on this till should have printed this code:',
	printerNeedsIdentifying: 'Needs identifying',
	printerNeedsIdentifyingNote: 'Bosla is not sure which printer this is.',
	printersIdentify: 'Identify',
	printerIdentifyTitle: 'Identify “%1”',
	printerIdentifyIntro:
		'Pick the printer you mean and print a test slip on it. The slip shows a code.',
	printerIdentifyNone:
		'There is no printer to pick now. Check it is on and on this network, or set it up again with its address.',
	printerIdentifyBack: 'Printers',
	printerIdentifyYes: 'Yes, this is %1',
	printerIdentifyNotThis:
		'Then the slip did not come from this printer. Pick another printer, or check this one.',
	printerIdentifyAnother: 'Pick another',
	printerModelLabel: 'Model',
	printerModelUnknown: 'Not known',
	ordersVoidWhy: 'Why void order',
	ordersVoidWrong: 'Wrong order',
	ordersVoidCustomer: 'Customer left',
	ordersVoidMistake: 'Rung up twice',
	ordersVoidQuality: 'Food returned',
	// A manager's approval of a void or a line cancellation (#379).
	cancelItem: 'Cancel item',
	cancelHowMany: 'How many?',
	cancelOf: 'of',
	cancelLess: 'Fewer',
	cancelMore: 'More',
	cancelWhy: 'Why?',
	cancelVoids: 'This voids the order.',
	cancelNeedsManager: 'The reason opens Manager approval.',
	cancelTitle: 'Cancel %1 × %2 on order %3',
	voidTitle: 'Void order %1',
	cardApprovalTitle: 'Approve a card payment for %1',
	payCardAsksManager: 'This delivery has a driver: Card asks a manager.',
	approvalTitle: 'Manager approval',
	approvalLead: 'A manager approves with their own passcode. You stay signed in.',
	approvalManagers: 'Managers',
	approvalOtherName: 'Another name',
	approvalNoManager: 'No manager in this shop. The owner adds one in the dashboard.',
	approvalInternet: 'Needs the internet. A manager signed in on this till can approve without it.',
	approvalManagerOnly: 'Only a manager can approve.',
	ordersVoidClosed: 'That order is from another day and can no longer be voided.',
	ordersPaidAlready: 'That order is already paid.',
	ordersFailed: 'That did not go through. Try again.',
	ordersLoadFailed: "Today's orders could not be loaded.",

	doneChange: 'Change',
	doneOrder: 'Order',
	doneNext: 'Next sale',
	donePrintAgain: 'Print again',

	sellPriceChanged: 'The price changed. Check the new total.',

	ownerMenu: 'Menu',
	ownerSettings: 'Settings',

	// Help (D22, UC-36): the walkthroughs, by section.
	ownerHelp: 'Help',
	helpIntro:
		'Short videos of each task, recorded on a demo restaurant — never your own data. Show one to a cashier, too.',
	helpSectionLabel: 'Section',
	helpGettingStarted: 'Getting started',
	helpTill: 'Selling on the till',
	helpMenu: 'Menu',
	helpDayToDay: 'Day to day',
	helpAccount: 'Your account',
	helpOnTheTill: 'On the till',
	helpOnTheDashboard: 'On the dashboard',
	helpVideosOne: '% video',
	helpVideosTwo: '% videos',
	helpVideosFew: '% videos',
	helpVideosMany: '% videos',
	helpScenesOne: '% scene',
	helpScenesTwo: '% scenes',
	helpScenesFew: '% scenes',
	helpScenesMany: '% scenes',
	helpScenes: 'Scenes',
	helpEmpty: 'There are no videos in English yet.',
	helpVideoLoading: 'Loading the video',
	helpVideoFailed: 'That video did not load.',
	helpRetry: 'Retry',
	helpPlay: 'Play',
	helpPause: 'Pause',
	helpCaptions: 'Captions',
	helpFullscreen: 'Full screen',
	helpExitFullscreen: 'Leave full screen',
	helpSeek: 'Position',

	menuAddCategory: 'Category',
	menuAddItem: 'New item',
	menuCategoryName: 'Category name',
	menuItemName: 'Item name',
	menuPriceHint: 'for example 50 or 12.50',
	menuCategory: 'Category',
	menuAvailable: 'For sale',
	menuSoldOut: 'Sold out',
	menuEmpty: 'Nothing on the menu yet.',
	menuNoCategories: 'Add a category first.',
	menuCategoryRemoved: 'Category removed:',
	menuCategoryNotEmpty:
		'“%1” still holds items (%2). Move them to another category or remove them, then remove the category.',
	menuOverrides: "Each shop's price and availability · empty means the base price",
	menuBase: 'Base',
	menuDescription: 'Description',
	menuDescriptionNote: 'Up to 300 characters, on your catalogue page. Line breaks are kept.',
	menuPhoto: 'Photo',
	menuPhotoAlt: 'Item photo',
	menuPhotoEmpty: 'No photo',
	menuPhotoAdd: 'Add a photo',
	menuPhotoReplace: 'Replace photo',
	menuPhotoRemove: 'Clear',
	menuPhotoPreparing: 'Preparing…',
	menuPhotoNote:
		'PNG, JPEG or WebP, up to 20 MB, at least 64 × 64 pixels. We crop it square, at most 1024 × 1024.',
	menuPhotoType: 'The photo must be a PNG, a JPEG or a WebP.',
	menuPhotoSize: 'The photo is over 20 MB.',
	menuPhotoSmall: 'The photo is under 64 by 64 pixels.',

	settingsLanguage: 'Language',
	settingsLanguageArabic: 'مصري',
	settingsLanguageMSA: 'فصحى',
	settingsLanguageEnglish: 'English',
	settingsCountryCode: 'Dialling code for customer phones',
	settingsIdleSeconds: 'Lock a till after no touch (seconds)',
	settingsSaved: 'Saved.',

	areasTitle: 'Delivery areas',
	areasAdd: 'New area',
	areasName: 'Area name',
	areasFee: 'Fee',
	areasEmpty: 'No areas. This shop charges its own delivery fee.',

	loading: 'Loading',
	retry: 'Try again',
	loadFailed: 'The dashboard could not be reached.',
	pairContinue: 'Continue',
	pairHasDrawer: 'has a cash drawer',
	lockScreenLocked: 'Screen locked. Enter your passcode.',
	lockPasscode: 'Passcode',
	lockUnreachable: "Can't reach the server. Check the connection.",
	lockUrgent: 'Urgent',
	lockWho: "Who's signing in?",
	lockWhoContinues: "Screen locked. Who's continuing?",
	lockNames: 'Names',
	lockNotYou: 'Not you?',
	lockNobody: 'Nobody can sign in here yet',
	lockNobodyHint: 'The owner adds cashiers in the dashboard.',
	sellPendingBody: 'Retry resends the same sale, so it can never charge twice.',
	sellSending: 'Sending…',
	sellLoadingMenu: 'Loading the menu',
	sheetClose: 'Close',
	priceInvalid: 'Type a price like 50 or 12.50.',
	priceDecimals: 'At most % digits after the point.',
	priceNegative: 'A price cannot be below zero.',
	priceLarge: 'That price is too large.',
	priceRequired: 'Type a price.',
	mineTitle: 'My items',
	mineCount: '% items, in your order',
	mineEmpty: 'Pick the items you ring up most.',
	mineEdit: 'Edit my items',
	mineWholeMenu: 'The whole menu',
	mineOrder: 'In this order ·',
	mineNone: 'Nothing yet. Tick an item to add it to the end.',
	mineUp: 'Up',
	mineDown: 'Down',
	mineDrag: 'Drag to reorder',
	mineSaving: 'Saving…',
	mineGone: 'An item is no longer on the menu.',
	editorFewer: 'Fewer',
	editorMore: 'More',
	payTakeTitle: 'Take payment · order',
	payCashAmount: 'Cash amount',
	payAmount: 'Amount',
	payFinishing: 'Finishing…',
	payFailedKept: 'That sale did not go through. The cart is kept.',
	donePaidByCard: 'Paid by card',
	doneWithDriver: 'With the driver, not paid yet',
	ordersHeading: "Today's orders",
	ordersTillHeading: "This till's orders today",
	ordersTime: 'Time',
	ordersType: 'Type',
	ordersCard: 'Card',
	ordersEmptyFilter: 'No orders with this status.',
	ordersDrawerAsk: 'Open the drawer with no sale?',
	ordersDrawerOpen: 'Open',
	ordersDrawerOpening: 'Opening…',
	ordersNo: 'No',
	ordersDetails: 'Details',
	ordersCloseDetails: 'Close',
	ordersVoidWhyEnd: '?',
	ordersVoiding: 'Voiding…',
	ordersVoidReason: 'Void reason:',
	ordersLoadingDay: "Loading today's orders",
	ordersPrinted: 'Printed',
	ordersPrintedOnce: 'once',
	ordersPrintedTimes: 'times',
	ordersChangeWord: 'change',
	ownerOrders: 'Orders',
	ownerShopsCount: 'shops',
	edit: 'Edit',
	remove: 'Remove',
	cancel: 'Cancel',
	save: 'Save',
	saving: 'Saving…',
	saved: 'Saved.',
	yes: 'Yes',
	no: 'No',
	signInOpening: 'Opening Google…',
	createExists: 'This account already has a restaurant.',
	// An email and a password as a way in (UC-25): D1's form, D17 and D18.
	signInOr: 'or',
	signInEmail: 'Email',
	signInPassword: 'Password',
	signInSubmit: 'Sign in',
	signInSigningIn: 'Signing in…',
	signInNew: 'New to Bosla?',
	signInCreateAccount: 'Create an account',
	signInFailed: 'That email and password do not match an account.',
	signInUnverified:
		'Open the link we mailed to this address to finish creating your account. We sent a new one.',
	signInLocked: 'Too many tries. Try again in %1 minutes.',
	createAccountTitle: 'Create an account',
	createAccountInstruction:
		'With an email and a password, instead of Google. The account starts when you open the link we mail you.',
	createAccountName: 'Name',
	passwordRule: 'Use a password of 15 to 128 characters.',
	passwordShow: 'Show',
	passwordHide: 'Hide',
	createAccountLanguage: 'Language of the mail',
	createAccountSubmit: 'Create account',
	createAccountCreating: 'Creating…',
	createAccountBadEmail: 'Enter an email address.',
	backToSignIn: 'Back to Sign in',
	checkEmailTitle: 'Check your email',
	checkEmailBefore: 'We sent a link to',
	checkEmailAfter: '. Open it within 24 hours to finish.',
	sendAgain: 'Send it again',
	sending: 'Sending…',
	sentAgain: 'Sent again.',
	askAgainIn: 'You can ask again in %1.',
	linkChecking: 'Checking your link…',
	linkInvalidTitle: 'This link has expired or was already used.',
	linkInvalidText:
		'If your account isn’t finished yet, sign in with the right email and password and we’ll mail a new link.',
	// A forgotten password and an owner's sign-in methods (UC-26, UC-28):
	// D1's link, D19, D18's reset and D20.
	signInForgot: 'Forgot password?',
	signInEmailTaken:
		'This email signed up another way. Sign in the way you first did, then add this way in Settings.',
	forgotTitle: 'Forgot password',
	forgotInstruction:
		'Type your email and we’ll mail a link to set a new password. If you sign in with Google, this adds a password too.',
	forgotSubmit: 'Send the link',
	forgotSentBefore: 'If',
	forgotSentAfter: ' has an account, we sent it a link. It works for 30 minutes.',
	resetTitle: 'Choose a new password',
	resetPassword: 'New password',
	resetSubmit: 'Save and sign in',
	resetNote: 'You’ll be signed in here, and signed out on every other device.',
	resetInvalidText: 'A reset link works once, for 30 minutes. Ask for a new one.',
	methodsTitle: 'Sign-in methods',
	methodsNote: 'Added and removed on their own page',
	methodsCountOne: 'You have one way in: %1.',
	methodsCountTwo: 'You have 2 ways in: %1.',
	methodsCountMany: 'You have %1 ways in: %2.',
	methodsAnd: ' and ',
	methodsGoogleWord: 'Google',
	methodsPasswordWord: 'a password',
	methodGoogle: 'Google',
	methodPassword: 'Password',
	methodAdded: 'Added %1',
	methodRemove: 'Remove',
	methodsLoading: 'Loading sign-in methods…',
	methodsLast: 'This is your only way in, so it cannot be removed.',
	methodsSentBefore: 'Check your email:',
	methodsSentAfter: '. Open the link to add the password.',
	methodsAdd: 'Add',
	methodsAdding: 'Adding…',
	methodsAddGoogle: 'Add Google',
	methodsAddPassword: 'Add a password',
	methodsFoot:
		'Every addition mails your own address, so one you didn’t make gets seen. A second way in is how you get back in after losing the first.',
	methodsGoogleAdded: 'Google added.',
	methodsInUse: 'This Google account belongs to another Bosla account.',
	methodsEmailTaken: 'This email belongs to another Bosla account.',
	methodsUnfinished: 'Google did not finish adding the account. Try again.',
	methodsRemoved: 'Removed: %1 · %2',
	createNamePlaceholder: 'e.g. Zafaran',
	createShopDefault: 'Main',
	createSetForYou: 'Currency is EGP and the time zone is Cairo. We set these for you.',
	homeToday: 'Today',
	homeShopOrders: 'Shop orders',
	shopsDetails: 'Shop details',
	shopsDayStartBefore: 'Orders before',
	shopsDayStartAfter: 'AM count toward the previous day.',
	shopsOpen: 'Open shop',
	shopsOpened: 'Shop opened.',
	shopsCloseNote: 'Past orders keep it.',
	shopsClosed: 'Closed:',
	shopsLast: 'The last open shop cannot be closed.',
	shopsDupe: 'Another shop has that name.',
	areasNoArea: 'No area:',
	receiptPreview: 'Receipt preview',
	cashiersResetFor: 'New passcode for',
	cashiersMoveWarn: 'Moving shops signs them out of every till.',
	cashiersPasscodeAgain: 'Type it again',
	cashiersPasscodeInUse: 'Another cashier has that passcode. Every passcode must be unique.',
	cashiersPasscodeDiffer: 'The two passcodes differ.',
	cashiersPasscodeMatch: '✓ They match',
	cashiersAddAction: 'Add',
	cashiersNewPasscode: 'New passcode',
	cashiersPasscodeChanged: 'Passcode changed.',
	cashiersRemoved: 'Removed:',
	cashiersSignedOut: 'Signed out of every till.',
	tillsNamePlaceholder: 'e.g. Till 2',
	tillsDrawerQ: 'Is a cash drawer connected?',
	tillsDrawer: 'Drawer',
	tillsAdding: 'Adding…',
	tillsCodeExpires: 'Type this on the till. Expires in',
	tillsCodeExpired: 'The code expired. Add the till again for a new code.',
	tillsCount: 'tills',
	tillsPaired: 'Paired',
	tillsHasDrawer: 'Has drawer',
	tillsNoDrawer: 'No drawer',
	tillsRevoked: 'Revoked:',
	tillsStopped: 'It stopped at once.',
	menuItems: 'Items',
	menuOptions: 'Options',
	menuCategories: 'Categories',
	menuUp: 'Up',
	menuDown: 'Down',
	menuItemsIn: 'items in',
	menuCategoryEmpty: 'No items in this category.',
	menuItem: 'Item',
	menuStatus: 'Status',
	menuEditTitle: 'Edit',
	menuBasePrice: 'Base price',
	menuItemOptions: 'Options for this item',
	menuShopPrice: 'Shop price',
	menuShopAvailability: 'Shop availability',
	menuUseBase: 'Use base',
	menuRemoveItem: 'Remove item',
	menuItemRemoved: 'Item removed. Past orders keep it.',
	menuNewOption: 'New option',
	menuOption: 'Option',
	menuMin: 'Min',
	menuMax: 'Max',
	menuOnTill: 'On the till',
	menuOnTillColon: 'On the till:',
	menuRemoveOption: 'Remove option',
	menuChoice: 'Choice',
	menuChoiceName: 'Choice',
	menuPriceChange: 'Price change',
	menuAddOrTake: 'Add or take off',
	menuAdd: 'Add',
	menuEditOption: 'Edit option',
	menuNoItemsYet: 'No items yet',
	menuRangeError: 'Must be at least 1, and not below the minimum.',
	ruleRequiredOne: 'Required, pick one',
	ruleOptionalOne: 'Optional, at most one',
	ruleOptionalUpTo: 'Optional, up to %1',
	ruleExactly: 'Pick exactly %1',
	ruleAtLeastUpTo: 'At least %1, up to %2',
	ordersAllShops: 'All shops',
	ordersShop: 'Shop',
	ordersTradingDay: 'Trading day starts %1 AM',
	ordersTillOnly: 'Voids happen on the till only.',
	ownerReports: 'Reports',
	reportsPeriod: 'Period',
	reportsToday: 'Today',
	reportsYesterday: 'Yesterday',
	reportsLast7: 'Last 7 days',
	reportsMonth: 'This month',
	reportsLastMonth: 'Last month',
	reportsRange: 'From–to',
	reportsFrom: 'From',
	reportsTo: 'To',
	reportsUpTo: 'Up to 366 days.',
	reportsInvalid: 'The period ends before it starts.',
	reportsTooLong: 'A report covers at most 366 days.',
	reportsDay1: '1 day',
	reportsDay2: '2 days',
	reportsDaysFew: '%1 days',
	reportsDaysMany: '%1 days',
	reportsPart: 'Report part',
	reportsOverview: 'Overview',
	reportsItems: 'Items',
	reportsHours: 'Hours',
	reportsLoading: 'Loading the report',
	reportsEmpty: 'No sales in this period.',
	reportsTotals: 'Totals',
	reportsTotalsNote: "Agrees with the till's day summary",
	reportsPaid: 'Paid orders',
	reportsNet: 'Net sales',
	reportsAverage: 'Average order',
	reportsFees: 'Delivery fees',
	reportsMix: 'Cash and card',
	reportsCash: 'Cash',
	reportsCard: 'Card',
	reportsVoid: 'Void',
	reportsOpen: 'Open, due',
	reportsNoSale: 'No-sale drawer opens',
	reportsExplain:
		"Net sales counts paid orders only, after their discounts, delivery fees included. Open orders are due, not sold; voids are counted apart. Each order counts on its shop's trading day (from %1 AM).",
	reportsByType: 'By order type',
	reportsType: 'Type',
	reportsOrders: 'Orders',
	reportsItemsSold: 'Items sold',
	reportsItemsNote:
		'Best sellers first · at menu price, before order discounts, without delivery fees',
	reportsItem: 'Item',
	reportsQty: 'Qty',
	reportsRevenue: 'Revenue',
	reportsZero: 'Sold nothing in this period · %1',
	reportsItemsTotal: 'Total at menu price, before discounts and delivery fees',
	reportsSoldOut: 'Sold out now',
	reportsSoldOutAt: 'Sold out at %1',
	reportsAdded: 'Added %1',
	reportsGone: 'No longer on the menu',
	reportsByHour: 'By hour',
	reportsHour: 'Hour',
	reportsHoursNote: 'From %1 AM · busiest by orders',
	reportsAm: 'AM',
	reportsPm: 'PM',
	reportsDiscounts: 'Discounts',
	reportsDiscountsOn1: 'Discounts given · on %1 order',
	reportsDiscountsOnFew: 'Discounts given · on %1 orders',
	reportsDiscountsOnMany: 'Discounts given · on %1 orders',
	reportsDiscountsNone: 'No discounts in this period.',
	reportsEachDiscount: 'Each discount',
	reportsEachDiscountNote: 'Paid orders · what it took off',
	reportsDiscount: 'Discount',
	reportsTookOff: 'Took off',
	reportsRemoved: 'Removed',
	reportsDiscountsTotal: 'Total · paid orders with a discount',
	reportsByCashier: 'Discounts by cashier',
	reportsByCashierNote: 'Most given first · ties by name',
	reportsCashier: 'Cashier',
	reportsWithDiscount: 'With a discount',
	reportsCashierNote:
		'Not sales. A discount is the cashier’s choice, so this is where you check it against your house rules. The cashier who gave the most comes first. A busy shift gives more in total, so each row stands beside the orders that cashier placed and how many had a discount. A cashier’s sales are never shown.',
	settingsIdleNote: 'The till locks after %1 seconds with nobody touching it. 1 to 3600.',
	settingsQuick: 'Quick cash amounts · up to 8',
	settingsAmount: 'Amount',
	settingsExampleBefore: 'If the total is',
	settingsExampleAfter: 'the cashier sees:',
	settingsCurrency: 'Currency',
	settingsTimezone: 'Time zone',
	menuChoiceNote:
		"A choice can't be edited. To change its price, remove it and add it again. Offered on:",
	settingsTheme: 'Theme',
	themeLight: 'Light',
	themeDark: 'Dark',
	themeSystem: 'Follow system',
	settingsBrowserTitle: 'This browser only: language and theme',
	settingsBrowserNote: 'No Save',
	settingsBrowserExplain:
		"Both apply at once. They change only this browser, not the tills or any other browser. Language starts as the restaurant's; theme starts as Follow system.",
	settingsRestaurantTitle: 'The restaurant',
	settingsRestaurantNote: 'Every till and receipt',
	settingsRestaurantLanguage: 'Restaurant language',
	settingsRestaurantLanguageNote:
		'Receipts print in it, and every screen shows it until it chooses its own.',
	settingsTillsTitle: 'Tills',
	settingsTillsNote: 'All tills',
	settingsSaveNote: 'Saves the restaurant and tills together.',
	settingsFixedTitle: 'Fixed for this restaurant',
	settingsFixedNote: 'Read-only',
	createLanguageNote: 'Receipts print in it, and every screen starts in it until it chooses.',
	tillDisplayTitle: 'Language and theme',
	tillDisplayNote:
		'This till only. Both apply at once, from the lock screen on, and never change the dashboard or another till.',
	customerSaved: 'Their addresses',
	customerEdit: 'Edit customer',
	customerEditNeedsInternet: 'Correcting a customer needs the internet.',
	customerEditAddress: 'Edit',
	customerSaveAddress: 'Save address',
	customerAddressRemoved: 'Address removed:',
	customerAddressRequired: 'Address is required.',
	customerAddressTaken: 'They already have this address saved.',
	customerAreaOf: 'Area · %1 shop',
	customerTitle: 'Customer',
	customerSaveName: 'Save name',
	customerNameRequired: 'Name is required.',
	customerAddresses: 'Saved addresses',
	customerNoAddresses: 'No saved addresses.',
	customerTillNote:
		'Changes here are saved to the customer, not just this order. Past orders keep the address they were placed with. Only the owner can delete a customer, from the dashboard.',
	customerGone: 'That customer is gone.',
	ownerCustomers: 'Customers',
	customersLoading: 'Loading customers',
	customersEmpty:
		"No customers yet. The till saves one each time a delivery's details are finished.",
	customersSearch: 'Search by phone or name',
	customersNoMatch: 'Nobody matches that search.',
	customersListNote: 'Most recent order first · 50 at a time',
	customersName: 'Name',
	customersPhone: 'Phone',
	customersOrders: 'Orders',
	customersLast: 'Last order',
	customersNoName: 'No name',
	customersOpen: 'Open',
	customersDelete: 'Delete customer',
	customersNoAddresses: 'No saved addresses. Every order so far was takeaway or dine in.',
	customersDeleteNote:
		'Deleting removes the customer and their addresses. Editing or removing an address never changes past orders: they keep the name, phone and address they were placed with.',
	customersDeleted: 'Customer deleted:',
	whenToday: 'Today',
	whenYesterday: 'Yesterday',

	// Bosla suspended the account (UC-23): T13 on the till, D14 on the dashboard.
	suspendedLabel: 'Account suspended',
	suspendedTillTitle: "Bosla has suspended this restaurant's account.",
	suspendedTillTell: 'Tell the owner.',
	suspendedTillContact: 'Bosla support:',
	suspendedOwnerTitle: "Bosla has suspended your restaurant's account.",
	suspendedOwnerTills: 'Your tills have stopped too.',
	suspendedOwnerContact: 'Contact Bosla:',
	suspendedWhatsapp: 'WhatsApp',
	suspendedCheck: 'Check again',
	suspendedChecking: 'Checking…',
	suspendedTillResumes:
		'When Bosla resumes the account, this message goes away on its own within a few minutes.',
	// T3: the till's link to Bosla, which its order store reads (#382, N13
	// to N15). The count is the till's closed sales Bosla has not confirmed.
	tillNetDown: 'No connection. Sales still finish, and go to Bosla when it is back.',
	tillNetDownOne: 'No connection. 1 sale goes to Bosla when it is back.',
	tillNetDownTwo: 'No connection. 2 sales go to Bosla when it is back.',
	tillNetDownMany: 'No connection. % sales go to Bosla when it is back.',
	tillWaitingOne: '1 sale not yet sent to Bosla.',
	tillWaitingTwo: '2 sales not yet sent to Bosla.',
	tillWaitingMany: '% sales not yet sent to Bosla.',
	// T21: a till the owner removed from its shop (#382, N16).
	removedLabel: 'This till was removed from the shop',
	removedTitle: 'This till was removed from the shop.',
	removedAsk: 'Ask the owner.',
	removedWaitOne:
		'1 sale has not reached Bosla yet. Keep this till on and connected until it does.',
	removedWaitTwo:
		'2 sales have not reached Bosla yet. Keep this till on and connected until they do.',
	removedWaitMany:
		'% sales have not reached Bosla yet. Keep this till on and connected until they do.',
	removedDone: 'Every sale on this till has reached Bosla.',
	removedOpenOne: '1 open order stays on this till. It cannot take its payment.',
	removedOpenTwo: '2 open orders stay on this till. It cannot take their payment.',
	removedOpenMany: '% open orders stay on this till. It cannot take their payment.',
	removedReturns: 'This message clears on its own if Bosla takes this till back.',
	// T18: the till's page in a browser with no Bosla app (#371, D21).
	shellNoticeLabel: 'The till needs the Bosla app',
	shellNoticeTitle: 'The till runs in the Bosla app.',
	shellNoticeTell: "Open the till from the Bosla app on the till's device.",
	shellNoticeOwner: 'Owners manage the restaurant from the dashboard.',
	shellNoticeDashboard: 'Open the dashboard',
	shellNoticeSetup: 'Setting up a till? Install the Bosla app on it.',
	shellNoticeWindows: 'Download for Windows',
	shellNoticeAndroid: 'Download for Android',
	shellNoticeWindowsWarn: 'Windows may warn about it: choose More info, then Run anyway.',
	// T22: the till's order store starting, or restarting, inside the app (#392).
	storeStartingLabel: 'The till is getting ready',
	storeStartingTitle: 'The till is getting ready.',
	storeStartingTell: 'Sales start in a moment.',
	storeDownTitle: 'The till is restarting.',
	storeDownTell: 'Every sale it kept is safe. It is back in a moment.',
	storeStillDown:
		'Still waiting? Close the Bosla app and open it again. If that does not help, contact Bosla.',

	// The Bosla subscription (UC-24): the warning and D15.
	subscriptionEndsOn: 'Your Bosla subscription ends on %1, in %2.',
	subscriptionEndsToday: 'Your Bosla subscription ends today.',
	subscriptionEnded: 'Your Bosla subscription ended on %1.',
	subscriptionOneDay: '1 day',
	subscriptionTwoDays: '2 days',
	subscriptionFewDays: '% days',
	subscriptionManyDays: '% days',
	subscriptionHowToRenew: 'How to renew',
	subscriptionTitle: 'Bosla subscription',
	subscriptionContact: 'Contact Bosla to renew:',
	subscriptionOnlyBosla: 'Only Bosla can change this date. Everything keeps working after it.',
	subscriptionClose: 'Close',
	// A Bosla admin's visit (UC-64): the dashboard's notice and D1's ended link.
	visitViewing: 'Viewing %1 as its owner until %2',
	visitExit: 'Exit',
	signInVisitEnded: 'This link has ended. Open the owner dashboard from the admin again.',

	ownerCatalogue: 'Catalogue',
	catalogueLoading: 'Loading the catalogue',
	cataloguePublished: 'Published in the catalogue',
	catalogueOpen: 'Open the page',
	catalogueNotPublished: 'Not published',
	catalogueNotPublishedNote: "Diners can't see the restaurant in the catalogue.",
	catalogueMissingTitle: 'To publish, you still need:',
	catalogueMissingLogo: 'Logo',
	catalogueMissingLogoWhy:
		"The catalogue shows each restaurant's logo beside its name; it is how diners recognise you.",
	catalogueMissingShop: 'A shop on the map',
	catalogueMissingShopWhy:
		'At least one shop needs an address and a place on the map, so diners know where to go.',
	catalogueMissingShopAct: 'Place a shop',
	catalogueIncomplete: 'Not published. Missing: %.',
	catalogueIncompleteLogo: 'a logo',
	catalogueIncompleteShop: 'a shop with an address and a place on the map',
	catalogueAnd: ' and ',
	cataloguePart: 'Catalogue part',
	cataloguePartPage: 'The page',
	cataloguePartShops: 'Shops on the map',
	catalogueInIt: 'In the catalogue',
	catalogueListed: 'Published',
	catalogueUnpublishNote:
		'It comes off the catalogue when you save. The address stays yours, and the link works again when you publish again.',
	catalogueLockedNote:
		'Published opens once there is a logo and a shop on the map. You can save the page without them.',
	listingSlug: 'Page address',
	listingSlugNote: '3 to 40 Latin letters, digits and hyphens.',
	listingName: 'Name',
	listingNameRequired: 'Name is required.',
	listingArea: 'Area (optional)',
	listingAreaNote: 'For example: Zamalek · Maadi. People search by it.',
	listingSlugInvalid:
		'3 to 40 Latin letters, digits and single hyphens, starting and ending with a letter or a digit.',
	listingSlugTaken: 'Another restaurant has this address. Pick another one.',
	listingNameInvalid: 'The name is 1 to 80 characters.',
	listingAreaInvalid: 'The area is at most 120 characters.',
	logoTitle: 'Logo',
	logoCardNote: 'Shown beside the name',
	logoAlt: 'Restaurant logo',
	logoEmpty: 'No logo',
	logoAdd: 'Add a logo',
	logoReplace: 'Replace logo',
	logoRemove: 'Remove',
	logoUploading: 'Uploading…',
	logoNote: 'PNG or JPEG, up to 2 MB, at least 64 × 64 pixels. We fit it to 512 × 512.',
	logoLockedNote: 'The restaurant is published: you can replace the logo, but not remove it.',
	logoType: 'The logo must be a PNG or a JPEG.',
	logoSize: 'The image is over 2 MB.',
	logoSmall: 'The image is under 64 by 64 pixels.',
	logoRemoved: 'Logo removed.',
	placesShown: '%1 of %2 shown',
	placeNoAddress: 'No address',
	placeShown: 'Shown',
	placeHidden: 'Not in the catalogue',
	placeLacks: 'Lacks %',
	placeLacksAddress: 'an address',
	placeLacksPlace: 'a place on the map',
	placeAnd: ' and ',
	placeAddressInShops: 'Address in Shops',
	placeEdit: 'Edit place',
	placeAdd: 'Place on the map',
	placeMapLabel: '% on the map',
	placeMapHint: 'Tap where the shop is on the map',
	placeZoomIn: 'Zoom in',
	placeZoomOut: 'Zoom out',
	placeLatitude: 'Latitude',
	placeLongitude: 'Longitude',
	placeDegrees: 'Type it in degrees, like 30.044420.',
	placeLatitudeRange: 'Latitude is from -90 to 90.',
	placeLongitudeRange: 'Longitude is from -180 to 180.',
	placeRequired: 'Required.',
	placeHelp:
		'Tap the map or drag the pin, or type both numbers in degrees. In Google Maps, tap the place and both numbers show at the top.',
	placeRemove: 'Remove place',
	placeSave: 'Save place',
	placeRemoved: 'Place removed: %.',
	placesFoot:
		'A shop shows in the catalogue once it has an address and a place on the map. Change the address in Shops. Diners see the address, and a link that opens the place in Google Maps.',
	ownerDiscounts: 'Discounts',
	discountsAdd: 'New discount',
	discountsIntro:
		'Every shop’s till offers this list. A cashier gives one per order, with two taps.',
	discountsCount: '% · by name',
	discountsEmpty: 'No discounts yet. Add one, and your cashiers can give it on the till.',
	draftChangesOne: '1 unpublished change',
	draftChangesTwo: '2 unpublished changes',
	draftChangesFew: '% unpublished changes',
	draftChangesMany: '% unpublished changes',
	draftPublish: 'Publish menu',
	draftWaiting: 'New menu takes effect on %',
	draftWhen: '%1 at %2',
	draftClean: 'The tills have this menu.',
	draftCancelled: 'Scheduled menu cancelled. Its changes are back in the draft.',
	draftTooLate: 'That menu has already started.',
	priceCheckTitle: 'Check these prices',
	priceCheckText: 'These prices are far from the ones they replace.',
	priceCheckFailed: 'The prices could not be checked. What you typed is kept.',
	priceAtShop: '%1 at %2',
	publishPending: 'Publishing…',
	publishLoading: 'Loading the menu',
	publishInEffect: 'On the tills now: menu %1, since %2.',
	publishWaitingTitle: 'Waiting for its time: menu %1, on %2',
	publishChangesTitle: 'What changes on the tills',
	publishPricesRight: 'These prices are right.',
	publishBeforeTitle: 'Before you publish',
	publishTills: 'Tills that are online get the new menu within %.',
	publishWindow:
		'A till that has not got it yet may still sell at the old prices for up to % after the switch. Those sales are kept.',
	publishSoldOut: 'Sold out takes effect at once. It never needs publishing.',
	publishWhenTitle: 'When',
	publishNow: 'Now',
	publishLater: 'At a date and time',
	publishDay: 'Day',
	publishTime: 'Time',
	publishTimezone: 'The day and the time are in % time.',
	cityCairo: 'Cairo',
	publishMinutes: '% minutes',
	publishMinutesFew: '% minutes',
	publishScheduled:
		'A new menu is already waiting for its time. Cancel it first, or wait until it starts.',
	publishCancelIt: 'Cancel it',
	publishChanged: 'The menu changed while this was open. Check the list again.',
	publishNothing: 'The tills already have this menu.',
	publishFailed: 'The menu could not be published. Your choices are kept.',
	publishPast: 'That time has passed. Pick a later one, or Now.',
	changesShopPrices: 'Shop prices',
	changeAdded: '%: added',
	changeAddedAt: '%: added at',
	changeRemoved: '%: removed',
	changeRenamed: '%1, was %2',
	changeOrder: 'Category order changed',
	changeItemOrder: '%: item order changed',
	changeChoiceOrder: '%: choice order changed',
	changeMoved: '%1: moved to %2',
	changeOptions: '%: the options it offers changed',
	changeDescription: '%: description changed',
	changePhoto: '%: photo changed',
	changeRange: '%1: %2, was %3',
	changeOwnPriceSet: '%: own price',
	changeOwnPriceCleared: '%: own price cleared',
	changeChoiceIn: '%1 in %2',
	changeBackOnSale: '%: back on sale',
	changeNowSoldOut: '%: sold out',
	discountsLoading: 'Loading discounts',
	discountsName: 'Name',
	discountsKind: 'Kind',
	discountsPercent: 'Percent',
	discountsAmount: 'Amount',
	discountsPercentLabel: 'Percent',
	discountsAmountLabel: 'Amount',
	discountsPercentNote: 'A whole number: 20 is 20%.',
	discountsAmountNote: 'EGP, typed like a price: 10 is 10.00.',
	discountsPercentRange: 'A percent is from 1 to 100.',
	discountsNameTaken: 'Another discount has this name.',
	discountsOnTill: 'On the till:',
	discountsTakesOn: 'On %1 it takes %2.',
	discountsAmountRate: '% off',
	discountsCreate: 'Add discount',
	discountsSave: 'Save discount',
	discountsRemoved: 'Discount removed: %',
	discountsHowTitle: 'How a discount works',
	discountsHow1: 'A discount comes off the whole order, delivery fee included.',
	discountsHow2: 'A percent is rounded down to a whole pound: 15% of 87.00 takes 13.00.',
	discountsHow3: '100% makes an order free, such as a staff meal. Its items are still recorded.',
	discountsHow4:
		'A change reaches the tills once the menu is published, and applies from the next sale. Past orders keep the discount they had.',
	discountsHow5:
		'Cashiers pick only from this list. A manager can also type a discount on the till.',

	// A shop's tables (UC-39): the till's table picker (T15), the dashboard's
	// tables (D23) and the report's dine-in by table.
	sellTable: 'Table',
	tableTitle: 'Pick a table',
	tableLabel: 'Table %1',
	tableNeeded: 'Pick the table this order is for.',
	tableRemoved: 'That table is no longer offered. Pick another.',
	tablesLoading: 'Loading the tables',
	tablesLoadFailed: 'The tables could not be loaded.',
	tablesTitle: 'Tables',
	tablesAdd: 'New table',
	tablesName: 'Table name',
	tablesNameHint: 'A name or a number, such as T5 or Terrace 2.',
	tablesEmpty: 'No tables. Dine-in orders at this shop name no table.',
	tablesNote:
		'Once this shop has a table, every dine-in order there names one. Renaming or removing a table changes no placed order.',
	tablesRemoved: 'Table removed: %1',
	tablesRuleWhat:
		'A table is a name or a number, such as T5 or Terrace 2. It has no area, and no busy or free state.',
	tablesRuleSeveral: 'One table may hold several orders at once.',
	tableNameInUse: 'Another table of this shop has this name.',
	tableLimit: 'A shop has at most 200 tables.',
	tableNameBounds: 'A table needs a name of 1 to 40 characters.',
	// A shop's drivers (D28, UC-65): the dashboard's task under a shop.
	driversTitle: 'Drivers',
	driversLoading: 'Loading the drivers',
	driversLoadFailed: 'The drivers could not be loaded.',
	driversAdd: 'New driver',
	driversName: 'Driver name',
	driversNameHint: 'A name, such as Ahmed Ali.',
	driversEmpty: 'No drivers yet.',
	driversRuleWhat: 'A driver is only a name. He signs in nowhere.',
	driversRuleKeep:
		'Renaming a driver keeps him the same driver. Removing a driver hides him from the tills; his name is never given again in this shop.',
	driversRuleTill: 'A manager signed in on a till of this shop keeps the same list.',
	driversRemoved: 'Driver removed: %1',
	driverNameTaken: 'That name is taken in this shop, by a current or a removed driver.',
	driverNameBounds: 'A driver needs a name of one line, 1 to 40 characters.',
	driverGone: "That driver is no longer on this shop's list.",
	driverNotSaved: 'The driver could not be saved. Try again.',
	driversRemoveRefused: '%1 could not be removed, and is still on the list.',
	reportsTables: 'Tables',
	reportsByTable: 'Dine-in by table',
	reportsByTableNote: 'In table order, not ranked · orders beside sales',
	reportsTable: 'Table',
	reportsNoTable: 'No table',
	reportsTablesPickShop: 'Pick a shop to see its tables.',
	reportsTablesNone: 'This shop has no tables.',
	reportsTablesFair:
		'A large table or one by the door sells more, so each row shows its orders beside its sales.',
	reportsTablesZero: 'A table at 0 had no paid dine-in order in this period.',
	// Items sold by weight (#917): the unit words, the kilo price, the grams
	// typed on T6, the item's kind on D8, the report's counts on D13, and the
	// dashboard's band and refusal.
	unitGrams: 'g',
	unitKilo: 'kg',
	perKilo: '%1 / kg',
	editorGrams: 'Weight in grams',
	menuSoldBy: 'Sold by',
	menuByPiece: 'By the piece',
	menuByWeight: 'By weight',
	menuPricePerKilo: 'Price per kg',
	reportsPortions: 'portions',
	reportsPieces: 'pieces',
	menuGroupKind: 'Priced',
	menuGroupFixed: 'Fixed price',
	menuGroupPerKg: 'Per kg',
	menuPerKgSoon: 'Groups priced per kg can be added again in a few minutes.',
	menuWeighedSoon: 'Items sold by weight can be added again in a few minutes.',
	// Pick a driver (T33, UC-4.R6, UC-66) on Sell and Orders.
	sellPickDriver: 'Pick a driver',
	driversNoneYet: 'No drivers yet.',
	driversAskSomeone: 'No drivers yet. Ask a manager or the owner to add a driver.',
	driverLabel: 'Driver: %1',
	driverNone: 'No driver',
	driverChange: 'Change driver',
	driverChangeTitle: 'Change driver of %1',
	driverNeeded: 'Pick the driver this delivery goes with.',
	driverRemoved: 'That driver was removed. Pick another.',
	driverClosed: 'This order is closed; its driver stays.',
	driversFilterAll: 'All drivers',
} as const;

/** The Arabic catalogue must hold exactly the same keys as the English one. */
const ar: Record<keyof typeof en, string> = {
	ordersShift: 'الوردية',
	shiftTitle: 'الوردية',
	shiftOpenSince: 'مفتوحة من %1',
	shiftOpenedBy: 'فتحها %1',
	shiftFloat: 'فكة البداية',
	shiftNoneOpen: 'مفيش وردية مفتوحة.',
	shiftOpen: 'افتح الوردية',
	shiftClose: 'اقفل الوردية',
	shiftLastClosed: 'آخر وردية اتقفلت',
	shiftClosedBy: 'اتقفلت %1، قفلها %2',
	shiftApprovedBy: 'بموافقة %1',
	shiftOver: 'زيادة %1',
	shiftShort: 'عجز %1',
	shiftEven: 'مظبوط',
	shiftPrintAgain: 'اطبع تاني',
	shiftPrinting: 'بيطبع…',
	shiftPrintFailed: 'تقرير الوردية ما اتطبعش.',
	shiftLoading: 'بيحمّل الوردية',
	shiftLoadFailed: 'الوردية ما اتحمّلتش.',
	shiftGoesTo: 'فلوس الجهاز ده بتروح على %1.',
	shiftThereOpen: 'وردية %1 مفتوحة من %2.',
	shiftOpenQuestion: 'فيه كام كاش في الدرج؟',
	shiftOpenAmount: 'الكاش اللي في الدرج',
	shiftOpening: 'بيفتح…',
	shiftAlreadyOpen: 'فيه وردية مفتوحة بالفعل.',
	shiftNotCashPoint: 'فلوس الجهاز ده بتروح على جهاز تاني، فمابيفتحش ولا بيقفل وردية.',
	shiftTooLarge: 'المبلغ ده أكبر من اللي الجهاز ياخده.',
	shiftAskTitle: 'مبيعات الأجهزة التانية',
	shiftAsking: 'بيسأل…',
	shiftReceived: 'وصلت',
	shiftCannotReach: 'مش قادر يوصله',
	shiftAskAgain: 'اسأل تاني',
	shiftCloseAnyway: 'اقفل برضه',
	shiftNext: 'التالي',
	shiftCountQuestion: 'عِدّ الكاش اللي في الدرج',
	shiftCountAmount: 'الكاش اللي اتعد',
	shiftClosing: 'بيقفل…',
	shiftApprovalTitle: 'قفل الوردية على %1',
	shiftCloseForbidden: 'المدير بس هو اللي يقفل الوردية.',
	shiftAlreadyClosed: 'الوردية دي اتقفلت قبل كده.',
	shiftClosedTitle: 'الوردية اتقفلت',
	shiftExpected: 'المفروض',
	shiftCounted: 'اللي اتعد',
	shiftCashSales: 'مبيعات كاش',
	shiftCardSales: 'مبيعات كارت',
	shiftVoids: 'الملغي',
	shiftNoSaleOpens: 'فتح الدرج من غير بيع على الجهاز ده',
	shiftDue: 'مستحق عند القفل',
	shiftDueOrders: 'طلبات مفتوحة',
	shiftDueAmount: 'المبلغ',
	shiftDueNoDriver: 'من غير طيار',
	shiftDueNotDelivery: 'مش توصيل',
	shiftDueByDriverNote: 'توصيلات لسه مفتوحة على الجهاز ده، مش فلوس مع الطيار',
	shiftNotReached: 'ما وصلناش له: %1',
	shiftBackToShift: 'ارجع للوردية',
	shiftNoShiftHere: 'مفيش وردية مفتوحة. افتح واحدة عشان تاخد فلوس.',
	shiftNoShiftThere: 'مفيش دفع: مفيش وردية مفتوحة على %1.',
	shiftStale: 'مفيش دفع: الجهاز ده ما سمعش من %1 من 5 دقايق. اتأكد إن %1 شغال وعلى واي فاي المحل.',
	shiftUnreachable: 'مفيش دفع: الجهاز ده مش قادر يوصل لـ %1، اللي فلوسه بتروح عليه.',
	shiftVoidNoShift: 'مفيش وردية مفتوحة. افتح واحدة عشان تلغي طلب مدفوع.',
	shiftCashOut: 'فلوس طالعة',
	shiftCashIn: 'فلوس راجعة',
	shiftCashOutIn: 'فلوس طالعة / راجعة',
	shiftCashTotals: 'طالعة %1 · راجعة %2',
	shiftMovementBy: '%1 · سجّلها %2',
	shiftMovementByOnly: 'سجّلها %1',
	cashMoveQuestion: 'كام؟',
	cashMoveAmount: 'المبلغ',
	cashMoveWhy: 'ليه؟',
	cashMoveRecording: 'بيسجّل…',
	cashMoveNoShift: 'افتح وردية الأول.',
	cashMoveAmountBounds: 'اكتب مبلغ من 0.01 لحد أكبر مبلغ الجهاز ياخده.',
	cashMoveNoteNeeded: 'قول الفلوس دي طالعة أو راجعة ليه، في سطر واحد.',
	cashMoveReason: 'السبب',
	cashMoveOther: 'حاجة تانية',
	cashMoveDriverFloat: 'فكة الطيار',
	cashMoveDriver: 'أنهي طيار؟',
	cashMoveNoDrivers: 'مفيش طيارين لسه. اطلب من المدير يضيفهم.',
	cashMoveDriversFailed: 'الطيارين ما ظهروش.',
	cashMoveDriverNotFound: 'الطيار ده مش في قايمة الفرع.',
	cashMoveDriverRemoved: 'الطيار ده اتشال.',
	shiftMovementDriverBy: 'فكة الطيار: %1 · سجّلها %2',
	ownerShifts: 'الورديات',
	shiftsAllCashPoints: 'كل الخزن',
	shiftsCashPoint: 'الخزنة',
	shiftsOpened: 'اتفتحت',
	shiftsClosed: 'اتقفلت',
	shiftsOverShort: 'زيادة أو عجز',
	shiftsLate: 'متأخر',
	shiftsLateCount: '%1 متأخر',
	shiftsOpenChip: 'مفتوحة',
	shiftsShowOlder: 'اعرض الأقدم',
	shiftsEmpty: 'مفيش ورديات لسه.',
	shiftsNote: 'الأحدث الأول. الأرقام بتتحسب من جديد كل مرة، ومعاها المبيعات اللي وصلت متأخر.',
	shiftsReportTitle: 'وردية %1',
	shiftsOpenedLine: 'اتفتحت %1، فتحها %2',
	shiftsNoSaleOpensOn: 'فتح الدرج من غير بيع على %1',
	shiftsSalesHeading: 'المبيعات والملغي',
	shiftsTill: 'الجهاز',
	shiftsSale: 'بيع',
	shiftsVoid: 'إلغاء',
	shiftsArrivedAfterClose: 'وصل بعد القفل',
	shiftsNoSales: 'مفيش مبيعات في الوردية دي لسه.',
	shiftsMovementsHeading: 'فلوس طالعة وراجعة',
	shiftsCashOut: 'فلوس طالعة',
	shiftsCashIn: 'فلوس راجعة',
	shiftsMovementDriver: 'فكة الطيار: %1',
	shiftsMovementBy: 'سجلها %1',
	shiftsFiguresClamped: 'الأرقام أكبر من اللي بوصلة بتعرضه.',
	shiftsNoShift: 'من غير وردية',
	tillsCashPointSelf: 'الخزنة: الجهاز ده',
	tillsCashPointOther: 'الخزنة: %1',
	tillsCashPointInUse: '%1 بيبعت فلوسه على الجهاز ده. وجّهه لجهاز تاني الأول.',
	shiftsDueOn: 'مستحق عند القفل على %1',
	shiftsDueByDriver: 'المستحق عند القفل حسب الطيار',
	shiftsDueByDriverNote: 'توصيلات لسه مفتوحة على الجهاز ده، مش فلوس مع الطيار',
	shiftsDueNoDriver: 'من غير طيار',
	shiftsDueNotDelivery: 'مش توصيل',
	shiftsDriverCollected: 'حصّلها',
	shiftsDriverHanded: 'سلّمها',
	shiftsDriverShortExtra: 'عجز أو زيادة',
	shiftsDriverFloats: 'الفكة',
	shiftsDriverRefused: 'رجع/اترفض',
	shiftsDriverPaidDirect: 'اتدفع مباشر',
	shiftsDriverBalance: 'اللي معاه',
	shiftsDriverExtra: 'زيادة %1',
	shiftsDriverExtraNote: 'فلوس مش معروف مصدرها، مش إيراد ولا بقشيش',
	shiftsOlderLoading: 'بيحمّل…',
	tillsCashPointInUseMany: '%1 بيبعتوا فلوسهم على الجهاز ده. وجّههم لأجهزة تانية الأول.',
	tillsOnline: 'متصل',
	tillsOffline: 'مش متصل',
	tillsNeverSeen: 'ما ظهرش لسه',
	tillsLastSeen: 'آخر ظهور %',
	tillsSeenToday: 'النهاردة',
	tillsSeenYesterday: 'امبارح',
	tillsAppBuild: 'نسخة التطبيق %',
	tillsWaitingSalesOne: 'بيعة واحدة مستنية',
	tillsWaitingSalesNone: 'مفيش بيعات مستنية',
	tillsWaitingSalesTwo: 'بيعتين مستنيين',
	tillsWaitingSalesFew: '% بيعات مستنية',
	tillsWaitingSalesMany: '% بيعة مستنية',
	tillsWindows: 'ويندوز',
	tillsAndroid: 'أندرويد',

	printingTitle: 'الطباعة',
	printingSharedNote: 'إعدادات واحدة لكل الفروع. كل طباعة بتستخدم بيانات فرعها.',
	printingReceipt: 'إيصال العميل',
	printingKitchen: 'بون المطبخ',
	printingLanguage: 'لغة الطباعة',
	printingFont: 'حجم الخط',
	printingNormal: 'عادي',
	printingLarge: 'كبير',
	printingRoomy: 'مسافات زيادة',
	printingSeparators: 'فواصل بين الأصناف',
	printingLogo: 'شعار المطعم',
	printingDetails: 'تخصيصات الأصناف والملاحظات',
	printingOptionPrices: 'أسعار الإضافات',
	printingUnitPrices: 'سعر الوحدة',
	printingLineTotals: 'إجمالي كل صنف',
	printingQR: 'رمز QR لدليل الفرع',
	printingAutomatic: 'طباعة تلقائية بعد الدفع',
	printingIdentifiers: 'تكبير رقم الطلب والترابيزة',
	printingType: 'إبراز نوع الطلب',
	printingInstructions: 'تعليمات خاصة بخط عريض',
	printingGroupIdentical: 'تجميع الأصناف بنفس التخصيصات',
	printingGroupCategories: 'تجميع حسب قسم التجهيز',
	printingItemsNote: 'الأصناف والكميات بتظهر دايمًا.',
	printingSample: 'معاينة تجريبية',
	printingSampleNote: 'اختيار الفرع والورق للمعاينة بس، ومش بيغيّر إعدادات الفرع أو بيطبع حاجة.',
	printingShop: 'فرع المعاينة',
	printingPaper: 'عرض الورق',
	printingPaperWidth: '%1 mm',
	printingUnpublished: 'صفحة الفرع في الدليل مش منشورة. رمز QR مش هيظهر.',
	printingNoLogo: 'لسه مفيش شعار للطباعة. ضيفه هنا في جزء شعار المطعم.',
	printingLogoUpload: 'الشعار اللي بيتطبع',
	printingLogoRemoved: 'اتشال الشعار اللي بيتطبع',
	printingLogoUnreadable: 'شعار المطعم ماتقريش، فهيتطبع من غيره.',
	printingUpdating: 'بنحدّث المعاينة…',
	printingSaved: 'اتحفظت إعدادات الطباعة',
	printingOrder: 'ترتيب الطباعة من فوق لتحت',
	printingOrderNote:
		'اسحب الصف من المقبض، أو ركّز على المقبض واستخدم سهم فوق أو تحت. الأصناف والحساب والدفع لازم يفضلوا بالترتيب ده.',
	printingRefused: 'الحساب والدفع لازم يفضلوا تحت الأصناف، والدفع تحت الحساب. الترتيب ما اتغيرش.',
	printingRefusedLive: 'الحركة اترفضت',
	printingSectionMoved: '%1: بقى رقم %2 من %3',
	printingAddText: 'ضيف سطر كلام',
	printingAddSeparator: 'ضيف فاصل',
	printingCenter: 'في النص',
	printingShown: 'ظاهر',
	printingHidden: 'مخفي',
	printingBold: 'عريض',
	printingDelete: 'امسح',
	printingDashed: 'متقطع',
	printingBlank: 'فراغ',
	printingLessSpace: 'مسافة أقل',
	printingMoreSpace: 'مسافة أكتر',
	printingSpace: '%1 mm',
	printingSmallerLogo: 'شعار أصغر',
	printingLargerLogo: 'شعار أكبر',
	printingWidth: '%1%',
	printingOriginalSize: 'الحجم الأصلي',
	printingOneLine: 'في سطر واحد',
	printingDrag: 'اسحب عشان ترتّب',
	printingTextLine: 'سطر كلام',
	printingSeparator: 'فاصل',
	printingShopName: 'اسم الفرع',
	printingShopAddress: 'عنوان الفرع',
	printingShopPhone: 'تليفون الفرع',
	printingOrderDetails: 'بيانات الطلب',
	printingItems: 'الأصناف',
	printingTotals: 'الحساب',
	printingPayment: 'الدفع',
	shopsPhone: 'التليفون (اختياري)',
	shopsPhoneNote: 'بيتطبع على الإيصال لو موجود.',
	shopsPhoneInvalid: 'راجع رقم التليفون.',
	printingMoved: 'كلام الإيصال وشكله بقوا واحد لكل الفروع، من الإعدادات ← الطباعة.',
	printingOpenSettings: 'افتح إعدادات الطباعة',

	ownerShopSettings: 'إعدادات الفرع',
	ownerAccountSettings: 'إعدادات الحساب',
	ownerPublicPage: 'صفحة المطعم',
	ownerDelivery: 'مناطق التوصيل',
	ownerShopVisibility: 'الموقع وجاهزية الفرع',
	ownerReturnToTask: 'ارجع للمهمة السابقة',
	ownerShopMissing: 'الفرع ده مش متاح. اختار فرع عشان تكمل.',
	ownerAllCashiers: 'كل الموظفين',
	ownerAllTills: 'كل الأجهزة',
	ownerOpenPublicSettings: 'افتح إعدادات صفحة المطعم',
	ownerOpenShop: 'افتح الفرع',
	ownerBackToBusiness: 'نظرة عامة على المطعم',

	appName: 'الكاشير',
	pairTitle: 'اربط الكاشير ده',
	pairInstruction: 'اكتب الكود اللي ظاهر في لوحة التحكم.',
	pairCodeLabel: 'كود الربط',
	lockTitle: 'أدخل رمزك',
	offline: 'مفيش نت. البيع مش هيخلص لحد ما يرجع.',
	tillNotReady: 'الكاشير لسه بيجهز. جرّب تاني كمان شوية.',
	backspace: 'امسح',

	signInTitle: 'لوحة تحكم المطعم',
	signInInstruction: 'ادخل بجوجل، أو بالإيميل والباسورد.',
	signInWithGoogle: 'ادخل بجوجل',

	createTitle: 'اعمل مطعمك',
	createInstruction: 'دقيقة واحدة، وكل حاجة تتغيّر بعدين.',
	createName: 'اسم المطعم',
	createCountryCode: 'كود الدولة للتليفونات',
	createCountryCodeHint: 'من غير +. مصر 20.',
	createShopName: 'اسم أول فرع',
	createShopNameHint: 'لو سبته فاضي هيبقى «الرئيسي».',
	createSubmit: 'اعمل المطعم',
	createWorking: 'بيعمل المطعم…',

	ownerHome: 'الرئيسية',
	ownerShops: 'الفروع',
	ownerCashiers: 'الكاشيرية',
	ownerTills: 'الأجهزة',
	ownerSignOut: 'اخرج',

	shopsAdd: 'فرع جديد',
	shopsName: 'اسم الفرع',
	shopsAddress: 'العنوان',
	shopsDayStart: 'اليوم بيبدأ الساعة',
	shopsFee: 'توصيل من غير منطقة',
	shopsClose: 'اقفل الفرع',

	tillsAdd: 'جهاز جديد',
	tillsName: 'اسم الجهاز',
	tillsShop: 'الفرع',
	tillsSave: 'ضيف الجهاز',
	tillsRevoke: 'فك الربط',
	tillsEmpty: 'مفيش أجهزة لسه.',
	tillsCodeTitle: 'كود الربط ·',
	tillsCodeOnce: 'بيظهر مرة واحدة؛ لو قفلته مش هتشوفه تاني.',
	tillsCodeDone: 'قفلته',
	tillsUnpaired: 'مستني الربط',
	tillsNoShops: 'افتح فرع الأول.',

	pairFailed: 'الكود ده مش شغال. قارنه بلوحة التحكم.',
	pairChecking: 'بنربط الكاشير…',
	pairLocked: 'محاولات كتير. استنى دقيقة.',
	pairedAs: 'اتربط',

	cashiersAdd: 'كاشير جديد',
	cashiersName: 'الاسم',
	cashiersShop: 'الفرع',
	cashiersRole: 'الدور',
	cashiersRoleCashier: 'كاشير',
	cashiersRoleManager: 'مدير',
	cashiersPasscode: 'الرقم السري',
	cashiersPasscodeHint: '8 أرقام. مش هيظهر تاني بعد الحفظ.',
	cashiersPasscodeKeep: 'سيبه فاضي عشان يفضل زي ما هو',
	cashiersEdit: 'تعديل',
	cashiersEmpty: 'مفيش كاشيرية لسه.',
	cashiersNoShops: 'افتح فرع الأول.',
	cashiersNameTooLong: 'الاسم 22 حرف بالكتير.',

	lockEnterPasscode: 'اكتب الرقم السري',
	lockWrong: 'الرقم السري ده مش شغال هنا.',
	lockLocked: 'محاولات كتير. استنى وجرّب تاني.',
	lockLockedFor: 'محاولات كتير. جرّب تاني بعد',
	lockChecking: 'بنتأكد…',
	lockAccountLocked: 'أرقام غلط كتير في المطعم. الكل لازم يستنى.',
	lockLockNow: 'اقفل',
	lockSignOut: 'اخرج',
	headerOpenCount: 'مفتوح',
	headerOpenCountAria: '%1 مفتوح، افتح الطلبات المفتوحة',
	lockOrderWaiting: 'طلب مفتوح',
	lockUnsentSale: 'بيع ما اتأكدش',
	lockItems: 'أصناف',

	sellPay: 'ادفع',
	sellPrintBill: 'اطبع الحساب',
	sellBillPending: 'بيطبع…',
	sellBillChanged: 'الحساب اتغيّر بعد ما اتطبع',
	sellBillFailed: 'الحساب ما اتطبعش. اتأكد إن الطابعة شغّالة وفيها ورق.',
	sellType: 'نوع الطلب',
	sellEmpty: 'دوس على صنف عشان تبدأ.',
	sellSubtotal: 'المجموع',
	sellDelivery: 'التوصيل',
	sellTotal: 'الإجمالي',
	sellDineIn: 'صالة',
	sellTakeaway: 'تيك أواي',
	sellDeliveryType: 'دليفري',
	sellRemove: 'حذف',
	sellUndo: 'تراجع',
	sellRemoved: 'اتحذف',
	sellDiscount: 'خصم',
	discountTitle: 'الخصم',
	discountBefore: 'قبل الخصم',
	discountNone: 'من غير خصم',
	discountType: 'اكتب خصم',
	discountPercentNote: 'رقم صحيح. بيتقرّب لتحت لأقرب جنيه.',
	discountAmountNote: 'لو أكتر من الإجمالي، الطلب ببلاش.',
	discountTakesOff: 'بيخصم %1. الإجمالي الجديد %2.',
	discountTakesAll: 'بيخصم %1. الطلب كده ببلاش.',
	discountEmpty: 'مفيش خصومات لسه. المالك بيضيفها من لوحة التحكم.',
	discountManager: 'خصم المدير',
	discountManagerOnly: 'الخصم ده المدير بس اللي يقدر يديه.',
	discountRemoved: 'الخصم ده مبقاش موجود. اختار غيره، أو من غير خصم.',
	discountsDown: 'مينفعش تدي خصومات دلوقتي. شيل الخصم عشان تبيع.',
	discountRefused: 'الخصم ده مينفعش يتدي. غيّره، أو شيله عشان تبيع.',
	payFree: 'طلب ببلاش. «كاش مظبوط» بيخلّصه، والدرج مش هيتفتح.',
	doneFree: 'طلب ببلاش',
	ordersFree: 'ببلاش',
	ordersDiscount: 'الخصم: %1',
	ordersDiscountGiven: 'الخصم: %1 · إداه %2',
	sellSoldOut: 'خلص',
	sellQty: 'العدد',
	sellPendingTitle: 'البيع ما اتأكدش',
	sellPendingRetry: 'ابعته تاني',
	sellSend: 'ابعت',
	sellSent: 'اتبعت',
	sellSendFirst: 'ابعت الأول',
	sellNewOrder: 'طلب جديد',
	sellOrderFrom: 'من %1',
	ordersOpenOnSell: 'افتح',
	sellNoMenu: 'الفرع ده لسه مالوش منيو.',

	confirm: 'تأكيد',
	editorNote: 'ملاحظة',
	editorChooseOne: 'اختار 1',
	editorChooseAtLeast: 'اختار على الأقل',
	editorBelowZero: 'السعر أقل من صفر، كلّم المدير.',

	deliveryTitle: 'تفاصيل التوصيل',
	deliveryArea: 'المنطقة',
	deliveryNoArea: 'من غير منطقة',
	deliveryPhone: 'التليفون',
	deliveryAddress: 'العنوان',
	deliveryName: 'اسم العميل',
	deliveryPhoneRequired: 'التليفون مطلوب',
	deliveryPhoneCheck: 'راجع الرقم',
	deliveryStepPhone: 'تليفون العميل',
	deliveryStepWhere: 'التوصيل فين؟',
	deliveryNewCustomer: 'عميل جديد',
	deliveryNewAddress: 'عنوان جديد',
	deliverySuggestFrom: 'العملاء المحفوظين بيظهروا من الرقم الخامس',
	deliverySuggestions: 'العملاء المحفوظين',
	deliveryNewNumber: 'رقم جديد. «عميل جديد» تحت.',
	deliveryNoMatch: 'مفيش عميل محفوظ بالأرقام دي.',
	deliveryNoSavedAddress: 'مفيش عنوان محفوظ',
	deliveryRequired: 'مطلوب',
	deliveryAddressHint: 'الشارع ورقم العمارة',
	deliveryLandmark: 'علامة مميزة (اختياري)',
	deliveryLandmarkHint: 'مثلًا: الدور 3، شقة 7، جنب الصيدلية',
	deliveryReadBack: 'اقراه للعميل',
	deliveryNoAreaYet: 'لسه من غير منطقة، اختار تحت',

	payTitle: 'الدفع',
	payExact: 'كاش مظبوط',
	payCard: 'كارت',
	payWithDriver: 'مع الطيار',
	payTendered: 'المدفوع',
	payChange: 'الباقي',

	doneSending: 'بيبعت البيعة لبوصلة…',
	doneNotPrinted: 'الإيصال ما اتطبعش. جرّب «اطبع تاني».',
	doneRetry: 'جرّب تاني',
	doneDrawerFailed: 'الدرج ما اتفتحش.',
	donePrinting: 'بيطبع…',

	ordersTitle: 'الطلبات',
	ordersNumber: 'رقم',
	ordersStatus: 'الحالة',
	ordersBack: 'رجوع',
	ordersToday: 'الكل',
	ordersPaid: 'مدفوع',
	ordersVoid: 'ملغي',
	ordersOpen: 'مفتوح',
	ordersWaiting: 'مستني يتبعت',
	ordersWaitingTell: 'الكاشير شايل البيعة دي وهيبعتها لبوصلة أول ما يقدر.',
	ordersNotTaken: 'ما اتقبلش',
	ordersNotTakenTell: 'بوصلة ما قبلتش البيعة دي: %1 الكاشير لسه شايلها.',
	ordersNotTakenTotal: 'الإجمالي بتاعها مختلف عن حساب بوصلة.',
	ordersNotTakenInvalid: 'فيها حاجة مش مظبوطة.',
	ordersNotTakenHeld: 'بوصلة عندها البيعة دي بتفاصيل تانية.',
	ordersNotTakenNumber: 'فيه بيعة تانية من الكاشير ده بنفس الرقم النهارده.',
	ordersNotTakenOther: 'بوصلة عندها الطلب ده على كاشير تاني.',
	ordersNotTakenCode: 'السبب %1.',
	ordersCash: 'كاش',
	ordersDue: 'مستحق',
	ordersNoSale: 'من غير بيع',
	ordersEmpty: 'مفيش طلبات النهاردة لسه.',
	ordersReprint: 'اطبع تاني',
	ordersTakePayment: 'حصّل',
	ordersVoidAction: 'الغي',
	ordersOpenDrawer: 'افتح الدرج',
	ordersTestPrint: 'اختبر الطابعة',
	ordersPrinters: 'الطابعات',
	ordersNoReceiptPrinter: 'مفيش طابعة إيصالات',
	ordersNoDrawer: 'مفيش درج',
	appUpdateOffered: 'فيه نسخة أحدث من تطبيق بوصلة جاهزة للتنزيل: %1.',
	appUpdateAction: 'حدّث التطبيق',
	appUpdateDownloading: 'بننزّل التحديث.',
	appUpdateDownloadingAction: 'بينزّل…',
	appUpdateReady: 'التحديث نزل. هيتسطّب لما الكاشير يتقفل ومفيش حاجة لسه في السكة.',
	appUpdateInstallNow: 'سطّبه دلوقتي',
	appUpdateInstallWaiting: 'هيتسطّب أول ما الكاشير يفضى…',
	appUpdateStopped: 'التحديث ما نزلش.',
	appUpdateNotChecked: 'الكاشير ما قدرش يتأكد إن التحديث سليم، فما احتفظش بيه.',
	// Till settings and onboarding (#774, UC-59, T26/T27).
	tillSettings: 'إعدادات الجهاز',
	tillSettingsLoading: 'بيحمّل إعدادات الجهاز',
	tillSettingsFailed: 'ما قدرناش نحمّل إعدادات الجهاز.',
	tillRefreshTitle: 'حدّث من بوصلة',
	tillRefreshState: 'بيجيب أحدث منيو وأسعار وطابعات وطيارين دلوقتي',
	tillRefresh: 'حدّث',
	tillRefreshing: 'بيحدّث…',
	tillRefreshed: 'متحدّث',
	tillRefreshFailed: 'بوصلة ما ردّتش. جرّب تاني بعد شوية.',
	tillDriversTitle: 'الطيارين',
	tillDriversNone: 'لسه مفيش طيارين',
	tillDriversOne: 'طيار واحد',
	tillDriversMany: '%1 طيارين',
	tillDriversLoading: 'بيحمّل الطيارين',
	tillDriversLoadFailed: 'ما قدرناش نحمّل الطيارين.',
	tillDriversEmpty: 'لسه مفيش طيارين.',
	tillDriversAdd: 'طيار جديد',
	tillDriversName: 'اسم الطيار',
	tillDriversNameTaken: 'الاسم ده متاخد في الفرع ده، لطيار موجود أو اتشال.',
	tillDriversNameBounds: 'الطيار محتاج اسم في سطر واحد، من 1 لـ 40 حرف.',
	tillDriversNeedsInternet: 'أي تغيير في الطيارين محتاج إنترنت.',
	tillDriversForbidden: 'المدير بس هو اللي يغيّر الطيارين.',
	tillDriversRemoved: 'اتشال الطيار: %1',
	tillDriversGone: 'الطيار ده اتشال من الفرع.',
	tillSettingsItem: 'الإعداد',
	tillSettingsState: 'الحالة',
	setupOpen: 'افتح',
	setupPrintersNone: 'لسه مفيش طابعة متظبطة',
	setupPrinterOne: 'طابعة واحدة',
	setupPrintersTwo: 'طابعتين',
	setupPrintersMany: '%1 طابعات',
	setupTitle: 'جهّز الجهاز ده',
	setupSkip: 'تخطّي',
	setupNext: 'التالي',
	setupPrintersOpen: 'ظبط الطابعات',

	// T28: sharing with this shop's tills and the link test (UC-58, UC-59, #775).
	sharingTitle: 'المشاركة مع أجهزة الفرع',
	sharingIntro: 'أجهزة الفرع بتشارك ظبط الطابعات مع بعض على شبكة الفرع.',
	sharingOn: 'المشاركة شغّالة.',
	sharingNeedsYes: 'المشاركة محتاجة أدمن ويندوز يوافق مرة واحدة.',
	sharingOff: 'الجهاز ده مش بيشارك دلوقتي.',
	sharingRowOn: 'شغّالة',
	sharingRowNeedsYes: 'محتاجة موافقة ويندوز',
	sharingRowOff: 'مقفولة',
	sharingTill: 'الجهاز',
	sharingLinkColumn: 'الاتصال',
	sharingHeard: 'آخر تواصل %1',
	sharingHeardToday: 'النهاردة',
	sharingHeardYesterday: 'امبارح',
	sharingNotHeard: 'لسه ما وصلش منه حاجة',
	sharingLegacy: 'عليه نسخة أقدم من تطبيق بوصلة',
	sharingNoTills: 'مفيش جهاز تاني في الفرع نختبره.',
	sharingReadFailed: 'الجهاز ده ما قدرش يقرا حالة المشاركة.',
	linkTest: 'اختبر الاتصال',
	linkTestAgain: 'اختبر الاتصال تاني',
	linkTesting: 'بنختبر…',
	linkAnswered: '%1 ردّ.',
	linkNotHeard: 'الجهاز ده مش سامع %1. اتأكد إن %1 شغّال وعلى نفس الواي فاي اللي عليه الجهاز ده.',
	linkNoAnswer: '%1 ما ردّش. فيه واي فاي بيفصل الأجهزة عن بعض، وساعتها الأجهزة ما تقدرش توصل لبعض.',
	linkMeshOff: 'المشاركة مقفولة على الجهاز ده دلوقتي.',
	linkGone: '%1 ما بقاش من أجهزة الفرع.',
	cashPointTitle: 'الخزنة',
	cashPointQuestion: 'فلوس الجهاز ده بتروح فين؟',
	cashPointThisTill: 'الجهاز ده',
	cashPointNoOthers: 'مفيش جهاز تاني في الفرع شغّال خزنة.',
	cashPointTestFirst: 'اختبر الاتصال بـ%1 الأول.',
	cashPointHearing: 'بنتأكد من وردية %1…',
	cashPointReady: 'تأكيد هيبعت فلوس الجهاز ده على %1.',
	cashPointOlderApp: '%1 محتاج النسخة الأحدث من تطبيق بوصلة.',
	cashPointOpenHere: 'اقفل وردية الجهاز ده الأول.',
	cashPointInUse: 'فيه أجهزة تانية بتبعت فلوسها على الجهاز ده. وجّهها لجهاز تاني الأول.',
	cashPointNotAllowed: '%1 بيبعت فلوسه على جهاز تاني، فمايقدرش ياخد فلوس الجهاز ده.',
	cashPointForbidden: 'المدير بس اللي بيختار فلوس الجهاز ده تروح فين.',
	cashPointNeedsInternet: 'اختيار الخزنة محتاج إنترنت.',
	cashPointReadFailed: 'الجهاز ده ما قدرش يقرا فلوسه بتروح فين.',
	cashPointRemoved: 'اتشال',
	pairNextSetup: 'بعد كده، مدير يسجّل دخوله عشان يجهّز الجهاز.',
	printersTitle: 'الطابعات',
	printersBack: 'رجوع',
	printersEmpty: 'لسه مفيش طابعة متظبطة.',
	printersManagerSetsUp: 'المدير هو اللي بيظبط الطابعات على الجهاز.',
	printerPrintsReceipts: 'الإيصالات والحساب',
	printerPrintsKitchen: 'بونات المطبخ',
	printerPrintsAll: 'الإيصالات والحساب وبونات المطبخ',
	printerPaper58: '58 مم',
	printerPaper80: '80 مم',
	printerDrawer: 'درج الفلوس',
	printerYes: 'أيوه',
	printerNo: 'لأ',
	printerNotAnswering: 'مش بترد',
	printersAdd: 'ضيف طابعة',
	printersChange: 'عدّل',
	printersSetUpAgain: 'اظبطها من تاني',
	printerSetupTitle: 'ظبط الطابعة',
	printerAddressLabel: 'عنوان الطابعة',
	printerAddressHint: 'زي 192.168.1.50',
	printerTestSlip: 'اطبع بون تجربة',
	printerCodeQuestion: 'الرقم ده اتطبع؟',
	printerCodeLine: 'الرقم: %1',
	printerNameLabel: 'الاسم',
	printerNameHint: 'من 1 لـ 40 حرف',
	printerPrintsLabel: 'بتطبع',
	printerPaperLabel: 'الورق',
	printerDrawerOn: 'درج الفلوس على الطابعة دي',
	printerSave: 'احفظ',
	printerCancel: 'إلغاء',
	printerPurposeTaken: 'الطابعة «%1» بتطبع %2 أصلًا.',
	printerPurposeTakenAnon: 'فيه طابعة تانية بتطبع %1 أصلًا.',
	printerAlreadyAddedAnon: 'فيه طابعة متظبطة أصلًا على العنوان ده.',
	printerPurposeReceipts: 'الإيصالات والحساب',
	printerPurposeKitchen: 'بونات المطبخ',
	printerNameInUse: 'الاسم ده مستخدم لطابعة تانية.',
	printerAlreadyAdded: 'الطابعة «%1» متظبطة أصلًا على العنوان ده.',
	printerNoAnswer: 'مفيش طابعة بترد على العنوان ده.',
	printerCheckAddress: 'راجع العنوان وجرّب تاني.',
	printerForbidden: 'المدير بس هو اللي بيظبط الطابعات.',
	printerFailed: 'العملية ما تمتش. جرّب تاني.',
	printerSlipUnreachable: 'الطابعة مش بترد. اتأكد إنها شغالة وعلى نفس الشبكة.',
	printerSlipOtherPrinter: 'طابعة تانية هي اللي بترد على العنوان ده.',
	printNoticeNoReceiptPrinter: 'مفيش طابعة إيصالات متظبطة. المدير بيظبطها من الطلبات ← الطابعات.',
	printNoticeNoDrawer: 'مفيش درج متظبط على طابعة الإيصالات.',
	printNoticeKitchen: 'بون المطبخ للطلب %1 ما اتطبعش.',
	printNoticeBill: 'الحساب للطلب %1 ما اتطبعش.',
	printNoticeKitchenAnon: 'بون المطبخ ما اتطبعش.',
	printNoticeBillAnon: 'الحساب ما اتطبعش.',
	printNoticeAgain: 'اطبع تاني',
	printHeldKitchen: 'بون المطبخ للطلب %1: مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldBill: 'الحساب للطلب %1: مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldKitchenAnon: 'بون المطبخ: مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldBillAnon: 'الحساب: مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldReceipt: 'الإيصال: مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldDrawer: 'الدرج: مش أكيد إن أمر الفتح وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printHeldAsk: 'مش أكيد إنه وصل. ممكن اللي اتبعت قبل كده لسه يكمّل.',
	printDecide: 'قرّر',
	printWaitingDecision: 'مستني قرار',
	printHaveIt: 'معايا',
	printAnotherCopy: 'اطبع نسخة تانية',
	printDrawerOpened: 'الدرج اتفتح',
	printOpenDrawerNow: 'افتح الدرج دلوقتي',
	printDocReceipt: 'إيصال',
	printDocBill: 'حساب',
	printDocKitchen: 'بون مطبخ',
	printDocDrawer: 'درج',
	printDocShiftReport: 'تقرير الوردية',
	printHeldNoSale: 'من غير بيع',
	printDecidedAlready: 'في حد قرّر في الطباعة دي قبل كده.',
	printBillChangedCopy: 'الطلب اتغيّر بعد الحساب ده. اطبع حسابه تاني.',
	printDrawerTooLate: 'عدّت الدقيقتين بتوع فتح الدرج. استخدم «افتح الدرج» في الطلبات.',
	printerChangeTitle: 'عدّل «%1»',
	printerSetUpAgainTitle: 'عنوان جديد لـ«%1»',
	printerKeepsName:
		'الطابعة هتفضل باسمها وبتطبع نفس الحاجة. هنتأكد منها بورقة اختبار على العنوان الجديد.',
	printerBackAddress: 'العنوان',
	printerTryAgain: 'جرّب تاني',
	printerChangeAddress: 'غيّر العنوان',
	printerCodeNotPrinted:
		'يبقى اللي على العنوان ده مش الطابعة دي، أو الورقة ما طلعتش. جرّب تاني أو غيّر العنوان.',
	printerAddressNote: 'مكتوب على ورقة الإعدادات اللي الطابعة بتطبعها، زي 192.168.1.60',
	printerNameNote: 'زي: الكاشير، المطبخ',
	printerCodeIntro: 'الطابعة اللي على %1 المفروض طبعت الكود ده:',
	printerAtAddress: 'على %1',
	printerPaperUnit: 'مم',
	printerTestSlipPrinting: 'بيطبع ورقة الاختبار…',
	printersFromShop: 'الطابعات دي جاية من قايمة الفرع على بوصلة.',
	printersSetUpHere: 'ظبط الطابعات على الجهاز ده',
	printersSetUpHereAsk:
		'الجهاز ده هيبقى ليه طابعاته، حتى من غير نت. أجهزة الفرع التانية هتفضل على طابعاتها.',
	printersPrintInProgress: 'فيه طباعة طالعة دلوقتي. جرّب تاني كمان شوية.',
	printersOnThisTill: 'متظبطة على الجهاز ده',
	printerSetupStale: 'الطابعات اتغيرت على الجهاز ده. راجعها واحفظ تاني.',
	printersShared: 'متشاركة مع أجهزة الفرع',
	printersSharedFromAnother: 'اتظبطت على جهاز تاني في الفرع، %1',
	printersSharedFromHere: 'اتظبطت على الجهاز ده، %1',
	printersShareWaiting:
		'فيه جهاز تاني في الفرع عنده طابعات أحدث، بس الجهاز ده مش واصل لواحدة منها. الجهاز ده هيفضل على طابعاته لحد ما يوصلها.',
	printersShareNeedsFirewall:
		'المشاركة مع أجهزة الفرع محتاجة خطوة الجدار الناري في دليل تجهيز الجهاز.',
	printersShareTurnOn: 'شغّل المشاركة',
	printersShareAllowed: 'ويندوز سمح بالمشاركة. هتشتغل خلال دقيقة.',
	printersShareDeclined: 'ويندوز ماسمحش. لازم حد معاه باسورد أدمن ويندوز على الجهاز ده يوافق.',
	printersShareFailed:
		'ويندوز معرفش يشغّل المشاركة. استخدم خطوة الجدار الناري اللي في دليل ظبط الجهاز.',
	printersHostStarting:
		'برنامج الطباعة المرافق بيشتغل على الجهاز ده. لو فضل كده، اقفل بوصلة وافتحه تاني.',
	printerHostNotReady: 'برنامج الطباعة المرافق لسه مجهزش. جرّب تاني كمان شوية.',
	printerUsbOnThisTill: 'طابعة USB على الجهاز ده: %1',
	printerUsbPick: 'طابعة متوصلة بالجهاز ده',
	printerUsbOrAddress: 'أو اكتب عنوان طابعة على الشبكة',
	printerShare: 'شاركها مع أجهزة الفرع التانية',
	printerSharedAt: 'متشاركة على %1: على جهاز أقدم، ضيف طابعة على العنوان ده',
	printerShareFirewall: 'مستنية خطوة الجدار الناري اللي في دليل ظبط الجهاز',
	printerSharePortTaken: 'برنامج تاني شغال على المنفذ 9100',
	printerShareOffline: 'الطابعة مش شغالة',
	printerShareHelp: 'الطابعة المتشاركة بتطبع لأي جهاز على شبكة الفرع.',
	printerUsbOptionAnon: 'طابعة USB على الجهاز ده',
	printerUsbHelp:
		'طابعة الـUSB بتظهر هنا لما أندرويد يسمح لبوصلة تستخدمها: وصّلها واضغط سماح. علّم على «دايمًا» عشان أندرويد ما يسألش تاني.',
	printerCodeIntroUsb: 'طابعة الـUSB اللي على الجهاز ده المفروض طبعت الكود ده:',
	printerNeedsIdentifying: 'محتاجة تتعرف',
	printerNeedsIdentifyingNote: 'بوصلة مش متأكدة دي أنهي طابعة.',
	printersIdentify: 'عرّفها',
	printerIdentifyTitle: 'عرّف “%1”',
	printerIdentifyIntro: 'اختار الطابعة اللي تقصدها واطبع عليها بون تجربة. البون فيه رقم.',
	printerIdentifyNone:
		'مفيش طابعة تختارها دلوقتي. اتأكد إنها شغالة وعلى نفس الشبكة، أو ظبطها تاني بالعنوان.',
	printerIdentifyBack: 'الطابعات',
	printerIdentifyYes: 'أيوه، دي %1',
	printerIdentifyNotThis: 'يبقى البون مش طالع من الطابعة دي. اختار طابعة تانية، أو راجع دي.',
	printerIdentifyAnother: 'اختار واحدة تانية',
	printerModelLabel: 'الموديل',
	printerModelUnknown: 'مش معروف',
	ordersVoidWhy: 'ليه هتلغي طلب',
	ordersVoidWrong: 'طلب غلط',
	ordersVoidCustomer: 'العميل مشي',
	ordersVoidMistake: 'اتسجّل مرتين',
	ordersVoidQuality: 'الأكل رجع',
	cancelItem: 'إلغاء صنف',
	cancelHowMany: 'كام واحد؟',
	cancelOf: 'من',
	cancelLess: 'أقل',
	cancelMore: 'أكتر',
	cancelWhy: 'ليه؟',
	cancelVoids: 'ده هيلغي الطلب.',
	cancelNeedsManager: 'السبب هيفتح موافقة المدير.',
	cancelTitle: 'إلغاء %1 × %2 من طلب %3',
	voidTitle: 'إلغاء طلب %1',
	cardApprovalTitle: 'وافق على دفع %1 بالكارت',
	payCardAsksManager: 'الطلب ده مع طيار: الكارت محتاج موافقة المدير.',
	approvalTitle: 'موافقة المدير',
	approvalLead: 'المدير بيوافق بالرقم السري بتاعه. انت هتفضل داخل.',
	approvalManagers: 'المديرين',
	approvalOtherName: 'اسم تاني',
	approvalNoManager: 'مفيش مدير في الفرع ده. صاحب المطعم بيضيفه من لوحة التحكم.',
	approvalInternet: 'محتاج إنترنت. مدير داخل على الجهاز ده يقدر يوافق من غيره.',
	approvalManagerOnly: 'الموافقة للمدير بس',
	ordersVoidClosed: 'الطلب ده من يوم تاني، ومينفعش يتلغي.',
	ordersPaidAlready: 'الطلب ده اتدفع خلاص.',
	ordersFailed: 'ما عدّاش. جرّب تاني.',
	ordersLoadFailed: 'طلبات النهاردة ما اتحمّلتش.',

	doneChange: 'الباقي',
	doneOrder: 'طلب رقم',
	doneNext: 'البيعة الجاية',
	donePrintAgain: 'اطبع تاني',

	sellPriceChanged: 'السعر اتغيّر. راجع الإجمالي الجديد.',

	ownerMenu: 'المنيو',
	ownerSettings: 'الإعدادات',

	ownerHelp: 'المساعدة',
	helpIntro:
		'فيديوهات قصيرة لكل مهمة، متسجّلة على مطعم تجريبي — عمرها ما بتوري بياناتك. تقدر تفرّج كاشير عليها كمان. الفيديو بالمصري، سواء اللوحة بالمصري أو بالفصحى.',
	helpSectionLabel: 'القسم',
	helpGettingStarted: 'البداية',
	helpTill: 'البيع على الجهاز',
	helpMenu: 'المنيو',
	helpDayToDay: 'كل يوم',
	helpAccount: 'حسابك',
	helpOnTheTill: 'على الجهاز',
	helpOnTheDashboard: 'على لوحة التحكم',
	helpVideosOne: 'فيديو واحد',
	helpVideosTwo: 'فيديوهين',
	helpVideosFew: '% فيديوهات',
	helpVideosMany: '% فيديو',
	helpScenesOne: 'مشهد واحد',
	helpScenesTwo: 'مشهدين',
	helpScenesFew: '% مشاهد',
	helpScenesMany: '% مشهد',
	helpScenes: 'المشاهد',
	helpEmpty: 'لسه مفيش فيديوهات بالعربي.',
	helpVideoLoading: 'بيحمّل الفيديو',
	helpVideoFailed: 'الفيديو ده ما اتحمّلش.',
	helpRetry: 'جرّب تاني',
	helpPlay: 'شغّل',
	helpPause: 'وقّف',
	helpCaptions: 'الترجمة',
	helpFullscreen: 'ملء الشاشة',
	helpExitFullscreen: 'اخرج من ملء الشاشة',
	helpSeek: 'مكانك في الفيديو',

	menuAddCategory: 'قسم',
	menuAddItem: 'صنف جديد',
	menuCategoryName: 'اسم القسم',
	menuItemName: 'اسم الصنف',
	menuPriceHint: 'مثلًا 50 أو 12.50',
	menuCategory: 'القسم',
	menuAvailable: 'بيتباع',
	menuSoldOut: 'خلص',
	menuEmpty: 'لسه مفيش حاجة في المنيو.',
	menuNoCategories: 'ضيف قسم الأول.',
	menuCategoryRemoved: 'القسم اتحذف:',
	menuCategoryNotEmpty: '«%1» لسه فيه أصناف (%2). انقلها لقسم تاني أو احذفها، وبعدين احذف القسم.',
	menuOverrides: 'سعر وحالة كل فرع · فاضي يعني السعر الأساسي',
	menuBase: 'الأساسي',
	menuDescription: 'الوصف',
	menuDescriptionNote: 'لحد 300 حرف، على صفحة الدليل. السطور الجديدة بتفضل زي ما هي.',
	menuPhoto: 'الصورة',
	menuPhotoAlt: 'صورة الصنف',
	menuPhotoEmpty: 'مفيش صورة',
	menuPhotoAdd: 'ضيف صورة',
	menuPhotoReplace: 'غيّر الصورة',
	menuPhotoRemove: 'امسح',
	menuPhotoPreparing: 'بيجهّز…',
	menuPhotoNote:
		'PNG أو JPEG أو WebP، لحد 20 ميجا، و64×64 بكسل على الأقل. بنقصّها مربعة، لحد 1024×1024.',
	menuPhotoType: 'الصورة لازم تبقى PNG أو JPEG أو WebP.',
	menuPhotoSize: 'الصورة أكبر من 20 ميجا.',
	menuPhotoSmall: 'الصورة أصغر من 64×64 بكسل.',

	settingsLanguage: 'اللغة',
	settingsLanguageArabic: 'مصري',
	settingsLanguageMSA: 'فصحى',
	settingsLanguageEnglish: 'English',
	settingsCountryCode: 'كود الدولة لتليفونات العملاء',
	settingsIdleSeconds: 'اقفل الجهاز لو محدش لمسه (ثانية)',
	settingsSaved: 'اتحفظ.',

	areasTitle: 'مناطق التوصيل',
	areasAdd: 'منطقة جديدة',
	areasName: 'اسم المنطقة',
	areasFee: 'الرسوم',
	areasEmpty: 'مفيش مناطق. الفرع بياخد رسوم التوصيل بتاعته.',

	loading: 'بيحمّل',
	retry: 'حاول مرة أخرى',
	loadFailed: 'مش قادرين نوصل للوحة التحكم.',
	pairContinue: 'كمّل',
	pairHasDrawer: 'فيه درج فلوس',
	lockScreenLocked: 'الشاشة مقفولة. اكتب الرقم السري.',
	lockPasscode: 'الرقم السري',
	lockUnreachable: 'مش قادرين نوصل للسيرفر. اتأكد من النت.',
	lockUrgent: 'مستعجل',
	lockWho: 'مين بيفتح؟',
	lockWhoContinues: 'الشاشة مقفولة. مين هيكمّل؟',
	lockNames: 'الأسماء',
	lockNotYou: 'مش انت؟',
	lockNobody: 'مفيش حد يقدر يفتح هنا لسه',
	lockNobodyHint: 'صاحب المطعم بيضيف الكاشيرية من لوحة التحكم.',
	sellPendingBody: 'ابعته تاني هيبعت نفس البيعة، فمش هتتحسب مرتين.',
	sellSending: 'بيبعت…',
	sellLoadingMenu: 'بيحمّل المنيو',
	sheetClose: 'اقفل',
	priceInvalid: 'اكتب السعر كده: 50 أو 12.50.',
	priceDecimals: 'بعد العلامة العشرية % أرقام بس.',
	priceNegative: 'السعر مينفعش يبقى أقل من صفر.',
	priceLarge: 'السعر ده كبير أوي.',
	priceRequired: 'اكتب السعر.',
	mineTitle: 'أصنافي',
	mineCount: '% أصناف بترتيبك',
	mineEmpty: 'اختار الأصناف اللي بتطلبها أكتر.',
	mineEdit: 'عدّل أصنافي',
	mineWholeMenu: 'المنيو كله',
	mineOrder: 'بالترتيب ده ·',
	mineNone: 'ولا صنف لسه. علّم على صنف يتضاف في الآخر.',
	mineUp: 'لفوق',
	mineDown: 'لتحت',
	mineDrag: 'اسحب عشان ترتّب',
	mineSaving: 'بيحفظ…',
	mineGone: 'صنف مبقاش في المنيو.',
	editorFewer: 'قلّل',
	editorMore: 'زوّد',
	payTakeTitle: 'تحصيل طلب',
	payCashAmount: 'دفع كاش بمبلغ',
	payAmount: 'المبلغ',
	payFinishing: 'بيخلّص…',
	payFailedKept: 'البيعة دي ما عدّتش. الطلب لسه موجود.',
	donePaidByCard: 'اتدفع بالكارت',
	doneWithDriver: 'مع الطيار، لسه ما اتدفعش',
	ordersHeading: 'طلبات النهاردة',
	ordersTillHeading: 'طلبات الكاشير ده النهاردة',
	ordersTime: 'الوقت',
	ordersType: 'النوع',
	ordersCard: 'كارت',
	ordersEmptyFilter: 'مفيش طلبات بالحالة دي.',
	ordersDrawerAsk: 'تفتح الدرج من غير بيع؟',
	ordersDrawerOpen: 'افتح',
	ordersDrawerOpening: 'بيفتح…',
	ordersNo: 'لأ',
	ordersDetails: 'التفاصيل',
	ordersCloseDetails: 'اقفل',
	ordersVoidWhyEnd: '؟',
	ordersVoiding: 'بيلغي…',
	ordersVoidReason: 'سبب الإلغاء:',
	ordersLoadingDay: 'بيحمّل طلبات النهاردة',
	ordersPrinted: 'اتطبع',
	ordersPrintedOnce: 'مرة',
	ordersPrintedTimes: 'مرات',
	ordersChangeWord: 'باقي',
	ownerOrders: 'الطلبات',
	ownerShopsCount: 'فروع',
	edit: 'عدّل',
	remove: 'حذف',
	cancel: 'إلغاء',
	save: 'احفظ',
	saving: 'بيحفظ…',
	saved: 'اتحفظ.',
	yes: 'أيوه',
	no: 'لأ',
	signInOpening: 'بيفتح جوجل…',
	createExists: 'الحساب ده عنده مطعم بالفعل.',
	signInOr: 'أو',
	signInEmail: 'الإيميل',
	signInPassword: 'الباسورد',
	signInSubmit: 'ادخل',
	signInSigningIn: 'بيدخّلك…',
	signInNew: 'أول مرة؟',
	signInCreateAccount: 'اعمل حساب بالإيميل',
	signInFailed: 'الإيميل والباسورد دول مش متطابقين مع أي حساب.',
	signInUnverified: 'افتح اللينك اللي بعتناه على الإيميل ده عشان تخلّص حسابك. بعتنالك واحد جديد.',
	signInLocked: 'محاولات كتير. جرّب تاني بعد %1 دقيقة.',
	createAccountTitle: 'اعمل حساب',
	createAccountInstruction: 'بإيميل وباسورد بدل جوجل. الحساب بيبدأ لما تفتح اللينك اللي هنبعتهولك.',
	createAccountName: 'الاسم',
	passwordRule: 'استخدم باسورد من 15 لـ 128 حرف.',
	passwordShow: 'اظهره',
	passwordHide: 'اخفيه',
	createAccountLanguage: 'لغة الإيميل',
	createAccountSubmit: 'اعمل الحساب',
	createAccountCreating: 'بيعمل…',
	createAccountBadEmail: 'اكتب إيميل صحيح.',
	backToSignIn: 'ارجع للدخول',
	checkEmailTitle: 'شوف إيميلك',
	checkEmailBefore: 'بعتنا لينك على',
	checkEmailAfter: '. افتحه خلال 24 ساعة عشان تخلّص.',
	sendAgain: 'ابعته تاني',
	sending: 'بيبعت…',
	sentAgain: 'اتبعت تاني.',
	askAgainIn: 'تقدر تطلبه تاني بعد %1',
	linkChecking: 'بنتأكد من اللينك…',
	linkInvalidTitle: 'اللينك ده خلص أو اتستخدم قبل كده.',
	linkInvalidText: 'لو حسابك لسه ما خلصش، ادخل بالإيميل والباسورد الصح ونبعتلك لينك جديد.',
	signInForgot: 'نسيت الباسورد؟',
	signInEmailTaken:
		'الإيميل ده اتسجّل بطريقة تانية. ادخل بالطريقة اللي دخلت بيها أول مرة، وبعدين ضيف الطريقة دي من الإعدادات.',
	forgotTitle: 'نسيت الباسورد',
	forgotInstruction:
		'اكتب الإيميل ونبعتلك لينك تختار بيه باسورد جديد. لو بتدخل بجوجل، تقدر تضيف باسورد بنفس الطريقة.',
	forgotSubmit: 'ابعت اللينك',
	forgotSentBefore: 'لو',
	forgotSentAfter: ' عليه حساب، بعتناله لينك. اللينك بيشتغل 30 دقيقة.',
	resetTitle: 'اختار باسورد جديد',
	resetPassword: 'الباسورد الجديد',
	resetSubmit: 'احفظ وادخل',
	resetNote: 'هتدخل هنا، وهتخرج من أي جهاز تاني.',
	resetInvalidText: 'لينك تغيير الباسورد بيشتغل 30 دقيقة ومرة واحدة بس. اطلب واحد جديد.',
	methodsTitle: 'طرق الدخول',
	methodsNote: 'بتتضاف وتتشال من صفحتها',
	methodsCountOne: 'عندك طريقة واحدة للدخول: %1.',
	methodsCountTwo: 'عندك طريقتين للدخول: %1.',
	methodsCountMany: 'عندك %1 طرق للدخول: %2.',
	methodsAnd: ' و',
	methodsGoogleWord: 'جوجل',
	methodsPasswordWord: 'باسورد',
	methodGoogle: 'جوجل',
	methodPassword: 'باسورد',
	methodAdded: 'اتضافت %1',
	methodRemove: 'شيل',
	methodsLoading: 'بيحمّل طرق الدخول…',
	methodsLast: 'دي طريقتك الوحيدة للدخول، فمينفعش تتشال.',
	methodsSentBefore: 'شوف إيميلك:',
	methodsSentAfter: '. افتح اللينك عشان الباسورد يتضاف.',
	methodsAdd: 'ضيف',
	methodsAdding: 'بيضيف…',
	methodsAddGoogle: 'ضيف جوجل',
	methodsAddPassword: 'ضيف باسورد',
	methodsFoot:
		'أي طريقة تتضاف بنبعت عنها إيميل لعنوانك، عشان لو حد غيرك ضافها تاخد بالك. طريقة تانية للدخول هي اللي هترجّعك لو الأولى ضاعت.',
	methodsGoogleAdded: 'جوجل اتضاف.',
	methodsInUse: 'حساب جوجل ده تبع حساب بوصلة تاني.',
	methodsEmailTaken: 'الإيميل ده تبع حساب بوصلة تاني.',
	methodsUnfinished: 'جوجل ما كمّلش إضافة الحساب. جرّب تاني.',
	methodsRemoved: 'اتشالت: %1 · %2',
	createNamePlaceholder: 'مثلًا: زعفران',
	createShopDefault: 'الرئيسي',
	createSetForYou: 'العملة جنيه مصري والتوقيت توقيت القاهرة. بنظبطهم إحنا.',
	homeToday: 'النهاردة',
	homeShopOrders: 'طلبات الفرع',
	shopsDetails: 'بيانات الفرع',
	shopsDayStartBefore: 'الطلبات لحد',
	shopsDayStartAfter: 'الفجر بتتحسب على اليوم اللي قبله.',
	shopsOpen: 'افتح الفرع',
	shopsOpened: 'الفرع اتفتح.',
	shopsCloseNote: 'الطلبات القديمة بتفضل.',
	shopsClosed: 'اتقفل:',
	shopsLast: 'مينفعش تقفل آخر فرع مفتوح.',
	shopsDupe: 'فيه فرع تاني بالاسم ده.',
	areasNoArea: 'من غير منطقة:',
	receiptPreview: 'شكل الإيصال',
	cashiersResetFor: 'رقم سري جديد لـ',
	cashiersMoveWarn: 'نقله لفرع تاني بيخرّجه من كل الأجهزة.',
	cashiersPasscodeAgain: 'اكتبه تاني',
	cashiersPasscodeInUse: 'فيه كاشير تاني بالرقم ده. كل رقم لازم يبقى مختلف.',
	cashiersPasscodeDiffer: 'الرقمين مش زي بعض.',
	cashiersPasscodeMatch: '✓ زي بعض',
	cashiersAddAction: 'ضيف',
	cashiersNewPasscode: 'رقم سري جديد',
	cashiersPasscodeChanged: 'الرقم السري اتغيّر.',
	cashiersRemoved: 'اتحذف:',
	cashiersSignedOut: 'اتخرّج من كل الأجهزة.',
	tillsNamePlaceholder: 'مثلًا: كاشير 2',
	tillsDrawerQ: 'فيه درج فلوس متوصّل؟',
	tillsDrawer: 'درج',
	tillsAdding: 'بيضيف…',
	tillsCodeExpires: 'اكتبه على الجهاز. بيخلص بعد',
	tillsCodeExpired: 'الكود خلص. اعمل جهاز جديد أو اطلب كود تاني.',
	tillsCount: 'أجهزة',
	tillsPaired: 'مربوط',
	tillsHasDrawer: 'فيه درج',
	tillsNoDrawer: 'من غير درج',
	tillsRevoked: 'اتفك:',
	tillsStopped: 'وقف على طول.',
	menuItems: 'الأصناف',
	menuOptions: 'الإضافات',
	menuCategories: 'الأقسام',
	menuUp: 'لفوق',
	menuDown: 'لتحت',
	menuItemsIn: 'أصناف في',
	menuCategoryEmpty: 'مفيش أصناف في القسم ده.',
	menuItem: 'الصنف',
	menuStatus: 'الحالة',
	menuEditTitle: 'تعديل',
	menuBasePrice: 'السعر الأساسي',
	menuItemOptions: 'الإضافات على الصنف ده',
	menuShopPrice: 'سعر الفرع',
	menuShopAvailability: 'حالة الفرع',
	menuUseBase: 'زي الأساسي',
	menuRemoveItem: 'حذف الصنف',
	menuItemRemoved: 'الصنف اتحذف. الطلبات القديمة بتفضل.',
	menuNewOption: 'إضافة جديدة',
	menuOption: 'الإضافة',
	menuMin: 'أقل عدد',
	menuMax: 'أكتر عدد',
	menuOnTill: 'على الكاشير',
	menuOnTillColon: 'على الكاشير:',
	menuRemoveOption: 'حذف الإضافة',
	menuChoice: 'اختيار',
	menuChoiceName: 'الاختيار',
	menuPriceChange: 'فرق السعر',
	menuAddOrTake: 'زوّد أو خصم',
	menuAdd: 'ضيف',
	menuEditOption: 'عدّل الإضافة',
	menuChoiceNote: 'الاختيار مبيتعدّلش. عشان تغيّر سعره احذفه وضيفه تاني. متاح على:',
	menuNoItemsYet: 'ولا صنف لسه',
	menuRangeError: 'لازم يبقى 1 على الأقل، ومش أقل من «أقل عدد».',
	ruleRequiredOne: 'لازم، اختار واحد',
	ruleOptionalOne: 'اختياري، واحد بالكتير',
	ruleOptionalUpTo: 'اختياري، لحد %1',
	ruleExactly: 'اختار %1 بالظبط',
	ruleAtLeastUpTo: 'لازم %1 على الأقل، لحد %2',
	ordersAllShops: 'كل الفروع',
	ordersShop: 'الفرع',
	ordersTradingDay: 'اليوم بيبدأ %1 الفجر',
	ordersTillOnly: 'الإلغاء من الكاشير بس.',
	ownerReports: 'التقارير',
	reportsPeriod: 'الفترة',
	reportsToday: 'النهاردة',
	reportsYesterday: 'امبارح',
	reportsLast7: 'آخر 7 أيام',
	reportsMonth: 'الشهر ده',
	reportsLastMonth: 'الشهر اللي فات',
	reportsRange: 'من … لحد',
	reportsFrom: 'من',
	reportsTo: 'لحد',
	reportsUpTo: 'لحد 366 يوم.',
	reportsInvalid: 'الفترة بتخلص قبل ما تبدأ.',
	reportsTooLong: 'التقرير بيغطي 366 يوم بالكتير.',
	reportsDay1: 'يوم واحد',
	reportsDay2: 'يومين',
	reportsDaysFew: '%1 أيام',
	reportsDaysMany: '%1 يوم',
	reportsPart: 'جزء التقرير',
	reportsOverview: 'الإجمالي',
	reportsItems: 'الأصناف',
	reportsHours: 'الساعات',
	reportsLoading: 'بيحمّل التقرير',
	reportsEmpty: 'مفيش مبيعات في الفترة دي.',
	reportsTotals: 'الإجمالي',
	reportsTotalsNote: 'بيطابق ملخص اليوم على الكاشير',
	reportsPaid: 'الطلبات المدفوعة',
	reportsNet: 'صافي المبيعات',
	reportsAverage: 'متوسط الطلب',
	reportsFees: 'رسوم التوصيل',
	reportsMix: 'كاش وكارت',
	reportsCash: 'كاش',
	reportsCard: 'كارت',
	reportsVoid: 'ملغي',
	reportsOpen: 'مفتوح، مستحق',
	reportsNoSale: 'فتح الدرج من غير بيع',
	reportsExplain:
		'صافي المبيعات هو الطلبات المدفوعة بس، بعد خصمها، والتوصيل جوّاه. المفتوح مستحق مش مبيوع، والملغي محسوب لوحده. كل طلب بيتحسب على اليوم التجاري لفرعه (بيبدأ %1 الفجر).',
	reportsByType: 'حسب نوع الطلب',
	reportsType: 'النوع',
	reportsOrders: 'الطلبات',
	reportsItemsSold: 'الأصناف المتباعة',
	reportsItemsNote: 'الأكتر مبيعًا الأول · بسعر المنيو، قبل خصم الطلب، من غير التوصيل',
	reportsItem: 'الصنف',
	reportsQty: 'الكمية',
	reportsRevenue: 'الإيراد',
	reportsZero: 'ما اتباعش في الفترة دي · %1',
	reportsItemsTotal: 'المجموع بسعر المنيو، قبل الخصومات والتوصيل',
	reportsSoldOut: 'خلص دلوقتي',
	reportsSoldOutAt: 'خلص في %1',
	reportsAdded: 'اتضاف %1',
	reportsGone: 'مبقاش في المنيو',
	reportsByHour: 'حسب الساعة',
	reportsHour: 'الساعة',
	reportsHoursNote: 'من %1 الفجر · الأزحم بالطلبات',
	reportsAm: 'ص',
	reportsPm: 'م',
	reportsDiscounts: 'الخصومات',
	reportsDiscountsOn1: 'الخصومات · على %1 طلب',
	reportsDiscountsOnFew: 'الخصومات · على %1 طلبات',
	reportsDiscountsOnMany: 'الخصومات · على %1 طلب',
	reportsDiscountsNone: 'مفيش خصومات في الفترة دي.',
	reportsEachDiscount: 'كل خصم',
	reportsEachDiscountNote: 'الطلبات المدفوعة · خصم قد إيه',
	reportsDiscount: 'الخصم',
	reportsTookOff: 'اتخصم',
	reportsRemoved: 'اتحذف',
	reportsDiscountsTotal: 'المجموع · الطلبات المدفوعة اللي عليها خصم',
	reportsByCashier: 'الخصومات حسب الكاشير',
	reportsByCashierNote: 'الأكتر خصمًا الأول · والتعادل بالاسم',
	reportsCashier: 'الكاشير',
	reportsWithDiscount: 'عليها خصم',
	reportsCashierNote:
		'ده مش مبيعات. الخصم اختيار الكاشير، فهنا تراجعه على قواعد المحل. اللي إدى أكتر بيجي الأول. الوردية الزحمة بتدي أكتر في المجموع، فكل كاشير جنب الطلبات اللي عملها وكام منها عليه خصم. مبيعات الكاشير مش بتظهر أبدًا.',
	settingsIdleNote: 'الجهاز يتقفل بعد %1 ثانية من غير لمس. من 1 لـ 3600.',
	settingsQuick: 'مبالغ الكاش السريعة · لحد 8',
	settingsAmount: 'مبلغ',
	settingsExampleBefore: 'لو الإجمالي',
	settingsExampleAfter: 'الكاشير يشوف:',
	settingsCurrency: 'العملة',
	settingsTimezone: 'التوقيت',
	settingsTheme: 'المظهر',
	themeLight: 'فاتح',
	themeDark: 'غامق',
	themeSystem: 'زي الجهاز',
	settingsBrowserTitle: 'المتصفح ده بس: اللغة والمظهر',
	settingsBrowserNote: 'من غير حفظ',
	settingsBrowserExplain:
		'الاتنين بيتطبقوا على طول. بيغيّروا المتصفح ده بس، مش الأجهزة ولا أي متصفح تاني. اللغة بتبدأ بلغة المطعم، والمظهر بيبدأ «زي الجهاز».',
	settingsRestaurantTitle: 'المطعم',
	settingsRestaurantNote: 'لكل الأجهزة والإيصالات',
	settingsRestaurantLanguage: 'لغة المطعم',
	settingsRestaurantLanguageNote: 'الإيصالات بتتطبع بيها، وأي شاشة بتبدأ بيها لحد ما تختار لغتها.',
	settingsTillsTitle: 'الأجهزة',
	settingsTillsNote: 'لكل الأجهزة',
	settingsSaveNote: 'بيحفظ المطعم والأجهزة مع بعض.',
	settingsFixedTitle: 'ثابت للمطعم ده',
	settingsFixedNote: 'مبيتغيّرش',
	createLanguageNote: 'الإيصالات بتتطبع بيها، وأي شاشة بتبدأ بيها لحد ما تختار.',
	tillDisplayTitle: 'اللغة والمظهر',
	tillDisplayNote:
		'للجهاز ده بس، وبيتطبقوا على طول من شاشة القفل. مش بيغيّروا اللوحة ولا أي جهاز تاني.',
	customerSaved: 'عناوينه',
	customerEdit: 'عدّل العميل',
	customerEditNeedsInternet: 'تعديل العميل محتاج إنترنت.',
	customerEditAddress: 'عدّل',
	customerSaveAddress: 'احفظ العنوان',
	customerAddressRemoved: 'العنوان اتحذف:',
	customerAddressRequired: 'العنوان مطلوب.',
	customerAddressTaken: 'العنوان ده محفوظ له قبل كده.',
	customerAreaOf: 'المنطقة · فرع %1',
	customerTitle: 'بيانات العميل',
	customerSaveName: 'احفظ الاسم',
	customerNameRequired: 'الاسم مطلوب.',
	customerAddresses: 'العناوين المحفوظة',
	customerNoAddresses: 'مفيش عناوين محفوظة.',
	customerTillNote:
		'التعديل هنا بيتحفظ على العميل نفسه، مش على الطلب ده بس. الطلبات القديمة بتفضل بالعنوان اللي اتعملت بيه. مسح العميل كله من لوحة صاحب المطعم بس.',
	customerGone: 'العميل ده مبقاش موجود.',
	ownerCustomers: 'العملاء',
	customersLoading: 'بيحمّل العملاء',
	customersEmpty: 'مفيش عملاء لسه. الكاشير بيحفظ عميل كل ما يخلّص تفاصيل توصيل.',
	customersSearch: 'دوّر بالتليفون أو الاسم',
	customersNoMatch: 'مفيش حد بالبحث ده.',
	customersListNote: 'آخر طلب الأول · 50 في المرة',
	customersName: 'الاسم',
	customersPhone: 'التليفون',
	customersOrders: 'الطلبات',
	customersLast: 'آخر طلب',
	customersNoName: 'من غير اسم',
	customersOpen: 'افتح',
	customersDelete: 'امسح العميل',
	customersNoAddresses: 'مفيش عناوين محفوظة. كل طلباته تيك أواي أو صالة.',
	customersDeleteNote:
		'المسح بيحذف العميل وعناوينه. تعديل أو حذف عنوان ما بيغيّرش الطلبات القديمة: بتفضل بالاسم والتليفون والعنوان اللي اتعملت بيهم.',
	customersDeleted: 'العميل اتمسح:',
	whenToday: 'النهاردة',
	whenYesterday: 'امبارح',

	suspendedLabel: 'الحساب موقوف',
	suspendedTillTitle: 'بوصلة وقّفت حساب المطعم ده.',
	suspendedTillTell: 'بلّغ صاحب المطعم.',
	suspendedTillContact: 'دعم بوصلة:',
	suspendedOwnerTitle: 'بوصلة وقّفت حساب مطعمك.',
	suspendedOwnerTills: 'وأجهزة الكاشير بتاعتك وقفت هي كمان.',
	suspendedOwnerContact: 'كلّم بوصلة:',
	suspendedWhatsapp: 'واتساب',
	suspendedCheck: 'اتأكد تاني',
	suspendedChecking: 'بنتأكد…',
	suspendedTillResumes: 'لما بوصلة ترجّع الحساب، الرسالة دي هتختفي لوحدها في خلال دقايق.',
	tillNetDown: 'مفيش نت. البيع بيكمل، وبيوصل لبوصلة لما النت يرجع.',
	tillNetDownOne: 'مفيش نت. بيعة واحدة هتوصل لبوصلة لما النت يرجع.',
	tillNetDownTwo: 'مفيش نت. بيعتين هيوصلوا لبوصلة لما النت يرجع.',
	tillNetDownMany: 'مفيش نت. % بيعات هيوصلوا لبوصلة لما النت يرجع.',
	tillWaitingOne: 'بيعة واحدة لسه ما وصلتش لبوصلة.',
	tillWaitingTwo: 'بيعتين لسه ما وصلوش لبوصلة.',
	tillWaitingMany: '% بيعات لسه ما وصلتش لبوصلة.',
	removedLabel: 'الجهاز اتشال من الفرع',
	removedTitle: 'الجهاز ده اتشال من الفرع.',
	removedAsk: 'اسأل صاحب المطعم.',
	removedWaitOne: 'بيعة واحدة لسه ما وصلتش لبوصلة. سيب الجهاز شغال ومتوصل بالنت لحد ما توصل.',
	removedWaitTwo: 'بيعتين لسه ما وصلوش لبوصلة. سيب الجهاز شغال ومتوصل بالنت لحد ما يوصلوا.',
	removedWaitMany: '% بيعات لسه ما وصلتش لبوصلة. سيب الجهاز شغال ومتوصل بالنت لحد ما يوصلوا.',
	removedDone: 'كل بيعات الجهاز ده وصلت لبوصلة.',
	removedOpenOne: 'فيه طلب مفتوح واحد على الجهاز ده، ومينفعش يتدفع هنا.',
	removedOpenTwo: 'فيه طلبين مفتوحين على الجهاز ده، ومينفعش يتدفعوا هنا.',
	removedOpenMany: 'فيه % طلبات مفتوحة على الجهاز ده، ومينفعش تتدفع هنا.',
	removedReturns: 'لو بوصلة رجّعت الجهاز، الرسالة دي هتختفي لوحدها.',
	shellNoticeLabel: 'الكاشير محتاج تطبيق بوصلة',
	shellNoticeTitle: 'الكاشير بيشتغل في تطبيق بوصلة.',
	shellNoticeTell: 'افتح الكاشير من تطبيق بوصلة على جهاز الكاشير.',
	shellNoticeOwner: 'صاحب المطعم بيدير المطعم من لوحة التحكم.',
	shellNoticeDashboard: 'افتح لوحة التحكم',
	shellNoticeSetup: 'بتجهّز كاشير؟ نزّل عليه تطبيق بوصلة.',
	shellNoticeWindows: 'نزّل لويندوز',
	shellNoticeAndroid: 'نزّل لأندرويد',
	shellNoticeWindowsWarn: 'ممكن ويندوز يحذّرك: دوس More info وبعدين Run anyway.',
	storeStartingLabel: 'الكاشير بيجهز',
	storeStartingTitle: 'الكاشير بيجهز.',
	storeStartingTell: 'البيع هيبدأ كمان لحظة.',
	storeDownTitle: 'الكاشير بيعيد التشغيل.',
	storeDownTell: 'كل البيع اللي اتحفظ عليه في أمان. هيرجع كمان لحظة.',
	storeStillDown: 'لسه مستني؟ اقفل تطبيق بوصلة وافتحه تاني. ولو مانفعش، كلّم بوصلة.',

	subscriptionEndsOn: 'اشتراكك في بوصلة بيخلص يوم %1، كمان %2.',
	subscriptionEndsToday: 'اشتراكك في بوصلة بيخلص النهارده.',
	subscriptionEnded: 'اشتراكك في بوصلة خلص يوم %1.',
	subscriptionOneDay: 'يوم واحد',
	subscriptionTwoDays: 'يومين',
	subscriptionFewDays: '% أيام',
	subscriptionManyDays: '% يوم',
	subscriptionHowToRenew: 'إزاي تجدّد',
	subscriptionTitle: 'اشتراك بوصلة',
	subscriptionContact: 'كلّم بوصلة عشان تجدّد:',
	subscriptionOnlyBosla: 'بوصلة بس اللي تقدر تغيّر التاريخ ده. وكل حاجة بتفضل شغالة بعده.',
	subscriptionClose: 'اقفل',
	visitViewing: 'بتشوف لوحة %1 بصفتك صاحبه لحد %2',
	visitExit: 'خروج',
	signInVisitEnded: 'اللينك ده خلص. افتح لوحة صاحب المطعم من صفحة الأدمن تاني.',

	ownerCatalogue: 'الدليل',
	catalogueLoading: 'بيحمّل الدليل',
	cataloguePublished: 'منشور في الدليل',
	catalogueOpen: 'افتح الصفحة',
	catalogueNotPublished: 'مش منشور',
	catalogueNotPublishedNote: 'الزباين مش شايفين المطعم في الدليل.',
	catalogueMissingTitle: 'عشان تنشر، ناقصك:',
	catalogueMissingLogo: 'لوجو',
	catalogueMissingLogoWhy: 'الدليل بيعرض لوجو كل مطعم جنب اسمه، والزبون بيعرف المطعم بيه.',
	catalogueMissingShop: 'فرع على الخريطة',
	catalogueMissingShopWhy:
		'لازم فرع واحد على الأقل بعنوان ومكان على الخريطة، عشان الزبون يعرف يروح فين.',
	catalogueMissingShopAct: 'حط فرع',
	catalogueIncomplete: 'المطعم ما اتنشرش. ناقص: %.',
	catalogueIncompleteLogo: 'لوجو',
	catalogueIncompleteShop: 'فرع بعنوان ومكان على الخريطة',
	catalogueAnd: '، و',
	cataloguePart: 'جزء الدليل',
	cataloguePartPage: 'الصفحة',
	cataloguePartShops: 'الفروع على الخريطة',
	catalogueInIt: 'في الدليل',
	catalogueListed: 'منشور',
	catalogueUnpublishNote:
		'هيتشال من الدليل لما تحفظ. العنوان بيفضل بتاعك، واللينك يرجع يشتغل لما تنشر تاني.',
	catalogueLockedNote:
		'«منشور» بيتفتح لما يبقى فيه لوجو وفرع على الخريطة. تقدر تحفظ الصفحة من غيرهم.',
	listingSlug: 'عنوان الصفحة',
	listingSlugNote: 'من 3 لـ 40 حرف إنجليزي وأرقام وشَرطة.',
	listingName: 'الاسم',
	listingNameRequired: 'الاسم مطلوب.',
	listingArea: 'المنطقة (اختياري)',
	listingAreaNote: 'مثلًا: الزمالك · المعادي. الناس بتدوّر بيها.',
	listingSlugInvalid: 'من 3 لـ 40 حرف إنجليزي وأرقام وشَرطة، ويبدأ ويخلص بحرف أو رقم.',
	listingSlugTaken: 'فيه مطعم تاني بالعنوان ده. اختار غيره.',
	listingNameInvalid: 'الاسم من 1 لـ 80 حرف.',
	listingAreaInvalid: 'المنطقة 120 حرف بالكتير.',
	logoTitle: 'اللوجو',
	logoCardNote: 'بيظهر جنب الاسم',
	logoAlt: 'لوجو المطعم',
	logoEmpty: 'مفيش لوجو',
	logoAdd: 'ضيف لوجو',
	logoReplace: 'غيّر اللوجو',
	logoRemove: 'حذف',
	logoUploading: 'بيرفع…',
	logoNote: 'PNG أو JPEG، لحد 2 ميجا، و64×64 بكسل على الأقل. بنظبطه على 512×512.',
	logoLockedNote: 'المطعم منشور: تقدر تغيّر اللوجو، بس متقدرش تحذفه.',
	logoType: 'اللوجو لازم يبقى PNG أو JPEG.',
	logoSize: 'الصورة أكبر من 2 ميجا.',
	logoSmall: 'الصورة أصغر من 64×64 بكسل.',
	logoRemoved: 'اللوجو اتحذف.',
	placesShown: '%1 من %2 ظاهرين',
	placeNoAddress: 'مفيش عنوان',
	placeShown: 'ظاهر في الدليل',
	placeHidden: 'مش في الدليل',
	placeLacks: 'ناقصه: %',
	placeLacksAddress: 'العنوان',
	placeLacksPlace: 'مكان على الخريطة',
	placeAnd: ' و',
	placeAddressInShops: 'العنوان من الفروع',
	placeEdit: 'عدّل المكان',
	placeAdd: 'حط على الخريطة',
	placeMapLabel: 'مكان فرع % على الخريطة',
	placeMapHint: 'دوس على مكان الفرع في الخريطة',
	placeZoomIn: 'قرّب',
	placeZoomOut: 'بعّد',
	placeLatitude: 'خط العرض',
	placeLongitude: 'خط الطول',
	placeDegrees: 'اكتبه بالدرجات، زي 30.044420.',
	placeLatitudeRange: 'خط العرض من -90 لـ 90.',
	placeLongitudeRange: 'خط الطول من -180 لـ 180.',
	placeRequired: 'مطلوب.',
	placeHelp:
		'دوس على الخريطة أو اسحب الدبوس، أو اكتب الرقمين بالدرجات. من خرائط جوجل: دوس على المكان والرقمين بيظهروا فوق.',
	placeRemove: 'حذف المكان',
	placeSave: 'احفظ المكان',
	placeRemoved: 'اتحذف مكان فرع %.',
	placesFoot:
		'الفرع بيظهر في الدليل لما يبقى ليه عنوان ومكان على الخريطة. العنوان بيتغيّر من الفروع. الزبون بيشوف العنوان، ولينك بيفتح المكان على خرائط جوجل.',
	ownerDiscounts: 'الخصومات',
	discountsAdd: 'خصم جديد',
	discountsIntro: 'جهاز كل فرع بيعرض اللستة دي. الكاشير بيدي خصم واحد للطلب، بضغطتين.',
	discountsCount: '% · بالاسم',
	discountsEmpty: 'مفيش خصومات لسه. ضيف واحد، والكاشيرية يقدروا يدوه على الجهاز.',
	draftChangesOne: 'تعديل واحد مش منشور',
	draftChangesTwo: 'تعديلين مش منشورين',
	draftChangesFew: '% تعديلات مش منشورة',
	draftChangesMany: '% تعديل مش منشور',
	draftPublish: 'انشر المنيو',
	draftWaiting: 'المنيو الجديد بيبدأ يوم %',
	draftWhen: '%1 الساعة %2',
	draftClean: 'الأجهزة عليها المنيو ده.',
	draftCancelled: 'المنيو المتجدول اتلغى. تعديلاته رجعت للمسودة.',
	draftTooLate: 'المنيو ده بدأ خلاص.',
	priceCheckTitle: 'راجع الأسعار دي',
	priceCheckText: 'الأسعار دي بعيدة أوي عن اللي قبلها.',
	priceCheckFailed: 'مقدرناش نراجع الأسعار. اللي كتبته لسه زي ما هو.',
	priceAtShop: '%1 في %2',
	publishPending: 'بينشر…',
	publishLoading: 'بيحمّل المنيو',
	publishInEffect: 'على الأجهزة دلوقتي: منيو %1، من يوم %2.',
	publishWaitingTitle: 'مستني ميعاده: منيو %1، يوم %2',
	publishChangesTitle: 'اللي هيتغيّر على الأجهزة',
	publishPricesRight: 'الأسعار دي صح.',
	publishBeforeTitle: 'قبل ما تنشر',
	publishTills: 'الأجهزة اللي على النت بيوصلها المنيو الجديد في خلال %.',
	publishWindow:
		'الجهاز اللي لسه موصلهوش ممكن يبيع بالأسعار القديمة لحد % بعد التغيير. البيعات دي بتتسجّل.',
	publishSoldOut: '«خلص» بيوصل على طول. عمره ما بيحتاج نشر.',
	publishWhenTitle: 'إمتى',
	publishNow: 'دلوقتي',
	publishLater: 'في يوم وساعة',
	publishDay: 'اليوم',
	publishTime: 'الساعة',
	publishTimezone: 'اليوم والساعة بتوقيت %.',
	cityCairo: 'القاهرة',
	publishMinutes: '% دقيقة',
	publishMinutesFew: '% دقايق',
	publishScheduled: 'فيه منيو جديد مستني ميعاده. ألغيه الأول، أو استنى لما يبدأ.',
	publishCancelIt: 'ألغيه',
	publishChanged: 'المنيو اتغيّر وانت فاتح الشاشة دي. راجع اللستة تاني.',
	publishNothing: 'الأجهزة عليها المنيو ده خلاص.',
	publishFailed: 'مقدرناش ننشر المنيو. اختياراتك لسه زي ما هي.',
	publishPast: 'الوقت ده عدّى. اختار وقت بعده، أو دلوقتي.',
	changesShopPrices: 'أسعار الفروع',
	changeAdded: '%: اتضاف',
	changeAddedAt: '%: اتضاف بسعر',
	changeRemoved: '%: اتحذف',
	changeRenamed: '%1، كان «%2»',
	changeOrder: 'ترتيب الأقسام اتغيّر',
	changeItemOrder: '%: ترتيب الأصناف اتغيّر',
	changeChoiceOrder: '%: ترتيب الاختيارات اتغيّر',
	changeMoved: '%1: اتنقل لـ «%2»',
	changeOptions: '%: الإضافات اللي عليه اتغيّرت',
	changeDescription: '%: اتغيّر الوصف',
	changePhoto: '%: اتغيّرت الصورة',
	changeRange: '%1: %2، كانت %3',
	changeOwnPriceSet: '%: سعر خاص',
	changeOwnPriceCleared: '%: السعر الخاص اتشال',
	changeChoiceIn: '%1 في «%2»',
	changeBackOnSale: '%: رجع يتباع',
	changeNowSoldOut: '%: خلص',
	discountsLoading: 'بيحمّل الخصومات',
	discountsName: 'الاسم',
	discountsKind: 'النوع',
	discountsPercent: 'نسبة',
	discountsAmount: 'مبلغ',
	discountsPercentLabel: 'النسبة',
	discountsAmountLabel: 'المبلغ',
	discountsPercentNote: 'رقم صحيح: 20 يعني 20%.',
	discountsAmountNote: 'بالجنيه، اكتبه زي السعر: 10 يعني 10.00.',
	discountsPercentRange: 'النسبة من 1 لـ 100.',
	discountsNameTaken: 'فيه خصم تاني بالاسم ده.',
	discountsOnTill: 'على الجهاز:',
	discountsTakesOn: 'على %1 بيخصم %2.',
	discountsAmountRate: '% جنيه',
	discountsCreate: 'ضيف الخصم',
	discountsSave: 'احفظ الخصم',
	discountsRemoved: 'الخصم اتحذف: %',
	discountsHowTitle: 'الخصم بيتحسب إزاي',
	discountsHow1: 'الخصم على الطلب كله، والتوصيل معاه.',
	discountsHow2: 'النسبة بتتقرّب لتحت لأقرب جنيه: 15% من 87.00 بتخصم 13.00.',
	discountsHow3: '100% بتخلّي الطلب ببلاش، زي وجبة موظف. أصنافه بتتسجّل برضه.',
	discountsHow4:
		'التغيير بيوصل للأجهزة لما تنشر المنيو، وبيمشي من البيعة الجاية. الطلبات اللي فاتت بتفضل بخصمها.',
	discountsHow5: 'الكاشيرية بيختاروا من اللستة دي بس. المدير يقدر كمان يكتب خصم على الجهاز.',

	sellTable: 'الترابيزة',
	tableTitle: 'اختار الترابيزة',
	tableLabel: 'ترابيزة %1',
	tableNeeded: 'اختار الترابيزة اللي الطلب ده ليها.',
	tableRemoved: 'الترابيزة دي مبقتش موجودة. اختار غيرها.',
	tablesLoading: 'بنحمّل الترابيزات',
	tablesLoadFailed: 'مقدرناش نحمّل الترابيزات.',
	tablesTitle: 'الترابيزات',
	tablesAdd: 'ترابيزة جديدة',
	tablesName: 'اسم الترابيزة',
	tablesNameHint: 'اسم أو رقم، زي T5 أو تراس 2.',
	tablesEmpty: 'مفيش ترابيزات. طلبات الصالة في الفرع ده من غير ترابيزة.',
	tablesNote:
		'أول ما يبقى في الفرع ترابيزة، كل طلب صالة فيه لازم يبقى على ترابيزة. تغيير اسم ترابيزة أو شيلها مبيغيرش أي طلب اتعمل.',
	tablesRemoved: 'اتشالت الترابيزة: %1',
	tablesRuleWhat: 'الترابيزة اسم أو رقم، زي T5 أو تراس 2. ملهاش منطقة، ولا فيها فاضية أو مشغولة.',
	tablesRuleSeveral: 'الترابيزة الواحدة ممكن يبقى عليها كذا طلب مع بعض.',
	tableNameInUse: 'في ترابيزة تانية في الفرع ده بنفس الاسم.',
	tableLimit: 'الفرع ليه 200 ترابيزة بالكتير.',
	tableNameBounds: 'الترابيزة محتاجة اسم من 1 لـ 40 حرف.',
	driversTitle: 'الطيارين',
	driversLoading: 'بيحمّل الطيارين',
	driversLoadFailed: 'ما قدرناش نحمّل الطيارين.',
	driversAdd: 'طيار جديد',
	driversName: 'اسم الطيار',
	driversNameHint: 'اسم، زي أحمد علي.',
	driversEmpty: 'لسه مفيش طيارين.',
	driversRuleWhat: 'الطيار مجرد اسم. مش بيدخل على أي حاجة.',
	driversRuleKeep:
		'تغيير اسم الطيار بيخليه نفس الطيار. شيل الطيار بيخفيه من الأجهزة، واسمه مش بيتدّى لحد تاني في الفرع ده.',
	driversRuleTill: 'المدير اللي داخل على جهاز في الفرع ده بيظبط نفس القايمة.',
	driversRemoved: 'اتشال الطيار: %1',
	driverNameTaken: 'الاسم ده متاخد في الفرع ده، لطيار موجود أو اتشال.',
	driverNameBounds: 'الطيار محتاج اسم في سطر واحد، من 1 لـ 40 حرف.',
	driverGone: 'الطيار ده مبقاش في قايمة الفرع.',
	driverNotSaved: 'ما قدرناش نحفظ الطيار. جرّب تاني.',
	driversRemoveRefused: 'ما قدرناش نشيل %1، ولسه في القايمة.',
	reportsTables: 'الترابيزات',
	reportsByTable: 'الصالة حسب الترابيزة',
	reportsByTableNote: 'بترتيب الترابيزات، مش بالمبيعات · الطلبات جنب المبيعات',
	reportsTable: 'الترابيزة',
	reportsNoTable: 'من غير ترابيزة',
	reportsTablesPickShop: 'اختار فرع عشان تشوف ترابيزاته.',
	reportsTablesNone: 'الفرع ده مفيهوش ترابيزات.',
	reportsTablesFair:
		'الترابيزة الكبيرة أو اللي جنب الباب بتبيع أكتر، فكل صف بيبين طلباته جنب مبيعاته.',
	reportsTablesZero: 'الترابيزة اللي عليها صفر محدش اتطلب عليها طلب صالة مدفوع في الفترة دي.',
	unitGrams: 'جم',
	unitKilo: 'كجم',
	perKilo: '%1 / كجم',
	editorGrams: 'الوزن بالجرام',
	menuSoldBy: 'طريقة البيع',
	menuByPiece: 'بالقطعة',
	menuByWeight: 'بالوزن',
	menuPricePerKilo: 'سعر الكيلو',
	reportsPortions: 'حصص',
	reportsPieces: 'قطع',
	menuGroupKind: 'طريقة التسعير',
	menuGroupFixed: 'سعر ثابت',
	menuGroupPerKg: 'لكل كجم',
	menuPerKgSoon: 'مجموعات الكيلو هتتضاف تاني بعد دقايق.',
	menuWeighedSoon: 'الأصناف اللي بتتباع بالوزن تقدر تضيفها تاني كمان كام دقيقة.',

	sellPickDriver: 'اختار الطيار',
	driversNoneYet: 'لسه مفيش طيارين.',
	driversAskSomeone: 'لسه مفيش طيارين. اطلب من المدير أو صاحب المحل يضيف طيار.',
	driverLabel: 'الطيار: %1',
	driverNone: 'من غير طيار',
	driverChange: 'غيّر الطيار',
	driverChangeTitle: 'غيّر طيار %1',
	driverNeeded: 'اختار الطيار اللي هيوصّل الطلب ده.',
	driverRemoved: 'الطيار ده اتشال. اختار غيره.',
	driverClosed: 'الطلب ده اتقفل، وطياره زي ما هو.',
	driversFilterAll: 'كل الطيارين',
};

// Modern Standard Arabic: the same screens in the written form, for a
// restaurant that wants it on its tills and receipts instead of the Egyptian
// colloquial above.
const msa: Record<keyof typeof en, string> = {
	ordersShift: 'الوردية',
	shiftTitle: 'الوردية',
	shiftOpenSince: 'مفتوحة منذ %1',
	shiftOpenedBy: 'فتحها %1',
	shiftFloat: 'مبلغ البداية',
	shiftNoneOpen: 'لا توجد وردية مفتوحة.',
	shiftOpen: 'افتح الوردية',
	shiftClose: 'أغلق الوردية',
	shiftLastClosed: 'آخر وردية مغلقة',
	shiftClosedBy: 'أُغلقت %1، أغلقها %2',
	shiftApprovedBy: 'بموافقة %1',
	shiftOver: 'زيادة %1',
	shiftShort: 'عجز %1',
	shiftEven: 'مطابق',
	shiftPrintAgain: 'اطبع مرة أخرى',
	shiftPrinting: 'جارٍ الطباعة…',
	shiftPrintFailed: 'لم يُطبع تقرير الوردية.',
	shiftLoading: 'جارٍ تحميل الوردية',
	shiftLoadFailed: 'تعذّر تحميل الوردية.',
	shiftGoesTo: 'نقدية هذا الجهاز تذهب إلى %1.',
	shiftThereOpen: 'وردية %1 مفتوحة منذ %2.',
	shiftOpenQuestion: 'كم النقدية في الدرج؟',
	shiftOpenAmount: 'النقدية في الدرج',
	shiftOpening: 'جارٍ الفتح…',
	shiftAlreadyOpen: 'توجد وردية مفتوحة بالفعل.',
	shiftNotCashPoint: 'نقدية هذا الجهاز تذهب إلى جهاز آخر، فلا يفتح وردية ولا يغلقها.',
	shiftTooLarge: 'هذا المبلغ أكبر مما يقبله الجهاز.',
	shiftAskTitle: 'مبيعات الأجهزة الأخرى',
	shiftAsking: 'جارٍ السؤال…',
	shiftReceived: 'وصلت',
	shiftCannotReach: 'تعذّر الوصول',
	shiftAskAgain: 'اسأل مجددًا',
	shiftCloseAnyway: 'أغلق على أي حال',
	shiftNext: 'التالي',
	shiftCountQuestion: 'عُدّ النقدية في الدرج',
	shiftCountAmount: 'النقدية المعدودة',
	shiftClosing: 'جارٍ الإغلاق…',
	shiftApprovalTitle: 'إغلاق الوردية على %1',
	shiftCloseForbidden: 'المدير وحده يغلق الوردية.',
	shiftAlreadyClosed: 'هذه الوردية مغلقة بالفعل.',
	shiftClosedTitle: 'أُغلقت الوردية',
	shiftExpected: 'المبلغ المتوقع',
	shiftCounted: 'المبلغ المعدود',
	shiftCashSales: 'المبيعات النقدية',
	shiftCardSales: 'المبيعات بالبطاقة',
	shiftVoids: 'الإلغاءات',
	shiftNoSaleOpens: 'فتح الدرج دون بيع على هذا الجهاز',
	shiftDue: 'المستحق عند الإغلاق',
	shiftDueOrders: 'طلبات مفتوحة',
	shiftDueAmount: 'المبلغ',
	shiftDueNoDriver: 'بدون مندوب',
	shiftDueNotDelivery: 'ليس توصيلًا',
	shiftDueByDriverNote: 'توصيلات مفتوحة على نقطة النقد هذه، وليست نقدًا مع المندوب',
	shiftNotReached: 'لم يُتوصل إليه: %1',
	shiftBackToShift: 'العودة إلى الوردية',
	shiftNoShiftHere: 'لا توجد وردية مفتوحة. افتح وردية لقبول المدفوعات.',
	shiftNoShiftThere: 'لا دفع: لا توجد وردية مفتوحة على %1.',
	shiftStale:
		'لا دفع: لم يسمع هذا الجهاز من %1 منذ 5 دقائق. تأكد أن %1 يعمل ومتصل بشبكة Wi-Fi الخاصة بالمحل.',
	shiftUnreachable: 'لا دفع: لا يستطيع هذا الجهاز الوصول إلى %1، حيث تذهب نقديته.',
	shiftVoidNoShift: 'لا توجد وردية مفتوحة. افتح وردية لإلغاء طلب مدفوع.',
	shiftCashOut: 'سحب نقدية',
	shiftCashIn: 'إيداع نقدية',
	shiftCashOutIn: 'سحب / إيداع نقدية',
	shiftCashTotals: 'سحب %1 · إيداع %2',
	shiftMovementBy: '%1 · سجّله %2',
	shiftMovementByOnly: 'سجّله %1',
	cashMoveQuestion: 'كم المبلغ؟',
	cashMoveAmount: 'المبلغ',
	cashMoveWhy: 'السبب؟',
	cashMoveRecording: 'جارٍ التسجيل…',
	cashMoveNoShift: 'افتح وردية أولًا.',
	cashMoveAmountBounds: 'اكتب مبلغًا من 0.01 إلى أكبر مبلغ يقبله الجهاز.',
	cashMoveNoteNeeded: 'اذكر سبب حركة النقدية في سطر واحد.',
	cashMoveReason: 'السبب',
	cashMoveOther: 'سبب آخر',
	cashMoveDriverFloat: 'فكة السائق',
	cashMoveDriver: 'أي سائق؟',
	cashMoveNoDrivers: 'لا يوجد سائقون بعد. اطلب من المدير إضافتهم.',
	cashMoveDriversFailed: 'لم يتم تحميل السائقين.',
	cashMoveDriverNotFound: 'هذا السائق ليس في قائمة الفرع.',
	cashMoveDriverRemoved: 'تمت إزالة هذا السائق.',
	shiftMovementDriverBy: 'فكة السائق: %1 · سجّله %2',
	ownerShifts: 'الورديات',
	shiftsAllCashPoints: 'كل الخزائن',
	shiftsCashPoint: 'الخزينة',
	shiftsOpened: 'فُتحت',
	shiftsClosed: 'أُغلقت',
	shiftsOverShort: 'زيادة أو عجز',
	shiftsLate: 'متأخر',
	shiftsLateCount: '%1 متأخر',
	shiftsOpenChip: 'مفتوحة',
	shiftsShowOlder: 'اعرض الأقدم',
	shiftsEmpty: 'لا توجد ورديات بعد.',
	shiftsNote: 'الأحدث أولًا. تُحسب الأرقام من جديد في كل مرة، ومعها المبيعات التي وصلت متأخرة.',
	shiftsReportTitle: 'وردية %1',
	shiftsOpenedLine: 'فُتحت %1، فتحها %2',
	shiftsNoSaleOpensOn: 'فتح الدرج دون بيع على %1',
	shiftsSalesHeading: 'المبيعات والإلغاءات',
	shiftsTill: 'الجهاز',
	shiftsSale: 'بيع',
	shiftsVoid: 'إلغاء',
	shiftsArrivedAfterClose: 'وصل بعد الإغلاق',
	shiftsNoSales: 'لا توجد مبيعات في هذه الوردية بعد.',
	shiftsMovementsHeading: 'السحب والإيداع النقدي',
	shiftsCashOut: 'سحب نقدية',
	shiftsCashIn: 'إيداع نقدية',
	shiftsMovementDriver: 'فكة السائق: %1',
	shiftsMovementBy: 'سجلها %1',
	shiftsFiguresClamped: 'الأرقام تتجاوز ما تعرضه بوصلة.',
	shiftsNoShift: 'بلا وردية',
	tillsCashPointSelf: 'الخزينة: هذا الجهاز',
	tillsCashPointOther: 'الخزينة: %1',
	tillsCashPointInUse: 'يرسل %1 نقوده إلى هذا الجهاز. وجّهه إلى جهاز آخر أولًا.',
	shiftsDueOn: 'المستحق عند الإغلاق على %1',
	shiftsDueByDriver: 'المستحق عند الإغلاق حسب المندوب',
	shiftsDueByDriverNote: 'توصيلات مفتوحة على نقطة النقد هذه، وليست نقدًا مع المندوب',
	shiftsDueNoDriver: 'بدون مندوب',
	shiftsDueNotDelivery: 'ليس توصيلًا',
	shiftsDriverCollected: 'حصّلها',
	shiftsDriverHanded: 'سلّمها',
	shiftsDriverShortExtra: 'عجز أو زيادة',
	shiftsDriverFloats: 'الفكة',
	shiftsDriverRefused: 'مرفوض أو مرتجع',
	shiftsDriverPaidDirect: 'دُفع مباشرة',
	shiftsDriverBalance: 'الرصيد لديه',
	shiftsDriverExtra: 'زيادة %1',
	shiftsDriverExtraNote: 'نقدية غير مفسَّرة، وليست إيرادًا ولا إكرامية',
	shiftsOlderLoading: 'جارٍ التحميل…',
	tillsCashPointInUseMany: 'ترسل %1 نقودها إلى هذا الجهاز. وجّهها إلى أجهزة أخرى أولًا.',
	tillsOnline: 'متصل',
	tillsOffline: 'غير متصل',
	tillsNeverSeen: 'لم يظهر بعد',
	tillsLastSeen: 'آخر ظهور %',
	tillsSeenToday: 'اليوم',
	tillsSeenYesterday: 'أمس',
	tillsAppBuild: 'نسخة التطبيق %',
	tillsWaitingSalesOne: 'عملية بيع واحدة منتظرة',
	tillsWaitingSalesNone: 'لا توجد مبيعات منتظرة',
	tillsWaitingSalesTwo: 'عمليتا بيع منتظرتان',
	tillsWaitingSalesFew: '% عمليات بيع منتظرة',
	tillsWaitingSalesMany: '% عملية بيع منتظرة',
	tillsWindows: 'ويندوز',
	tillsAndroid: 'أندرويد',

	printingTitle: 'الطباعة',
	printingSharedNote: 'إعدادات مشتركة بين الفروع. تستخدم كل طباعة بيانات فرعها.',
	printingReceipt: 'إيصال العميل',
	printingKitchen: 'تذكرة المطبخ',
	printingLanguage: 'لغة الطباعة',
	printingFont: 'حجم الخط',
	printingNormal: 'عادي',
	printingLarge: 'كبير',
	printingRoomy: 'مسافات إضافية',
	printingSeparators: 'فواصل بين الأصناف',
	printingLogo: 'شعار المطعم',
	printingDetails: 'تخصيصات الأصناف والملاحظات',
	printingOptionPrices: 'أسعار الإضافات',
	printingUnitPrices: 'سعر الوحدة',
	printingLineTotals: 'إجمالي كل صنف',
	printingQR: 'رمز QR لدليل الفرع',
	printingAutomatic: 'طباعة تلقائية بعد الدفع',
	printingIdentifiers: 'تكبير رقم الطلب والطاولة',
	printingType: 'إبراز نوع الطلب',
	printingInstructions: 'تعليمات خاصة بخط عريض',
	printingGroupIdentical: 'تجميع الأصناف ذات التخصيصات المتطابقة',
	printingGroupCategories: 'تجميع حسب قسم التحضير',
	printingItemsNote: 'تظهر الأصناف والكميات دائمًا.',
	printingSample: 'معاينة تجريبية',
	printingSampleNote: 'اختيار الفرع والورق للمعاينة فقط، ولا يغيّر إعدادات الفرع أو يطبع شيئًا.',
	printingShop: 'فرع المعاينة',
	printingPaper: 'عرض الورق',
	printingPaperWidth: '%1 mm',
	printingUnpublished: 'صفحة الفرع في الدليل غير منشورة. لا يظهر رمز QR.',
	printingNoLogo: 'لم يُرفع شعار للطباعة بعد. أضفه هنا في جزء شعار المطعم.',
	printingLogoUpload: 'الشعار المطبوع',
	printingLogoRemoved: 'أُزيل الشعار المطبوع',
	printingLogoUnreadable: 'تعذرت قراءة شعار المطعم، فيُطبع بدونه.',
	printingUpdating: 'جارٍ تحديث المعاينة…',
	printingSaved: 'حُفظت إعدادات الطباعة',
	printingOrder: 'ترتيب الطباعة من الأعلى إلى الأسفل',
	printingOrderNote:
		'اسحب الصف من مقبضه، أو انتقل إلى المقبض واضغط سهم الأعلى أو الأسفل. تبقى الأصناف والإجمالي والدفع بهذا الترتيب.',
	printingRefused: 'يبقى الإجمالي والدفع تحت الأصناف، والدفع تحت الإجمالي. لم يتغير الترتيب.',
	printingRefusedLive: 'رُفض النقل',
	printingSectionMoved: '%1: أصبح رقم %2 من %3',
	printingAddText: 'أضف سطر نص',
	printingAddSeparator: 'أضف فاصلًا',
	printingCenter: 'في المنتصف',
	printingShown: 'ظاهر',
	printingHidden: 'مخفي',
	printingBold: 'عريض',
	printingDelete: 'احذف',
	printingDashed: 'متقطع',
	printingBlank: 'فراغ',
	printingLessSpace: 'مسافة أقل',
	printingMoreSpace: 'مسافة أكبر',
	printingSpace: '%1 mm',
	printingSmallerLogo: 'شعار أصغر',
	printingLargerLogo: 'شعار أكبر',
	printingWidth: '%1%',
	printingOriginalSize: 'الحجم الأصلي',
	printingOneLine: 'في سطر واحد',
	printingDrag: 'اسحب لإعادة الترتيب',
	printingTextLine: 'سطر نص',
	printingSeparator: 'فاصل',
	printingShopName: 'اسم الفرع',
	printingShopAddress: 'عنوان الفرع',
	printingShopPhone: 'هاتف الفرع',
	printingOrderDetails: 'بيانات الطلب',
	printingItems: 'الأصناف',
	printingTotals: 'الإجمالي',
	printingPayment: 'الدفع',
	shopsPhone: 'الهاتف (اختياري)',
	shopsPhoneNote: 'يُطبع على الإيصال عند إدخاله.',
	shopsPhoneInvalid: 'تحقق من رقم الهاتف.',
	printingMoved: 'أصبح نص الإيصال وتنسيقه مشتركين بين كل الفروع، من الإعدادات ← الطباعة.',
	printingOpenSettings: 'افتح إعدادات الطباعة',

	ownerShopSettings: 'إعدادات الفرع',
	ownerAccountSettings: 'إعدادات الحساب',
	ownerPublicPage: 'صفحة المطعم',
	ownerDelivery: 'مناطق التوصيل',
	ownerShopVisibility: 'الموقع وجاهزية الفرع',
	ownerReturnToTask: 'العودة إلى المهمة السابقة',
	ownerShopMissing: 'هذا الفرع غير متاح. اختر فرعًا للمتابعة.',
	ownerAllCashiers: 'جميع الموظفين',
	ownerAllTills: 'جميع الأجهزة',
	ownerOpenPublicSettings: 'فتح إعدادات صفحة المطعم',
	ownerOpenShop: 'فتح الفرع',
	ownerBackToBusiness: 'نظرة عامة على المطعم',

	appName: 'نقطة البيع',
	pairTitle: 'اربط هذا الجهاز',
	pairInstruction: 'أدخل الرمز الظاهر في لوحة التحكم.',
	pairCodeLabel: 'رمز الربط',
	lockTitle: 'أدخل رمزك',
	offline: 'لا يوجد اتصال. لن يكتمل البيع حتى يعود.',
	tillNotReady: 'الكاشير ما زال يستعد. حاول مرة أخرى بعد قليل.',
	backspace: 'احذف',

	signInTitle: 'لوحة تحكم المطعم',
	signInInstruction: 'سجّل الدخول بحساب جوجل، أو ببريدك الإلكتروني وكلمة المرور.',
	signInWithGoogle: 'الدخول بجوجل',

	createTitle: 'أنشئ مطعمك',
	createInstruction: 'يستغرق هذا دقيقة واحدة، ويمكن تغيير كل شيء لاحقًا.',
	createName: 'اسم المطعم',
	createCountryCode: 'رمز الدولة للهواتف',
	createCountryCodeHint: 'بدون علامة +. مصر 20.',
	createShopName: 'اسم الفرع الأول',
	createShopNameHint: 'إن تركته فارغًا فسيُسمّى «الرئيسي».',
	createSubmit: 'أنشئ المطعم',
	createWorking: 'جارٍ إنشاء المطعم…',

	ownerHome: 'الرئيسية',
	ownerShops: 'الفروع',
	ownerCashiers: 'أمناء الصندوق',
	ownerTills: 'الأجهزة',
	ownerSignOut: 'تسجيل الخروج',

	shopsAdd: 'فرع جديد',
	shopsName: 'اسم الفرع',
	shopsAddress: 'العنوان',
	shopsDayStart: 'يبدأ اليوم الساعة',
	shopsFee: 'توصيل بلا منطقة',
	shopsClose: 'أغلق الفرع',

	tillsAdd: 'جهاز جديد',
	tillsName: 'اسم الجهاز',
	tillsShop: 'الفرع',
	tillsSave: 'أضف الجهاز',
	tillsRevoke: 'إلغاء الربط',
	tillsEmpty: 'لا توجد أجهزة بعد.',
	tillsCodeTitle: 'رمز الربط ·',
	tillsCodeOnce: 'يظهر مرة واحدة؛ إن أغلقته فلن تراه مجددًا.',
	tillsCodeDone: 'أغلقته',
	tillsUnpaired: 'في انتظار الربط',
	tillsNoShops: 'افتح فرعًا أولًا.',

	pairFailed: 'هذا الرمز لا يعمل. قارنه بلوحة التحكم.',
	pairChecking: 'جارٍ ربط الجهاز…',
	pairLocked: 'محاولات كثيرة. انتظر دقيقة.',
	pairedAs: 'تم الربط',

	cashiersAdd: 'أمين صندوق جديد',
	cashiersName: 'الاسم',
	cashiersShop: 'الفرع',
	cashiersRole: 'الدور',
	cashiersRoleCashier: 'أمين صندوق',
	cashiersRoleManager: 'مدير',
	cashiersPasscode: 'الرمز السري',
	cashiersPasscodeHint: '8 أرقام. لن يظهر مجددًا بعد الحفظ.',
	cashiersPasscodeKeep: 'اتركه فارغًا ليبقى كما هو',
	cashiersEdit: 'تعديل',
	cashiersEmpty: 'لا يوجد أمناء صندوق بعد.',
	cashiersNoShops: 'افتح فرعًا أولًا.',
	cashiersNameTooLong: 'الاسم 22 حرفًا على الأكثر.',

	lockEnterPasscode: 'أدخل الرمز السري',
	lockWrong: 'هذا الرمز السري لا يعمل هنا.',
	lockLocked: 'محاولات كثيرة. انتظر ثم حاول مجددًا.',
	lockLockedFor: 'محاولات كثيرة. حاول مجددًا بعد',
	lockChecking: 'جارٍ التحقق…',
	lockAccountLocked: 'رموز خاطئة كثيرة في هذا المطعم. على الجميع الانتظار.',
	lockLockNow: 'قفل',
	lockSignOut: 'تسجيل الخروج',
	headerOpenCount: 'مفتوح',
	headerOpenCountAria: '%1 مفتوح، افتح الطلبات المفتوحة',
	lockOrderWaiting: 'طلب مفتوح',
	lockUnsentSale: 'بيع لم يُؤكَّد',
	lockItems: 'أصناف',

	sellPay: 'ادفع',
	sellPrintBill: 'اطبع الحساب',
	sellBillPending: 'جارٍ الطباعة…',
	sellBillChanged: 'تغيّر الحساب بعد طباعته',
	sellBillFailed: 'لم يُطبع الحساب. تأكّد من أن الطابعة تعمل وبها ورق.',
	sellType: 'نوع الطلب',
	sellEmpty: 'اضغط على صنف للبدء.',
	sellSubtotal: 'المجموع',
	sellDelivery: 'التوصيل',
	sellTotal: 'الإجمالي',
	sellDineIn: 'داخل المطعم',
	sellTakeaway: 'سفري',
	sellDeliveryType: 'توصيل',
	sellRemove: 'حذف',
	sellUndo: 'تراجع',
	sellRemoved: 'حُذف',
	sellDiscount: 'خصم',
	discountTitle: 'الخصم',
	discountBefore: 'قبل الخصم',
	discountNone: 'بدون خصم',
	discountType: 'اكتب خصمًا',
	discountPercentNote: 'عدد صحيح. يُقرَّب إلى الأدنى لأقرب جنيه.',
	discountAmountNote: 'إذا زاد على الإجمالي، يصبح الطلب مجانيًا.',
	discountTakesOff: 'يخصم %1. الإجمالي الجديد %2.',
	discountTakesAll: 'يخصم %1. يصبح الطلب مجانيًا.',
	discountEmpty: 'لا توجد خصومات بعد. يضيفها المالك من لوحة التحكم.',
	discountManager: 'خصم المدير',
	discountManagerOnly: 'لا يمنح هذا الخصم إلا المدير.',
	discountRemoved: 'لم يعد هذا الخصم متاحًا. اختر غيره، أو بدون خصم.',
	discountsDown: 'لا يمكن منح الخصومات الآن. أزل الخصم لإتمام البيع.',
	discountRefused: 'لا يمكن منح هذا الخصم. غيّره، أو أزله لإتمام البيع.',
	payFree: 'طلب مجاني. «المبلغ نقدًا بالضبط» يُنهيه، ويبقى الدرج مغلقًا.',
	doneFree: 'طلب مجاني',
	ordersFree: 'مجاني',
	ordersDiscount: 'الخصم: %1',
	ordersDiscountGiven: 'الخصم: %1 · منحه %2',
	sellSoldOut: 'نفد',
	sellQty: 'الكمية',
	sellPendingTitle: 'لم يُؤكَّد البيع',
	sellPendingRetry: 'أعد الإرسال',
	sellSend: 'أرسل',
	sellSent: 'أُرسل',
	sellSendFirst: 'أرسل أولًا',
	sellNewOrder: 'طلب جديد',
	sellOrderFrom: 'من %1',
	ordersOpenOnSell: 'افتح',
	sellNoMenu: 'لا توجد قائمة طعام لهذا الفرع بعد.',

	confirm: 'تأكيد',
	editorNote: 'ملاحظة',
	editorChooseOne: 'اختر 1',
	editorChooseAtLeast: 'اختر على الأقل',
	editorBelowZero: 'السعر أقل من صفر، راجع المدير.',

	deliveryTitle: 'تفاصيل التوصيل',
	deliveryArea: 'المنطقة',
	deliveryNoArea: 'بلا منطقة',
	deliveryPhone: 'الهاتف',
	deliveryAddress: 'العنوان',
	deliveryName: 'اسم العميل',
	deliveryPhoneRequired: 'الهاتف مطلوب',
	deliveryPhoneCheck: 'راجع الرقم',
	deliveryStepPhone: 'هاتف العميل',
	deliveryStepWhere: 'إلى أين التوصيل؟',
	deliveryNewCustomer: 'عميل جديد',
	deliveryNewAddress: 'عنوان جديد',
	deliverySuggestFrom: 'يظهر العملاء المحفوظون من الرقم الخامس',
	deliverySuggestions: 'العملاء المحفوظون',
	deliveryNewNumber: 'رقم جديد. «عميل جديد» بالأسفل.',
	deliveryNoMatch: 'لا يوجد عميل محفوظ بهذه الأرقام.',
	deliveryNoSavedAddress: 'لا يوجد عنوان محفوظ',
	deliveryRequired: 'مطلوب',
	deliveryAddressHint: 'الشارع ورقم المبنى',
	deliveryLandmark: 'علامة مميزة (اختياري)',
	deliveryLandmarkHint: 'مثلًا: الطابق 3، شقة 7، بجوار الصيدلية',
	deliveryReadBack: 'اقرأه على العميل',
	deliveryNoAreaYet: 'لا توجد منطقة بعد، اختر من الأسفل',

	payTitle: 'الدفع',
	payExact: 'المبلغ نقدًا بالضبط',
	payCard: 'بطاقة',
	payWithDriver: 'مع المندوب',
	payTendered: 'المدفوع',
	payChange: 'الباقي',

	doneSending: 'جارٍ إرسال البيع إلى بوصلة…',
	doneNotPrinted: 'لم يُطبع الإيصال. جرّب «اطبع مجددًا».',
	doneRetry: 'حاول مجددًا',
	doneDrawerFailed: 'لم يُفتح الدرج.',
	donePrinting: 'جارٍ الطباعة…',

	ordersTitle: 'الطلبات',
	ordersNumber: 'رقم',
	ordersStatus: 'الحالة',
	ordersBack: 'رجوع',
	ordersToday: 'الكل',
	ordersPaid: 'مدفوع',
	ordersVoid: 'ملغى',
	ordersOpen: 'مفتوح',
	ordersWaiting: 'بانتظار الإرسال',
	ordersWaitingTell: 'يحتفظ الكاشير بهذا البيع ويرسله إلى بوصلة حين يستطيع.',
	ordersNotTaken: 'لم يُقبل',
	ordersNotTakenTell: 'لم تقبل بوصلة هذا البيع: %1 ويحتفظ به الكاشير.',
	ordersNotTakenTotal: 'إجماليه يختلف عن حساب بوصلة.',
	ordersNotTakenInvalid: 'فيه ما ليس صحيحًا.',
	ordersNotTakenHeld: 'لدى بوصلة هذا البيع بتفاصيل أخرى.',
	ordersNotTakenNumber: 'لبيع آخر من هذا الكاشير الرقم نفسه اليوم.',
	ordersNotTakenOther: 'لدى بوصلة هذا الطلب على كاشير آخر.',
	ordersNotTakenCode: 'السبب %1.',
	ordersCash: 'نقدًا',
	ordersDue: 'مستحق',
	ordersNoSale: 'بلا بيع',
	ordersEmpty: 'لا توجد طلبات اليوم بعد.',
	ordersReprint: 'اطبع مجددًا',
	ordersTakePayment: 'حصّل المبلغ',
	ordersVoidAction: 'ألغِ',
	ordersOpenDrawer: 'افتح الدرج',
	ordersTestPrint: 'اختبر الطابعة',
	ordersPrinters: 'الطابعات',
	ordersNoReceiptPrinter: 'لا توجد طابعة إيصالات',
	ordersNoDrawer: 'لا يوجد درج',
	appUpdateOffered: 'تتوفر نسخة أحدث من تطبيق بوصلة جاهزة للتنزيل: %1.',
	appUpdateAction: 'حدّث التطبيق',
	appUpdateDownloading: 'جارٍ تنزيل التحديث.',
	appUpdateDownloadingAction: 'جارٍ التنزيل…',
	appUpdateReady: 'اكتمل تنزيل التحديث. يُثبَّت حين يُقفَل الكاشير ولا يكون شيء في الطريق.',
	appUpdateInstallNow: 'ثبّته الآن',
	appUpdateInstallWaiting: 'يُثبَّت حين يفرغ الكاشير…',
	appUpdateStopped: 'لم يكتمل تنزيل التحديث.',
	appUpdateNotChecked: 'تعذّر التحقق من سلامة التحديث، فلم يحتفظ به الكاشير.',
	// Till settings and onboarding (#774, UC-59, T26/T27).
	tillSettings: 'إعدادات الجهاز',
	tillSettingsLoading: 'جارٍ تحميل إعدادات الجهاز',
	tillSettingsFailed: 'تعذّر تحميل إعدادات الجهاز.',
	tillRefreshTitle: 'حدِّث من بوصلة',
	tillRefreshState: 'يجلب أحدث قائمة وأسعار وطابعات ومندوبين الآن',
	tillRefresh: 'حدِّث',
	tillRefreshing: 'جارٍ التحديث…',
	tillRefreshed: 'محدَّث',
	tillRefreshFailed: 'لم تردّ بوصلة. حاول مجددًا بعد قليل.',
	tillDriversTitle: 'المندوبون',
	tillDriversNone: 'لا يوجد مندوبون بعد',
	tillDriversOne: 'مندوب واحد',
	tillDriversMany: '%1 مندوبين',
	tillDriversLoading: 'جارٍ تحميل المندوبين',
	tillDriversLoadFailed: 'تعذّر تحميل المندوبين.',
	tillDriversEmpty: 'لا يوجد مندوبون بعد.',
	tillDriversAdd: 'مندوب جديد',
	tillDriversName: 'اسم المندوب',
	tillDriversNameTaken: 'هذا الاسم مستخدم في هذا الفرع، لمندوب حالي أو مُزال.',
	tillDriversNameBounds: 'يحتاج المندوب إلى اسم في سطر واحد، من 1 إلى 40 حرفًا.',
	tillDriversNeedsInternet: 'يحتاج أي تغيير في المندوبين إلى الإنترنت.',
	tillDriversForbidden: 'لا يغيّر المندوبين إلا المدير.',
	tillDriversRemoved: 'أُزيل المندوب: %1',
	tillDriversGone: 'أُزيل هذا المندوب من الفرع.',
	tillSettingsItem: 'الإعداد',
	tillSettingsState: 'الحالة',
	setupOpen: 'افتح',
	setupPrintersNone: 'لم تُضبط أي طابعة بعد',
	setupPrinterOne: 'طابعة واحدة',
	setupPrintersTwo: 'طابعتان',
	setupPrintersMany: '%1 طابعات',
	setupTitle: 'تجهيز هذا الجهاز',
	setupSkip: 'تخطَّ',
	setupNext: 'التالي',
	setupPrintersOpen: 'اضبط الطابعات',

	// T28: sharing with this shop's tills and the link test (UC-58, UC-59, #775).
	sharingTitle: 'المشاركة مع أجهزة الفرع',
	sharingIntro: 'تتشارك أجهزة الفرع ضبط الطابعات فيما بينها على شبكة الفرع.',
	sharingOn: 'المشاركة مفعّلة.',
	sharingNeedsYes: 'تحتاج المشاركة إلى موافقة مسؤول ويندوز مرة واحدة.',
	sharingOff: 'لا يشارك هذا الجهاز الآن.',
	sharingRowOn: 'مفعّلة',
	sharingRowNeedsYes: 'تحتاج موافقة ويندوز',
	sharingRowOff: 'متوقفة',
	sharingTill: 'الجهاز',
	sharingLinkColumn: 'الاتصال',
	sharingHeard: 'آخر اتصال %1',
	sharingHeardToday: 'اليوم',
	sharingHeardYesterday: 'أمس',
	sharingNotHeard: 'لم يصل منه شيء بعد',
	sharingLegacy: 'يعمل بنسخة أقدم من تطبيق بوصلة',
	sharingNoTills: 'لا يوجد جهاز آخر في الفرع لاختباره.',
	sharingReadFailed: 'تعذّر على هذا الجهاز قراءة حالة المشاركة.',
	linkTest: 'اختبر الاتصال',
	linkTestAgain: 'اختبر الاتصال مجددًا',
	linkTesting: 'جارٍ الاختبار…',
	linkAnswered: 'ردّ %1.',
	linkNotHeard:
		'لا يسمع هذا الجهاز %1. تأكد أن %1 يعمل ومتصل بشبكة الواي فاي نفسها المتصل بها هذا الجهاز.',
	linkNoAnswer:
		'لم يردّ %1. بعض شبكات الواي فاي تعزل أجهزتها بعضها عن بعض، فلا تصل الأجهزة إلى بعضها.',
	linkMeshOff: 'المشاركة متوقفة على هذا الجهاز الآن.',
	linkGone: 'لم يعد %1 من أجهزة الفرع.',
	cashPointTitle: 'الخزينة',
	cashPointQuestion: 'إلى أين تذهب نقدية هذا الجهاز؟',
	cashPointThisTill: 'هذا الجهاز',
	cashPointNoOthers: 'لا يوجد جهاز آخر في الفرع يعمل خزينة.',
	cashPointTestFirst: 'اختبر الاتصال بـ%1 أولًا.',
	cashPointHearing: 'جارٍ التحقق من وردية %1…',
	cashPointReady: 'التأكيد يرسل نقدية هذا الجهاز إلى %1.',
	cashPointOlderApp: 'يحتاج %1 إلى النسخة الأحدث من تطبيق بوصلة.',
	cashPointOpenHere: 'أغلق وردية هذا الجهاز أولًا.',
	cashPointInUse: 'هناك أجهزة أخرى ترسل نقديتها إلى هذا الجهاز. وجّهها إلى جهاز آخر أولًا.',
	cashPointNotAllowed: 'يرسل %1 نقديته إلى جهاز آخر، فلا يستطيع أن يستلم نقدية هذا الجهاز.',
	cashPointForbidden: 'المدير وحده يختار أين تذهب نقدية هذا الجهاز.',
	cashPointNeedsInternet: 'اختيار الخزينة يحتاج إلى الإنترنت.',
	cashPointReadFailed: 'تعذّر على هذا الجهاز قراءة أين تذهب نقديته.',
	cashPointRemoved: 'أُزيل',
	pairNextSetup: 'بعد ذلك، يسجّل مدير دخوله لتجهيز الجهاز.',
	printersTitle: 'الطابعات',
	printersBack: 'رجوع',
	printersEmpty: 'لم تُضبط أي طابعة بعد.',
	printersManagerSetsUp: 'المدير هو من يضبط الطابعات على الجهاز.',
	printerPrintsReceipts: 'الإيصالات والحساب',
	printerPrintsKitchen: 'بونات المطبخ',
	printerPrintsAll: 'الإيصالات والحساب وبونات المطبخ',
	printerPaper58: '58 مم',
	printerPaper80: '80 مم',
	printerDrawer: 'درج النقود',
	printerYes: 'نعم',
	printerNo: 'لا',
	printerNotAnswering: 'لا تستجيب',
	printersAdd: 'أضف طابعة',
	printersChange: 'تعديل',
	printersSetUpAgain: 'إعداد من جديد',
	printerSetupTitle: 'إعداد الطابعة',
	printerAddressLabel: 'عنوان الطابعة',
	printerAddressHint: 'مثل 192.168.1.50',
	printerTestSlip: 'اطبع بونًا تجريبيًا',
	printerCodeQuestion: 'هل طُبع هذا الرقم؟',
	printerCodeLine: 'الرقم: %1',
	printerNameLabel: 'الاسم',
	printerNameHint: 'من 1 إلى 40 حرفًا',
	printerPrintsLabel: 'تطبع',
	printerPaperLabel: 'الورق',
	printerDrawerOn: 'درج النقود على هذه الطابعة',
	printerSave: 'حفظ',
	printerCancel: 'إلغاء',
	printerPurposeTaken: 'الطابعة «%1» تطبع %2 بالفعل.',
	printerPurposeTakenAnon: 'توجد طابعة أخرى تطبع %1 بالفعل.',
	printerAlreadyAddedAnon: 'توجد طابعة مضبوطة بالفعل على هذا العنوان.',
	printerPurposeReceipts: 'الإيصالات والحساب',
	printerPurposeKitchen: 'بونات المطبخ',
	printerNameInUse: 'هذا الاسم مستخدم لطابعة أخرى.',
	printerAlreadyAdded: 'الطابعة «%1» مضبوطة بالفعل على هذا العنوان.',
	printerNoAnswer: 'لا توجد طابعة تستجيب على هذا العنوان.',
	printerCheckAddress: 'تحقق من العنوان وحاول مجددًا.',
	printerForbidden: 'المدير وحده يضبط الطابعات.',
	printerFailed: 'لم تتم العملية. حاول مجددًا.',
	printerSlipUnreachable: 'الطابعة لا تستجيب. تأكد أنها تعمل وأنها على هذه الشبكة.',
	printerSlipOtherPrinter: 'تستجيب طابعة أخرى على هذا العنوان.',
	printNoticeNoReceiptPrinter: 'لم تُضبط طابعة للإيصالات. يضبطها المدير من الطلبات ← الطابعات.',
	printNoticeNoDrawer: 'لم يُضبط درج على طابعة الإيصالات.',
	printNoticeKitchen: 'لم يُطبع بون المطبخ للطلب %1.',
	printNoticeBill: 'لم يُطبع الحساب للطلب %1.',
	printNoticeKitchenAnon: 'لم يُطبع بون المطبخ.',
	printNoticeBillAnon: 'لم يُطبع الحساب.',
	printNoticeAgain: 'اطبع مجددًا',
	printHeldKitchen: 'بون المطبخ للطلب %1: وصوله غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldBill: 'الحساب للطلب %1: وصوله غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldKitchenAnon: 'بون المطبخ: وصوله غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldBillAnon: 'الحساب: وصوله غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldReceipt: 'الإيصال: وصوله غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldDrawer: 'الدرج: وصول أمر الفتح غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printHeldAsk: 'الوصول غير مؤكد. قد يكتمل ما أُرسل من قبل.',
	printDecide: 'قرِّر',
	printWaitingDecision: 'بانتظار قرار',
	printHaveIt: 'استلمته',
	printAnotherCopy: 'اطبع نسخة أخرى',
	printDrawerOpened: 'فُتح الدرج',
	printOpenDrawerNow: 'افتح الدرج الآن',
	printDocReceipt: 'إيصال',
	printDocBill: 'حساب',
	printDocKitchen: 'بون مطبخ',
	printDocDrawer: 'درج',
	printDocShiftReport: 'تقرير الوردية',
	printHeldNoSale: 'بلا بيع',
	printDecidedAlready: 'اتخذ أحدهم قرارًا بشأن هذه الطباعة من قبل.',
	printBillChangedCopy: 'تغيّر الطلب بعد هذا الحساب. اطبع حسابه مجددًا.',
	printDrawerTooLate: 'انقضت الدقيقتان المتاحتان لفتح الدرج. استخدم «افتح الدرج» في الطلبات.',
	printerChangeTitle: 'تعديل «%1»',
	printerSetUpAgainTitle: 'عنوان جديد لـ«%1»',
	printerKeepsName: 'ستحتفظ الطابعة باسمها وما تطبعه. سنتأكد منها بورقة اختبار على العنوان الجديد.',
	printerBackAddress: 'العنوان',
	printerTryAgain: 'حاول مجددًا',
	printerChangeAddress: 'تغيير العنوان',
	printerCodeNotPrinted:
		'إذن ما على هذا العنوان ليس هذه الطابعة، أو لم تخرج الورقة. حاول مجددًا أو غيّر العنوان.',
	printerAddressNote: 'مكتوب على ورقة الإعدادات التي تطبعها الطابعة، مثل 192.168.1.60',
	printerNameNote: 'مثل: الكاشير، المطبخ',
	printerCodeIntro: 'الطابعة التي على %1 يُفترض أنها طبعت هذا الكود:',
	printerAtAddress: 'على %1',
	printerPaperUnit: 'مم',
	printerTestSlipPrinting: 'جارٍ طباعة ورقة الاختبار…',
	printersFromShop: 'هذه الطابعات من قائمة الفرع على بوصلة.',
	printersSetUpHere: 'اضبط الطابعات على هذا الجهاز',
	printersSetUpHereAsk:
		'سيحتفظ هذا الجهاز بطابعاته، حتى دون إنترنت. وتحتفظ أجهزة الفرع الأخرى بطابعاتها.',
	printersPrintInProgress: 'تخرج طباعة الآن. حاول مجددًا بعد قليل.',
	printersOnThisTill: 'مضبوطة على هذا الجهاز',
	printerSetupStale: 'تغيّرت الطابعات على هذا الجهاز. راجعها واحفظ مجددًا.',
	printersShared: 'مشتركة مع أجهزة الفرع',
	printersSharedFromAnother: 'ضُبطت على جهاز آخر في الفرع، %1',
	printersSharedFromHere: 'ضُبطت على هذا الجهاز، %1',
	printersShareWaiting:
		'لدى جهاز آخر في الفرع طابعات أحدث، لكن هذا الجهاز لا يصل إلى إحداها. سيبقى هذا الجهاز على طابعاته حتى يصل إليها.',
	printersShareNeedsFirewall:
		'تحتاج المشاركة مع أجهزة الفرع إلى خطوة جدار الحماية في دليل تجهيز الجهاز.',
	printersShareTurnOn: 'تشغيل المشاركة',
	printersShareAllowed: 'سمح ويندوز بالمشاركة. ستبدأ خلال دقيقة.',
	printersShareDeclined:
		'لم يسمح ويندوز بذلك. يجب أن يوافق من لديه كلمة مرور مسؤول ويندوز على هذا الجهاز.',
	printersShareFailed:
		'تعذّر على ويندوز تشغيل المشاركة. استخدم خطوة جدار الحماية في دليل إعداد الجهاز.',
	printersHostStarting:
		'يبدأ برنامج الطباعة المرافق على هذا الجهاز. إن استمر ذلك، أعد تشغيل بوصلة.',
	printerHostNotReady: 'برنامج الطباعة المرافق غير جاهز بعد. حاول مجددًا بعد قليل.',
	printerUsbOnThisTill: 'طابعة USB على هذا الجهاز: %1',
	printerUsbPick: 'طابعة موصولة بهذا الجهاز',
	printerUsbOrAddress: 'أو اكتب عنوان طابعة على الشبكة',
	printerShare: 'شاركها مع أجهزة الفرع الأخرى',
	printerSharedAt: 'مشتركة على %1: على جهاز أقدم، أضف طابعة على هذا العنوان',
	printerShareFirewall: 'في انتظار خطوة جدار الحماية في دليل إعداد الجهاز',
	printerSharePortTaken: 'برنامج آخر يستخدم المنفذ 9100',
	printerShareOffline: 'الطابعة غير متصلة',
	printerShareHelp: 'تطبع الطابعة المشتركة لأي جهاز على شبكة الفرع.',
	printerUsbOptionAnon: 'طابعة USB على هذا الجهاز',
	printerUsbHelp:
		'تظهر طابعة USB هنا عندما يسمح أندرويد لبوصلة باستخدامها: وصّلها ثم اضغط سماح. حدّد «دائمًا» حتى لا يسأل أندرويد مرة أخرى.',
	printerCodeIntroUsb: 'طابعة USB التي على هذا الجهاز يُفترض أنها طبعت هذا الكود:',
	printerNeedsIdentifying: 'تحتاج إلى تعريف',
	printerNeedsIdentifyingNote: 'لا تتأكد بوصلة أي طابعة هذه.',
	printersIdentify: 'عرّفها',
	printerIdentifyTitle: 'تعريف “%1”',
	printerIdentifyIntro: 'اختر الطابعة المقصودة واطبع عليها بونًا تجريبيًا. يظهر على البون رمز.',
	printerIdentifyNone:
		'لا توجد طابعة للاختيار الآن. تأكد أنها تعمل وعلى هذه الشبكة، أو اضبطها مجددًا بعنوانها.',
	printerIdentifyBack: 'الطابعات',
	printerIdentifyYes: 'نعم، هذه %1',
	printerIdentifyNotThis: 'إذن لم يخرج البون من هذه الطابعة. اختر طابعة أخرى، أو افحص هذه.',
	printerIdentifyAnother: 'اختر طابعة أخرى',
	printerModelLabel: 'الطراز',
	printerModelUnknown: 'غير معروف',
	ordersVoidWhy: 'لماذا تلغي الطلب',
	ordersVoidWrong: 'طلب خاطئ',
	ordersVoidCustomer: 'غادر العميل',
	ordersVoidMistake: 'سُجّل مرتين',
	ordersVoidQuality: 'أُعيد الطعام',
	cancelItem: 'إلغاء صنف',
	cancelHowMany: 'كم واحدًا؟',
	cancelOf: 'من',
	cancelLess: 'أقل',
	cancelMore: 'أكثر',
	cancelWhy: 'لماذا؟',
	cancelVoids: 'سيُلغي هذا الطلب.',
	cancelNeedsManager: 'يفتح السبب موافقة المدير.',
	cancelTitle: 'إلغاء %1 × %2 من الطلب %3',
	voidTitle: 'إلغاء الطلب %1',
	cardApprovalTitle: 'الموافقة على الدفع بالبطاقة للطلب %1',
	payCardAsksManager: 'هذا الطلب مع مندوب: الدفع بالبطاقة يحتاج موافقة المدير.',
	approvalTitle: 'موافقة المدير',
	approvalLead: 'يوافق المدير برقمه السري. ستبقى مسجّلًا.',
	approvalManagers: 'المديرون',
	approvalOtherName: 'اسم آخر',
	approvalNoManager: 'لا يوجد مدير في هذا الفرع. يضيفه صاحب المطعم من لوحة التحكم.',
	approvalInternet: 'يحتاج إلى الإنترنت. يمكن لمدير مسجّل على هذا الجهاز أن يوافق دونه.',
	approvalManagerOnly: 'الموافقة للمدير فقط',
	ordersVoidClosed: 'هذا الطلب من يوم آخر، ولم يعد إلغاؤه ممكنًا.',
	ordersPaidAlready: 'هذا الطلب مدفوع بالفعل.',
	ordersFailed: 'لم تتم العملية. حاول مجددًا.',
	ordersLoadFailed: 'تعذّر تحميل طلبات اليوم.',

	doneChange: 'الباقي',
	doneOrder: 'طلب رقم',
	doneNext: 'البيع التالي',
	donePrintAgain: 'اطبع مجددًا',

	sellPriceChanged: 'تغيّر السعر. راجع الإجمالي الجديد.',

	ownerMenu: 'قائمة الطعام',
	ownerSettings: 'الإعدادات',

	ownerHelp: 'المساعدة',
	helpIntro:
		'مقاطع فيديو قصيرة لكل مهمة، مسجّلة على مطعم تجريبي، ولا تعرض بياناتك أبدًا. ويمكنك أن تعرضها على أمين الصندوق أيضًا. الفيديو باللهجة المصرية، سواء كانت اللوحة بالمصرية أو بالفصحى.',
	helpSectionLabel: 'القسم',
	helpGettingStarted: 'البداية',
	helpTill: 'البيع على الجهاز',
	helpMenu: 'قائمة الطعام',
	helpDayToDay: 'العمل اليومي',
	helpAccount: 'حسابك',
	helpOnTheTill: 'على الجهاز',
	helpOnTheDashboard: 'على لوحة التحكم',
	helpVideosOne: 'مقطع واحد',
	helpVideosTwo: 'مقطعان',
	helpVideosFew: '% مقاطع',
	helpVideosMany: '% مقطعًا',
	helpScenesOne: 'مشهد واحد',
	helpScenesTwo: 'مشهدان',
	helpScenesFew: '% مشاهد',
	helpScenesMany: '% مشهدًا',
	helpScenes: 'المشاهد',
	helpEmpty: 'لا توجد مقاطع فيديو بالعربية بعد.',
	helpVideoLoading: 'جارٍ تحميل الفيديو',
	helpVideoFailed: 'لم يُحمَّل هذا الفيديو.',
	helpRetry: 'حاول مجددًا',
	helpPlay: 'تشغيل',
	helpPause: 'إيقاف مؤقت',
	helpCaptions: 'الترجمة',
	helpFullscreen: 'ملء الشاشة',
	helpExitFullscreen: 'الخروج من ملء الشاشة',
	helpSeek: 'موضعك في الفيديو',

	menuAddCategory: 'قسم',
	menuAddItem: 'صنف جديد',
	menuCategoryName: 'اسم القسم',
	menuItemName: 'اسم الصنف',
	menuPriceHint: 'مثلًا 50 أو 12.50',
	menuCategory: 'القسم',
	menuAvailable: 'متاح للبيع',
	menuSoldOut: 'نفد',
	menuEmpty: 'لا شيء في قائمة الطعام بعد.',
	menuNoCategories: 'أضف قسمًا أولًا.',
	menuCategoryRemoved: 'حُذف القسم:',
	menuCategoryNotEmpty:
		'«%1» ما زال يحتوي على أصناف (%2). انقلها إلى قسم آخر أو احذفها، ثم احذف القسم.',
	menuOverrides: 'سعر كل فرع وحالته · الفارغ يعني السعر الأساسي',
	menuBase: 'الأساسي',
	menuDescription: 'الوصف',
	menuDescriptionNote: 'حتى 300 حرف، على صفحة الدليل. تبقى الأسطر الجديدة كما هي.',
	menuPhoto: 'الصورة',
	menuPhotoAlt: 'صورة الصنف',
	menuPhotoEmpty: 'لا توجد صورة',
	menuPhotoAdd: 'أضف صورة',
	menuPhotoReplace: 'غيّر الصورة',
	menuPhotoRemove: 'امسح',
	menuPhotoPreparing: 'جارٍ التجهيز…',
	menuPhotoNote:
		'PNG أو JPEG أو WebP، حتى 20 ميجابايت، و64×64 بكسل على الأقل. نقصّها مربعة، حتى 1024×1024.',
	menuPhotoType: 'يجب أن تكون الصورة PNG أو JPEG أو WebP.',
	menuPhotoSize: 'الصورة أكبر من 20 ميجابايت.',
	menuPhotoSmall: 'الصورة أصغر من 64×64 بكسل.',

	settingsLanguage: 'اللغة',
	settingsLanguageArabic: 'مصري',
	settingsLanguageMSA: 'فصحى',
	settingsLanguageEnglish: 'English',
	settingsCountryCode: 'رمز الدولة لهواتف العملاء',
	settingsIdleSeconds: 'اقفل الجهاز إن لم يلمسه أحد (ثانية)',
	settingsSaved: 'حُفظ.',

	areasTitle: 'مناطق التوصيل',
	areasAdd: 'منطقة جديدة',
	areasName: 'اسم المنطقة',
	areasFee: 'الرسوم',
	areasEmpty: 'لا توجد مناطق. يأخذ الفرع رسوم التوصيل الخاصة به.',

	loading: 'جارٍ التحميل',
	retry: 'حاول مرة أخرى',
	loadFailed: 'تعذّر الوصول إلى لوحة التحكم.',
	pairContinue: 'متابعة',
	pairHasDrawer: 'فيه درج نقود',
	lockScreenLocked: 'الشاشة مقفلة. أدخل الرمز السري.',
	lockPasscode: 'الرمز السري',
	lockUnreachable: 'تعذّر الوصول إلى الخادم. تحقّق من الاتصال.',
	lockUrgent: 'عاجل',
	lockWho: 'من يسجّل الدخول؟',
	lockWhoContinues: 'الشاشة مقفلة. من سيتابع؟',
	lockNames: 'الأسماء',
	lockNotYou: 'لست أنت؟',
	lockNobody: 'لا يمكن لأحد تسجيل الدخول هنا بعد',
	lockNobodyHint: 'يضيف صاحب المطعم أمناء الصندوق من لوحة التحكم.',
	sellPendingBody: 'إعادة الإرسال ترسل البيع نفسه، فلن يُحتسب مرتين أبدًا.',
	sellSending: 'جارٍ الإرسال…',
	sellLoadingMenu: 'جارٍ تحميل قائمة الطعام',
	sheetClose: 'إغلاق',
	priceInvalid: 'اكتب السعر هكذا: 50 أو 12.50.',
	priceDecimals: 'لا يزيد ما بعد الفاصلة العشرية على % أرقام.',
	priceNegative: 'لا يمكن أن يكون السعر أقل من صفر.',
	priceLarge: 'هذا السعر كبير جدًا.',
	priceRequired: 'اكتب السعر.',
	mineTitle: 'أصنافي',
	mineCount: '% أصناف بترتيبك',
	mineEmpty: 'اختر الأصناف التي تطلبها أكثر.',
	mineEdit: 'عدّل أصنافي',
	mineWholeMenu: 'قائمة الطعام كاملة',
	mineOrder: 'بهذا الترتيب ·',
	mineNone: 'لا يوجد صنف بعد. علّم صنفًا ليُضاف في النهاية.',
	mineUp: 'لأعلى',
	mineDown: 'لأسفل',
	mineDrag: 'اسحب لإعادة الترتيب',
	mineSaving: 'جارٍ الحفظ…',
	mineGone: 'صنف لم يعد في قائمة الطعام.',
	editorFewer: 'أنقص',
	editorMore: 'زِد',
	payTakeTitle: 'تحصيل طلب',
	payCashAmount: 'دفع نقدي بمبلغ',
	payAmount: 'المبلغ',
	payFinishing: 'جارٍ الإنهاء…',
	payFailedKept: 'لم يتم هذا البيع. الطلب ما زال موجودًا.',
	donePaidByCard: 'دُفع بالبطاقة',
	doneWithDriver: 'مع المندوب، لم يُدفع بعد',
	ordersHeading: 'طلبات اليوم',
	ordersTillHeading: 'طلبات هذا الكاشير اليوم',
	ordersTime: 'الوقت',
	ordersType: 'النوع',
	ordersCard: 'بطاقة',
	ordersEmptyFilter: 'لا توجد طلبات بهذه الحالة.',
	ordersDrawerAsk: 'أتفتح الدرج دون بيع؟',
	ordersDrawerOpen: 'افتح',
	ordersDrawerOpening: 'جارٍ الفتح…',
	ordersNo: 'لا',
	ordersDetails: 'التفاصيل',
	ordersCloseDetails: 'إغلاق',
	ordersVoidWhyEnd: '؟',
	ordersVoiding: 'جارٍ الإلغاء…',
	ordersVoidReason: 'سبب الإلغاء:',
	ordersLoadingDay: 'جارٍ تحميل طلبات اليوم',
	ordersPrinted: 'طُبع',
	ordersPrintedOnce: 'مرة',
	ordersPrintedTimes: 'مرات',
	ordersChangeWord: 'باقٍ',
	ownerOrders: 'الطلبات',
	ownerShopsCount: 'فروع',
	edit: 'عدّل',
	remove: 'حذف',
	cancel: 'إلغاء',
	save: 'احفظ',
	saving: 'جارٍ الحفظ…',
	saved: 'حُفظ.',
	yes: 'نعم',
	no: 'لا',
	signInOpening: 'جارٍ فتح جوجل…',
	createExists: 'لهذا الحساب مطعم بالفعل.',
	signInOr: 'أو',
	signInEmail: 'البريد الإلكتروني',
	signInPassword: 'كلمة المرور',
	signInSubmit: 'تسجيل الدخول',
	signInSigningIn: 'جارٍ تسجيل الدخول…',
	signInNew: 'جديد على بوصلة؟',
	signInCreateAccount: 'أنشئ حسابًا بالبريد الإلكتروني',
	signInFailed: 'البريد الإلكتروني وكلمة المرور لا يطابقان أي حساب.',
	signInUnverified:
		'افتح الرابط الذي أرسلناه إلى هذا البريد لإكمال إنشاء حسابك. أرسلنا إليك رابطًا جديدًا.',
	signInLocked: 'محاولات كثيرة. حاول مرة أخرى بعد %1 دقيقة.',
	createAccountTitle: 'أنشئ حسابًا',
	createAccountInstruction:
		'ببريد إلكتروني وكلمة مرور بدلًا من جوجل. يبدأ الحساب عندما تفتح الرابط الذي سنرسله إليك.',
	createAccountName: 'الاسم',
	passwordRule: 'استخدم كلمة مرور من 15 إلى 128 حرفًا.',
	passwordShow: 'إظهار',
	passwordHide: 'إخفاء',
	createAccountLanguage: 'لغة الرسالة',
	createAccountSubmit: 'أنشئ الحساب',
	createAccountCreating: 'جارٍ الإنشاء…',
	createAccountBadEmail: 'اكتب بريدًا إلكترونيًا صحيحًا.',
	backToSignIn: 'العودة إلى تسجيل الدخول',
	checkEmailTitle: 'تحقّق من بريدك',
	checkEmailBefore: 'أرسلنا رابطًا إلى',
	checkEmailAfter: '. افتحه خلال 24 ساعة لإكمال التسجيل.',
	sendAgain: 'أرسله مرة أخرى',
	sending: 'جارٍ الإرسال…',
	sentAgain: 'أُرسل مرة أخرى.',
	askAgainIn: 'يمكنك طلبه مرة أخرى بعد %1',
	linkChecking: 'جارٍ التحقق من الرابط…',
	linkInvalidTitle: 'انتهت صلاحية هذا الرابط أو استُخدم من قبل.',
	linkInvalidText:
		'إن لم يكتمل حسابك بعد، فسجّل الدخول بالبريد الإلكتروني وكلمة المرور الصحيحين وسنرسل إليك رابطًا جديدًا.',
	signInForgot: 'نسيت كلمة المرور؟',
	signInEmailTaken:
		'سُجّل هذا البريد بطريقة أخرى. سجّل الدخول بالطريقة التي استخدمتها أول مرة، ثم أضف هذه الطريقة من الإعدادات.',
	forgotTitle: 'نسيت كلمة المرور',
	forgotInstruction:
		'اكتب بريدك الإلكتروني وسنرسل رابطًا لتعيين كلمة مرور جديدة. إن كنت تدخل بجوجل، فهذا يضيف كلمة مرور أيضًا.',
	forgotSubmit: 'أرسل الرابط',
	forgotSentBefore: 'إن كان للعنوان',
	forgotSentAfter: ' حساب، فقد أرسلنا إليه رابطًا. يعمل الرابط 30 دقيقة.',
	resetTitle: 'اختر كلمة مرور جديدة',
	resetPassword: 'كلمة المرور الجديدة',
	resetSubmit: 'احفظ وسجّل الدخول',
	resetNote: 'سيُسجَّل دخولك هنا، ويُسجَّل خروجك من كل جهاز آخر.',
	resetInvalidText: 'يعمل رابط تعيين كلمة المرور مرة واحدة لمدة 30 دقيقة. اطلب رابطًا جديدًا.',
	methodsTitle: 'طرق تسجيل الدخول',
	methodsNote: 'تُضاف وتُزال من صفحتها',
	methodsCountOne: 'لديك طريقة دخول واحدة: %1.',
	methodsCountTwo: 'لديك طريقتان للدخول: %1.',
	methodsCountMany: 'لديك %1 طرق للدخول: %2.',
	methodsAnd: ' و',
	methodsGoogleWord: 'جوجل',
	methodsPasswordWord: 'كلمة مرور',
	methodGoogle: 'جوجل',
	methodPassword: 'كلمة مرور',
	methodAdded: 'أُضيفت %1',
	methodRemove: 'إزالة',
	methodsLoading: 'جارٍ تحميل طرق تسجيل الدخول…',
	methodsLast: 'هذه طريقتك الوحيدة للدخول، فلا يمكن إزالتها.',
	methodsSentBefore: 'تحقّق من بريدك:',
	methodsSentAfter: '. افتح الرابط لإضافة كلمة المرور.',
	methodsAdd: 'أضف',
	methodsAdding: 'جارٍ الإضافة…',
	methodsAddGoogle: 'أضف جوجل',
	methodsAddPassword: 'أضف كلمة مرور',
	methodsFoot:
		'نرسل إلى عنوانك رسالة عن كل إضافة، فتلاحظ أي إضافة لم تقم بها. طريقة الدخول الثانية هي ما يعيدك إذا فقدت الأولى.',
	methodsGoogleAdded: 'أُضيف جوجل.',
	methodsInUse: 'حساب جوجل هذا تابع لحساب آخر في بوصلة.',
	methodsEmailTaken: 'هذا البريد تابع لحساب آخر في بوصلة.',
	methodsUnfinished: 'لم يُكمل جوجل إضافة الحساب. حاول مرة أخرى.',
	methodsRemoved: 'أُزيلت: %1 · %2',
	createNamePlaceholder: 'مثلًا: زعفران',
	createShopDefault: 'الرئيسي',
	createSetForYou: 'العملة الجنيه المصري والتوقيت توقيت القاهرة. ضبطناهما لك.',
	homeToday: 'اليوم',
	homeShopOrders: 'طلبات الفرع',
	shopsDetails: 'بيانات الفرع',
	shopsDayStartBefore: 'الطلبات قبل',
	shopsDayStartAfter: 'فجرًا تُحتسب على اليوم السابق.',
	shopsOpen: 'افتح الفرع',
	shopsOpened: 'فُتح الفرع.',
	shopsCloseNote: 'تبقى الطلبات السابقة.',
	shopsClosed: 'أُغلق:',
	shopsLast: 'لا يمكن إغلاق آخر فرع مفتوح.',
	shopsDupe: 'يوجد فرع آخر بهذا الاسم.',
	areasNoArea: 'بلا منطقة:',
	receiptPreview: 'معاينة الإيصال',
	cashiersResetFor: 'رمز سري جديد لـ',
	cashiersMoveWarn: 'نقله إلى فرع آخر يُخرجه من كل الأجهزة.',
	cashiersPasscodeAgain: 'اكتبه مرة أخرى',
	cashiersPasscodeInUse: 'لدى أمين صندوق آخر هذا الرمز. يجب أن يكون كل رمز مختلفًا.',
	cashiersPasscodeDiffer: 'الرمزان غير متطابقين.',
	cashiersPasscodeMatch: '✓ متطابقان',
	cashiersAddAction: 'أضف',
	cashiersNewPasscode: 'رمز سري جديد',
	cashiersPasscodeChanged: 'تغيّر الرمز السري.',
	cashiersRemoved: 'حُذف:',
	cashiersSignedOut: 'سُجّل خروجه من كل الأجهزة.',
	tillsNamePlaceholder: 'مثلًا: جهاز 2',
	tillsDrawerQ: 'هل درج النقود موصول؟',
	tillsDrawer: 'درج',
	tillsAdding: 'جارٍ الإضافة…',
	tillsCodeExpires: 'اكتبه على الجهاز. تنتهي صلاحيته بعد',
	tillsCodeExpired: 'انتهت صلاحية الرمز. أضف الجهاز مجددًا لرمز جديد.',
	tillsCount: 'أجهزة',
	tillsPaired: 'مربوط',
	tillsHasDrawer: 'فيه درج',
	tillsNoDrawer: 'بلا درج',
	tillsRevoked: 'أُلغي ربطه:',
	tillsStopped: 'توقف فورًا.',
	menuItems: 'الأصناف',
	menuOptions: 'الإضافات',
	menuCategories: 'الأقسام',
	menuUp: 'لأعلى',
	menuDown: 'لأسفل',
	menuItemsIn: 'أصناف في',
	menuCategoryEmpty: 'لا توجد أصناف في هذا القسم.',
	menuItem: 'الصنف',
	menuStatus: 'الحالة',
	menuEditTitle: 'تعديل',
	menuBasePrice: 'السعر الأساسي',
	menuItemOptions: 'الإضافات على هذا الصنف',
	menuShopPrice: 'سعر الفرع',
	menuShopAvailability: 'حالة الفرع',
	menuUseBase: 'كالأساسي',
	menuRemoveItem: 'حذف الصنف',
	menuItemRemoved: 'حُذف الصنف. تبقى الطلبات السابقة.',
	menuNewOption: 'إضافة جديدة',
	menuOption: 'الإضافة',
	menuMin: 'الحد الأدنى',
	menuMax: 'الحد الأقصى',
	menuOnTill: 'على نقطة البيع',
	menuOnTillColon: 'على نقطة البيع:',
	menuRemoveOption: 'حذف الإضافة',
	menuChoice: 'اختيار',
	menuChoiceName: 'الاختيار',
	menuPriceChange: 'فرق السعر',
	menuAddOrTake: 'زيادة أو خصم',
	menuAdd: 'أضف',
	menuEditOption: 'عدّل الإضافة',
	menuChoiceNote: 'لا يمكن تعديل الاختيار. لتغيير سعره احذفه وأضفه مجددًا. متاح على:',
	menuNoItemsYet: 'لا يوجد صنف بعد',
	menuRangeError: 'يجب أن يكون 1 على الأقل، وألا يقل عن «الحد الأدنى».',
	ruleRequiredOne: 'مطلوب، اختر واحدًا',
	ruleOptionalOne: 'اختياري، واحد على الأكثر',
	ruleOptionalUpTo: 'اختياري، حتى %1',
	ruleExactly: 'اختر %1 بالضبط',
	ruleAtLeastUpTo: '%1 على الأقل، حتى %2',
	ordersAllShops: 'كل الفروع',
	ordersShop: 'الفرع',
	ordersTradingDay: 'يبدأ اليوم الساعة %1 فجرًا',
	ordersTillOnly: 'الإلغاء من نقطة البيع فقط.',
	ownerReports: 'التقارير',
	reportsPeriod: 'الفترة',
	reportsToday: 'اليوم',
	reportsYesterday: 'أمس',
	reportsLast7: 'آخر 7 أيام',
	reportsMonth: 'هذا الشهر',
	reportsLastMonth: 'الشهر الماضي',
	reportsRange: 'من … إلى',
	reportsFrom: 'من',
	reportsTo: 'إلى',
	reportsUpTo: 'حتى 366 يومًا.',
	reportsInvalid: 'تنتهي الفترة قبل أن تبدأ.',
	reportsTooLong: 'يغطي التقرير 366 يومًا على الأكثر.',
	reportsDay1: 'يوم واحد',
	reportsDay2: 'يومان',
	reportsDaysFew: '%1 أيام',
	reportsDaysMany: '%1 يومًا',
	reportsPart: 'جزء التقرير',
	reportsOverview: 'الإجمالي',
	reportsItems: 'الأصناف',
	reportsHours: 'الساعات',
	reportsLoading: 'جارٍ تحميل التقرير',
	reportsEmpty: 'لا توجد مبيعات في هذه الفترة.',
	reportsTotals: 'الإجمالي',
	reportsTotalsNote: 'يطابق ملخص اليوم على نقطة البيع',
	reportsPaid: 'الطلبات المدفوعة',
	reportsNet: 'صافي المبيعات',
	reportsAverage: 'متوسط الطلب',
	reportsFees: 'رسوم التوصيل',
	reportsMix: 'نقدًا وبالبطاقة',
	reportsCash: 'نقدًا',
	reportsCard: 'بطاقة',
	reportsVoid: 'ملغى',
	reportsOpen: 'مفتوح، مستحق',
	reportsNoSale: 'فتح الدرج بلا بيع',
	reportsExplain:
		'صافي المبيعات هو الطلبات المدفوعة فقط، بعد خصوماتها، شاملةً رسوم التوصيل. الطلب المفتوح مستحق لا مَبيع، والملغى يُحتسب منفصلًا. يُحتسب كل طلب على يوم العمل لفرعه (يبدأ %1 فجرًا).',
	reportsByType: 'حسب نوع الطلب',
	reportsType: 'النوع',
	reportsOrders: 'الطلبات',
	reportsItemsSold: 'الأصناف المَبيعة',
	reportsItemsNote: 'الأكثر مبيعًا أولًا · بسعر قائمة الطعام، قبل خصم الطلب، بدون رسوم التوصيل',
	reportsItem: 'الصنف',
	reportsQty: 'الكمية',
	reportsRevenue: 'الإيراد',
	reportsZero: 'لم يُبع شيء في هذه الفترة · %1',
	reportsItemsTotal: 'المجموع بسعر قائمة الطعام، قبل الخصومات ورسوم التوصيل',
	reportsSoldOut: 'نفد الآن',
	reportsSoldOutAt: 'نفد في %1',
	reportsAdded: 'أُضيف %1',
	reportsGone: 'لم يعد في قائمة الطعام',
	reportsByHour: 'حسب الساعة',
	reportsHour: 'الساعة',
	reportsHoursNote: 'من %1 فجرًا · الأكثر ازدحامًا بالطلبات',
	reportsAm: 'ص',
	reportsPm: 'م',
	reportsDiscounts: 'الخصومات',
	reportsDiscountsOn1: 'الخصومات الممنوحة · على %1 طلب',
	reportsDiscountsOnFew: 'الخصومات الممنوحة · على %1 طلبات',
	reportsDiscountsOnMany: 'الخصومات الممنوحة · على %1 طلب',
	reportsDiscountsNone: 'لا توجد خصومات في هذه الفترة.',
	reportsEachDiscount: 'كل خصم',
	reportsEachDiscountNote: 'الطلبات المدفوعة · قيمة الخصم',
	reportsDiscount: 'الخصم',
	reportsTookOff: 'المخصوم',
	reportsRemoved: 'محذوف',
	reportsDiscountsTotal: 'المجموع · الطلبات المدفوعة التي عليها خصم',
	reportsByCashier: 'الخصومات حسب الكاشير',
	reportsByCashierNote: 'الأكثر خصمًا أولًا · والتعادل بالاسم',
	reportsCashier: 'الكاشير',
	reportsWithDiscount: 'عليها خصم',
	reportsCashierNote:
		'هذه ليست مبيعات. الخصم اختيار الكاشير، فهنا تراجعه وفق قواعد المحل. من منح أكثر يأتي أولًا. الوردية المزدحمة تمنح أكثر في المجموع، لذا يقف كل كاشير بجانب الطلبات التي أنشأها وعدد ما عليه خصم منها. لا تظهر مبيعات الكاشير أبدًا.',
	settingsIdleNote: 'يُقفل الجهاز بعد %1 ثانية دون لمس. من 1 إلى 3600.',
	settingsQuick: 'مبالغ نقدية سريعة · حتى 8',
	settingsAmount: 'مبلغ',
	settingsExampleBefore: 'إذا كان الإجمالي',
	settingsExampleAfter: 'يرى أمين الصندوق:',
	settingsCurrency: 'العملة',
	settingsTimezone: 'التوقيت',
	settingsTheme: 'المظهر',
	themeLight: 'فاتح',
	themeDark: 'داكن',
	themeSystem: 'كالجهاز',
	settingsBrowserTitle: 'هذا المتصفح فقط: اللغة والمظهر',
	settingsBrowserNote: 'بلا حفظ',
	settingsBrowserExplain:
		'يُطبَّق كلاهما فورًا. يغيّران هذا المتصفح فقط، لا الأجهزة ولا أي متصفح آخر. تبدأ اللغة بلغة المطعم، ويبدأ المظهر «كالجهاز».',
	settingsRestaurantTitle: 'المطعم',
	settingsRestaurantNote: 'لكل الأجهزة والإيصالات',
	settingsRestaurantLanguage: 'لغة المطعم',
	settingsRestaurantLanguageNote: 'تُطبع بها الإيصالات، وتبدأ بها كل شاشة حتى تختار لغتها.',
	settingsTillsTitle: 'الأجهزة',
	settingsTillsNote: 'لكل الأجهزة',
	settingsSaveNote: 'يحفظ المطعم والأجهزة معًا.',
	settingsFixedTitle: 'ثابت لهذا المطعم',
	settingsFixedNote: 'للقراءة فقط',
	createLanguageNote: 'تُطبع بها الإيصالات، وتبدأ بها كل شاشة حتى تختار.',
	tillDisplayTitle: 'اللغة والمظهر',
	tillDisplayNote:
		'لهذا الجهاز فقط، ويُطبَّقان فورًا من شاشة القفل. لا يغيّران لوحة التحكم ولا أي جهاز آخر.',
	customerSaved: 'عناوينه',
	customerEdit: 'عدّل العميل',
	customerEditNeedsInternet: 'تعديل العميل يحتاج إلى الإنترنت.',
	customerEditAddress: 'عدّل',
	customerSaveAddress: 'احفظ العنوان',
	customerAddressRemoved: 'حُذف العنوان:',
	customerAddressRequired: 'العنوان مطلوب.',
	customerAddressTaken: 'هذا العنوان محفوظ له من قبل.',
	customerAreaOf: 'المنطقة · فرع %1',
	customerTitle: 'بيانات العميل',
	customerSaveName: 'احفظ الاسم',
	customerNameRequired: 'الاسم مطلوب.',
	customerAddresses: 'العناوين المحفوظة',
	customerNoAddresses: 'لا توجد عناوين محفوظة.',
	customerTillNote:
		'يُحفظ التعديل هنا على العميل نفسه، لا على هذا الطلب فقط. تبقى الطلبات السابقة بالعنوان الذي سُجّلت به. حذف العميل من لوحة صاحب المطعم فقط.',
	customerGone: 'هذا العميل لم يعد موجودًا.',
	ownerCustomers: 'العملاء',
	customersLoading: 'جارٍ تحميل العملاء',
	customersEmpty: 'لا يوجد عملاء بعد. تحفظ نقطة البيع عميلًا كلما اكتملت تفاصيل توصيل.',
	customersSearch: 'ابحث بالهاتف أو الاسم',
	customersNoMatch: 'لا أحد يطابق هذا البحث.',
	customersListNote: 'آخر طلب أولًا · 50 في كل مرة',
	customersName: 'الاسم',
	customersPhone: 'الهاتف',
	customersOrders: 'الطلبات',
	customersLast: 'آخر طلب',
	customersNoName: 'بلا اسم',
	customersOpen: 'افتح',
	customersDelete: 'احذف العميل',
	customersNoAddresses: 'لا توجد عناوين محفوظة. كل طلباته حتى الآن سفري أو داخل المطعم.',
	customersDeleteNote:
		'الحذف يزيل العميل وعناوينه. تعديل عنوان أو حذفه لا يغيّر الطلبات السابقة: تبقى بالاسم والهاتف والعنوان التي سُجّلت بها.',
	customersDeleted: 'حُذف العميل:',
	whenToday: 'اليوم',
	whenYesterday: 'أمس',

	suspendedLabel: 'الحساب موقوف',
	suspendedTillTitle: 'أوقفت بوصلة حساب هذا المطعم.',
	suspendedTillTell: 'أبلغ صاحب المطعم.',
	suspendedTillContact: 'دعم بوصلة:',
	suspendedOwnerTitle: 'أوقفت بوصلة حساب مطعمك.',
	suspendedOwnerTills: 'وتوقفت أجهزتك أيضًا.',
	suspendedOwnerContact: 'تواصل مع بوصلة:',
	suspendedWhatsapp: 'واتساب',
	suspendedCheck: 'تحقّق مجددًا',
	suspendedChecking: 'جارٍ التحقق…',
	suspendedTillResumes: 'حين تعيد بوصلة الحساب، تختفي هذه الرسالة وحدها خلال دقائق.',
	tillNetDown: 'لا يوجد اتصال. يكتمل البيع، ويصل إلى بوصلة حين يعود الاتصال.',
	tillNetDownOne: 'لا يوجد اتصال. ستصل عملية بيع واحدة إلى بوصلة حين يعود الاتصال.',
	tillNetDownTwo: 'لا يوجد اتصال. ستصل عمليتا بيع إلى بوصلة حين يعود الاتصال.',
	tillNetDownMany: 'لا يوجد اتصال. ستصل % عمليات بيع إلى بوصلة حين يعود الاتصال.',
	tillWaitingOne: 'عملية بيع واحدة لم تصل إلى بوصلة بعد.',
	tillWaitingTwo: 'عمليتا بيع لم تصلا إلى بوصلة بعد.',
	tillWaitingMany: '% عمليات بيع لم تصل إلى بوصلة بعد.',
	removedLabel: 'أُزيل الجهاز من الفرع',
	removedTitle: 'أُزيل هذا الجهاز من الفرع.',
	removedAsk: 'اسأل صاحب المطعم.',
	removedWaitOne: 'عملية بيع واحدة لم تصل إلى بوصلة بعد. أبقِ الجهاز يعمل ومتصلًا حتى تصل.',
	removedWaitTwo: 'عمليتا بيع لم تصلا إلى بوصلة بعد. أبقِ الجهاز يعمل ومتصلًا حتى تصلا.',
	removedWaitMany: '% عمليات بيع لم تصل إلى بوصلة بعد. أبقِ الجهاز يعمل ومتصلًا حتى تصل.',
	removedDone: 'وصلت كل مبيعات هذا الجهاز إلى بوصلة.',
	removedOpenOne: 'يبقى طلب مفتوح واحد على هذا الجهاز، ولا يمكن تحصيل ثمنه هنا.',
	removedOpenTwo: 'يبقى طلبان مفتوحان على هذا الجهاز، ولا يمكن تحصيل ثمنهما هنا.',
	removedOpenMany: 'تبقى % طلبات مفتوحة على هذا الجهاز، ولا يمكن تحصيل ثمنها هنا.',
	removedReturns: 'إذا أعادت بوصلة هذا الجهاز، تختفي هذه الرسالة وحدها.',
	shellNoticeLabel: 'يحتاج الكاشير إلى تطبيق بوصلة',
	shellNoticeTitle: 'يعمل الكاشير في تطبيق بوصلة.',
	shellNoticeTell: 'افتح الكاشير من تطبيق بوصلة على جهاز الكاشير.',
	shellNoticeOwner: 'يدير صاحب المطعم مطعمه من لوحة التحكم.',
	shellNoticeDashboard: 'افتح لوحة التحكم',
	shellNoticeSetup: 'هل تجهّز جهاز كاشير؟ ثبّت عليه تطبيق بوصلة.',
	shellNoticeWindows: 'تنزيل لنظام ويندوز',
	shellNoticeAndroid: 'تنزيل لنظام أندرويد',
	shellNoticeWindowsWarn: 'قد يُظهر ويندوز تحذيرًا: اختر More info، ثم Run anyway.',
	storeStartingLabel: 'يجري تجهيز الكاشير',
	storeStartingTitle: 'يجري تجهيز الكاشير.',
	storeStartingTell: 'يبدأ البيع بعد لحظة.',
	storeDownTitle: 'يُعاد تشغيل الكاشير.',
	storeDownTell: 'كل المبيعات المحفوظة عليه في أمان. يعود بعد لحظة.',
	storeStillDown:
		'ما زلت تنتظر؟ أغلق تطبيق بوصلة ثم افتحه من جديد. وإن لم يُجدِ ذلك، فتواصل مع بوصلة.',

	subscriptionEndsOn: 'ينتهي اشتراكك في بوصلة يوم %1، بعد %2.',
	subscriptionEndsToday: 'ينتهي اشتراكك في بوصلة اليوم.',
	subscriptionEnded: 'انتهى اشتراكك في بوصلة يوم %1.',
	subscriptionOneDay: 'يوم واحد',
	subscriptionTwoDays: 'يومين',
	subscriptionFewDays: '% أيام',
	subscriptionManyDays: '% يومًا',
	subscriptionHowToRenew: 'كيف تجدّد',
	subscriptionTitle: 'اشتراك بوصلة',
	subscriptionContact: 'تواصل مع بوصلة للتجديد:',
	subscriptionOnlyBosla: 'بوصلة وحدها تستطيع تغيير هذا التاريخ. ويبقى كل شيء يعمل بعده.',
	subscriptionClose: 'إغلاق',
	visitViewing: 'تعرض لوحة %1 بصفة صاحبه حتى %2',
	visitExit: 'خروج',
	signInVisitEnded: 'انتهت صلاحية هذا الرابط. افتح لوحة صاحب المطعم من صفحة الإدارة مرة أخرى.',
	ownerCatalogue: 'الدليل',
	catalogueLoading: 'جارٍ تحميل الدليل',
	cataloguePublished: 'منشور في الدليل',
	catalogueOpen: 'افتح الصفحة',
	catalogueNotPublished: 'غير منشور',
	catalogueNotPublishedNote: 'لا يرى الزبائن المطعم في الدليل.',
	catalogueMissingTitle: 'للنشر، ينقصك:',
	catalogueMissingLogo: 'شعار',
	catalogueMissingLogoWhy: 'يعرض الدليل شعار كل مطعم بجانب اسمه، وبه يتعرّف الزبون على المطعم.',
	catalogueMissingShop: 'فرع على الخريطة',
	catalogueMissingShopWhy:
		'يلزم فرع واحد على الأقل له عنوان ومكان على الخريطة، ليعرف الزبون إلى أين يذهب.',
	catalogueMissingShopAct: 'ضع فرعًا',
	catalogueIncomplete: 'لم يُنشر المطعم. ينقصه: %.',
	catalogueIncompleteLogo: 'شعار',
	catalogueIncompleteShop: 'فرع له عنوان ومكان على الخريطة',
	catalogueAnd: '، و',
	cataloguePart: 'جزء الدليل',
	cataloguePartPage: 'الصفحة',
	cataloguePartShops: 'الفروع على الخريطة',
	catalogueInIt: 'في الدليل',
	catalogueListed: 'منشور',
	catalogueUnpublishNote:
		'سيُزال من الدليل عند الحفظ. يبقى العنوان لك، ويعود الرابط للعمل حين تنشر مرة أخرى.',
	catalogueLockedNote:
		'يُتاح «منشور» حين يكون هناك شعار وفرع على الخريطة. يمكنك حفظ الصفحة بدونهما.',
	listingSlug: 'عنوان الصفحة',
	listingSlugNote: 'من 3 إلى 40 حرفًا لاتينيًا وأرقامًا وشرطات.',
	listingName: 'الاسم',
	listingNameRequired: 'الاسم مطلوب.',
	listingArea: 'المنطقة (اختياري)',
	listingAreaNote: 'مثلًا: الزمالك · المعادي. يبحث الناس بها.',
	listingSlugInvalid: 'من 3 إلى 40 حرفًا لاتينيًا وأرقامًا وشرطات مفردة، يبدأ وينتهي بحرف أو رقم.',
	listingSlugTaken: 'هناك مطعم آخر بهذا العنوان. اختر غيره.',
	listingNameInvalid: 'الاسم من 1 إلى 80 حرفًا.',
	listingAreaInvalid: 'المنطقة 120 حرفًا على الأكثر.',
	logoTitle: 'الشعار',
	logoCardNote: 'يظهر بجانب الاسم',
	logoAlt: 'شعار المطعم',
	logoEmpty: 'لا يوجد شعار',
	logoAdd: 'أضف شعارًا',
	logoReplace: 'غيّر الشعار',
	logoRemove: 'احذف',
	logoUploading: 'جارٍ الرفع…',
	logoNote: 'PNG أو JPEG، حتى 2 ميجابايت، و64×64 بكسل على الأقل. نضبطه على 512×512.',
	logoLockedNote: 'المطعم منشور: يمكنك تغيير الشعار، لكن لا يمكنك حذفه.',
	logoType: 'يجب أن يكون الشعار PNG أو JPEG.',
	logoSize: 'الصورة أكبر من 2 ميجابايت.',
	logoSmall: 'الصورة أصغر من 64×64 بكسل.',
	logoRemoved: 'حُذف الشعار.',
	placesShown: '%1 من %2 ظاهرة',
	placeNoAddress: 'لا يوجد عنوان',
	placeShown: 'ظاهر في الدليل',
	placeHidden: 'ليس في الدليل',
	placeLacks: 'ينقصه: %',
	placeLacksAddress: 'العنوان',
	placeLacksPlace: 'مكان على الخريطة',
	placeAnd: ' و',
	placeAddressInShops: 'العنوان من الفروع',
	placeEdit: 'عدّل المكان',
	placeAdd: 'ضعه على الخريطة',
	placeMapLabel: 'مكان فرع % على الخريطة',
	placeMapHint: 'اضغط على مكان الفرع في الخريطة',
	placeZoomIn: 'قرّب',
	placeZoomOut: 'أبعِد',
	placeLatitude: 'خط العرض',
	placeLongitude: 'خط الطول',
	placeDegrees: 'اكتبه بالدرجات، مثل 30.044420.',
	placeLatitudeRange: 'خط العرض من -90 إلى 90.',
	placeLongitudeRange: 'خط الطول من -180 إلى 180.',
	placeRequired: 'مطلوب.',
	placeHelp:
		'اضغط على الخريطة أو اسحب الدبوس، أو اكتب الرقمين بالدرجات. في خرائط جوجل: اضغط على المكان فيظهر الرقمان في الأعلى.',
	placeRemove: 'احذف المكان',
	placeSave: 'احفظ المكان',
	placeRemoved: 'حُذف مكان فرع %.',
	placesFoot:
		'يظهر الفرع في الدليل حين يكون له عنوان ومكان على الخريطة. يُغيَّر العنوان من الفروع. يرى الزبون العنوان، ورابطًا يفتح المكان في خرائط جوجل.',
	ownerDiscounts: 'الخصومات',
	discountsAdd: 'خصم جديد',
	discountsIntro: 'يعرض جهاز كل فرع هذه القائمة. يمنح أمين الصندوق خصمًا واحدًا لكل طلب بضغطتين.',
	discountsCount: '% · حسب الاسم',
	discountsEmpty: 'لا توجد خصومات بعد. أضف خصمًا، ويستطيع أمناء الصندوق منحه على الجهاز.',
	draftChangesOne: 'تعديل واحد غير منشور',
	draftChangesTwo: 'تعديلان غير منشورين',
	draftChangesFew: '% تعديلات غير منشورة',
	draftChangesMany: '% تعديلًا غير منشور',
	draftPublish: 'انشر القائمة',
	draftWaiting: 'تبدأ القائمة الجديدة يوم %',
	draftWhen: '%1 الساعة %2',
	draftClean: 'الأجهزة تعمل بهذه القائمة.',
	draftCancelled: 'أُلغيت القائمة المجدولة. عادت تعديلاتها إلى المسودة.',
	draftTooLate: 'بدأت هذه القائمة بالفعل.',
	priceCheckTitle: 'راجع هذه الأسعار',
	priceCheckText: 'هذه الأسعار بعيدة جدًا عن التي تحل محلها.',
	priceCheckFailed: 'تعذّرت مراجعة الأسعار. ما كتبته باقٍ كما هو.',
	priceAtShop: '%1 في %2',
	publishPending: 'جارٍ النشر…',
	publishLoading: 'جارٍ تحميل القائمة',
	publishInEffect: 'على الأجهزة الآن: القائمة %1، منذ يوم %2.',
	publishWaitingTitle: 'تنتظر موعدها: القائمة %1، يوم %2',
	publishChangesTitle: 'ما سيتغيّر على الأجهزة',
	publishPricesRight: 'هذه الأسعار صحيحة.',
	publishBeforeTitle: 'قبل أن تنشر',
	publishTills: 'تصل القائمة الجديدة إلى الأجهزة المتصلة بالإنترنت خلال %.',
	publishWindow:
		'قد يبيع الجهاز الذي لم تصله بعد بالأسعار القديمة حتى % بعد التغيير. وتُسجَّل هذه المبيعات.',
	publishSoldOut: '«نفد» يسري فورًا ولا يحتاج إلى نشر أبدًا.',
	publishWhenTitle: 'متى',
	publishNow: 'الآن',
	publishLater: 'في يوم وساعة',
	publishDay: 'اليوم',
	publishTime: 'الساعة',
	publishTimezone: 'اليوم والساعة بتوقيت %.',
	cityCairo: 'القاهرة',
	publishMinutes: '% دقيقة',
	publishMinutesFew: '% دقائق',
	publishScheduled: 'توجد قائمة جديدة تنتظر موعدها. ألغِها أولًا، أو انتظر حتى تبدأ.',
	publishCancelIt: 'ألغِها',
	publishChanged: 'تغيّرت القائمة أثناء فتح هذه الشاشة. راجع القائمة مرة أخرى.',
	publishNothing: 'الأجهزة تعمل بهذه القائمة بالفعل.',
	publishFailed: 'تعذّر نشر القائمة. اختياراتك كما هي.',
	publishPast: 'مضى هذا الوقت. اختر وقتًا بعده، أو الآن.',
	changesShopPrices: 'أسعار الفروع',
	changeAdded: '%: أُضيف',
	changeAddedAt: '%: أُضيف بسعر',
	changeRemoved: '%: حُذف',
	changeRenamed: '%1، كان «%2»',
	changeOrder: 'تغيّر ترتيب الأقسام',
	changeItemOrder: '%: تغيّر ترتيب الأصناف',
	changeChoiceOrder: '%: تغيّر ترتيب الاختيارات',
	changeMoved: '%1: نُقل إلى «%2»',
	changeOptions: '%: تغيّرت الإضافات المعروضة عليه',
	changeDescription: '%: تغيّر الوصف',
	changePhoto: '%: تغيّرت الصورة',
	changeRange: '%1: %2، كانت %3',
	changeOwnPriceSet: '%: سعر خاص',
	changeOwnPriceCleared: '%: أُزيل السعر الخاص',
	changeChoiceIn: '%1 في «%2»',
	changeBackOnSale: '%: عاد متاحًا للبيع',
	changeNowSoldOut: '%: نفد',
	discountsLoading: 'جارٍ تحميل الخصومات',
	discountsName: 'الاسم',
	discountsKind: 'النوع',
	discountsPercent: 'نسبة',
	discountsAmount: 'مبلغ',
	discountsPercentLabel: 'النسبة',
	discountsAmountLabel: 'المبلغ',
	discountsPercentNote: 'عدد صحيح: 20 تعني 20%.',
	discountsAmountNote: 'بالجنيه، ويُكتب كالسعر: 10 تعني 10.00.',
	discountsPercentRange: 'النسبة من 1 إلى 100.',
	discountsNameTaken: 'يوجد خصم آخر بهذا الاسم.',
	discountsOnTill: 'على الجهاز:',
	discountsTakesOn: 'على %1 يخصم %2.',
	discountsAmountRate: '% جنيه',
	discountsCreate: 'أضف الخصم',
	discountsSave: 'احفظ الخصم',
	discountsRemoved: 'حُذف الخصم: %',
	discountsHowTitle: 'كيف يُحسب الخصم',
	discountsHow1: 'يُخصم الخصم من الطلب كله، ومعه رسوم التوصيل.',
	discountsHow2: 'تُقرَّب النسبة إلى الجنيه الأدنى: 15% من 87.00 تخصم 13.00.',
	discountsHow3: 'تجعل 100% الطلب مجانيًا، مثل وجبة موظف. وتُسجَّل أصنافه أيضًا.',
	discountsHow4:
		'يصل التغيير إلى الأجهزة عند نشر القائمة، ويسري من البيعة التالية. وتحتفظ الطلبات السابقة بخصمها.',
	discountsHow5:
		'يختار أمناء الصندوق من هذه القائمة فقط. ويستطيع المدير أيضًا كتابة خصم على الجهاز.',

	sellTable: 'الطاولة',
	tableTitle: 'اختر الطاولة',
	tableLabel: 'طاولة %1',
	tableNeeded: 'اختر الطاولة الخاصة بهذا الطلب.',
	tableRemoved: 'لم تعد هذه الطاولة متاحة. اختر طاولة أخرى.',
	tablesLoading: 'جارٍ تحميل الطاولات',
	tablesLoadFailed: 'تعذّر تحميل الطاولات.',
	tablesTitle: 'الطاولات',
	tablesAdd: 'طاولة جديدة',
	tablesName: 'اسم الطاولة',
	tablesNameHint: 'اسم أو رقم، مثل T5 أو التراس 2.',
	tablesEmpty: 'لا توجد طاولات. طلبات داخل المطعم في هذا الفرع بلا طاولة.',
	tablesNote:
		'متى كان في الفرع طاولة، يحدد كل طلب داخل المطعم طاولته. تغيير اسم طاولة أو إزالتها لا يغيّر أي طلب سابق.',
	tablesRemoved: 'أُزيلت الطاولة: %1',
	tablesRuleWhat:
		'الطاولة اسم أو رقم، مثل T5 أو التراس 2. ليست لها منطقة، ولا حالة مشغولة أو متاحة.',
	tablesRuleSeveral: 'قد تحمل الطاولة الواحدة عدة طلبات في الوقت نفسه.',
	tableNameInUse: 'توجد طاولة أخرى في هذا الفرع بهذا الاسم.',
	tableLimit: 'للفرع 200 طاولة على الأكثر.',
	tableNameBounds: 'تحتاج الطاولة إلى اسم من 1 إلى 40 حرفًا.',
	driversTitle: 'المندوبون',
	driversLoading: 'جارٍ تحميل المندوبين',
	driversLoadFailed: 'تعذّر تحميل المندوبين.',
	driversAdd: 'مندوب جديد',
	driversName: 'اسم المندوب',
	driversNameHint: 'اسم، مثل أحمد علي.',
	driversEmpty: 'لا يوجد مندوبون بعد.',
	driversRuleWhat: 'المندوب اسم فقط، ولا يسجّل الدخول في أي مكان.',
	driversRuleKeep:
		'تغيير اسم المندوب يُبقيه المندوب نفسه. إزالة المندوب تخفيه من الأجهزة، ولا يُعطى اسمه لأحد آخر في هذا الفرع.',
	driversRuleTill: 'يدير المدير المسجّل على جهاز في هذا الفرع القائمة نفسها.',
	driversRemoved: 'أُزيل المندوب: %1',
	driverNameTaken: 'هذا الاسم مستخدم في هذا الفرع، لمندوب حالي أو مُزال.',
	driverNameBounds: 'يحتاج المندوب إلى اسم في سطر واحد، من 1 إلى 40 حرفًا.',
	driverGone: 'لم يعد هذا المندوب في قائمة الفرع.',
	driverNotSaved: 'تعذّر حفظ المندوب. حاول مرة أخرى.',
	driversRemoveRefused: 'تعذّرت إزالة %1، وما زال في القائمة.',
	reportsTables: 'الطاولات',
	reportsByTable: 'داخل المطعم حسب الطاولة',
	reportsByTableNote: 'بترتيب الطاولات لا بالمبيعات · الطلبات بجانب المبيعات',
	reportsTable: 'الطاولة',
	reportsNoTable: 'بلا طاولة',
	reportsTablesPickShop: 'اختر فرعًا لترى طاولاته.',
	reportsTablesNone: 'لا توجد طاولات في هذا الفرع.',
	reportsTablesFair:
		'الطاولة الكبيرة أو القريبة من الباب تبيع أكثر، لذا يعرض كل صف طلباته بجانب مبيعاته.',
	reportsTablesZero: 'الطاولة التي عليها صفر لم يُدفع عليها طلب داخل المطعم في هذه الفترة.',
	unitGrams: 'جم',
	unitKilo: 'كجم',
	perKilo: '%1 / كجم',
	editorGrams: 'الوزن بالجرام',
	menuSoldBy: 'طريقة البيع',
	menuByPiece: 'بالقطعة',
	menuByWeight: 'بالوزن',
	menuPricePerKilo: 'سعر الكيلو',
	reportsPortions: 'حصص',
	reportsPieces: 'قطع',
	menuGroupKind: 'طريقة التسعير',
	menuGroupFixed: 'سعر ثابت',
	menuGroupPerKg: 'لكل كجم',
	menuPerKgSoon: 'يمكن إضافة مجموعات الكيلو بعد دقائق.',
	menuWeighedSoon: 'يمكن إضافة الأصناف التي تُباع بالوزن مرة أخرى بعد بضع دقائق.',

	sellPickDriver: 'اختر المندوب',
	driversNoneYet: 'لا يوجد مندوبون بعد.',
	driversAskSomeone: 'لا يوجد مندوبون بعد. اطلب من المدير أو المالك إضافة مندوب.',
	driverLabel: 'المندوب: %1',
	driverNone: 'بدون مندوب',
	driverChange: 'غيّر المندوب',
	driverChangeTitle: 'تغيير مندوب %1',
	driverNeeded: 'اختر المندوب الذي سيوصل هذا الطلب.',
	driverRemoved: 'أُزيل هذا المندوب. اختر غيره.',
	driverClosed: 'أُغلق هذا الطلب، ويبقى مندوبه كما هو.',
	driversFilterAll: 'كل المندوبين',
};

export type MessageKey = keyof typeof en;

const catalogues: Record<Language, Record<MessageKey, string>> = { en, ar, msa };

/** Look up one string. An unknown key falls back to English, never to blank. */
export function message(language: Language, key: MessageKey): string {
	return catalogues[language][key] ?? en[key];
}

/**
 * Put values into a message that holds places for them: %1, %2, or a lone %.
 * Word order differs between the languages, so the place is the message's
 * to choose, not the caller's.
 *
 * One pass over the message, so a value that holds a % of its own, such as a
 * discount's "Staff 20%", is put in as it is rather than read as a place.
 */
export function fill(text: string, ...values: (string | number)[]): string {
	return text.replace(/%(\d?)/g, (_, index: string) =>
		String(values[index ? Number(index) - 1 : 0] ?? ''),
	);
}
